import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import {
  Add,
  ArticleOutlined as ArticleOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  DeleteSweep as DeleteSweepIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore,
  FileDownload as FileDownloadIcon,
  FolderOpen as FolderOpenIcon,
  MergeType as MergeTypeIcon,
  PersonOutline,
  PlayArrow as PlayArrowIcon,
  ReadMore as ReadMoreIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
  Visibility,
  VisibilityOff,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
  ImageSearch as ImageSearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  UploadFile as UploadFileIcon,
  Business as BusinessIcon,
  CloudDownloadOutlined as CloudDownloadIcon,
  CloudUploadOutlined as CloudUploadIcon,
  Language as LanguageIcon,
  VerifiedUserOutlined as VerifiedUserIcon,
  WarningAmberOutlined as WarningAmberIcon,
  SmartToyOutlined as SmartToyIcon,
  Tune as TuneIcon,
  StraightenOutlined as StraightenIcon,
  GroupsOutlined as GroupsIcon,
  CheckCircleOutline as CheckCircleIcon,
  ErrorOutline as ErrorCircleIcon,
  Layers as LayersIcon,
  Replay as ReplayTagIcon,
  FolderZipOutlined as FolderZipIcon,
  UnarchiveOutlined as UnarchiveIcon,
} from "@mui/icons-material";
import {
  BotOptionSpec,
  BotRegistryItem,
  MajorJob,
  useCreateBackendUserBotConnectionMutation,
  useCreateMajorJobMutation,
  useCreateTasksBatchMutation,
  useDeleteAdminConnectionMutation,
  useDeleteMajorJobMutation,
  useDeleteTaskJobMutation,
  useDeleteTaskMutation,
  useGetBotRegistryQuery,
  useGetMajorJobsQuery,
  useGetTaskJobsQuery,
  useTaskJobsBatchDeleteMutation,
  useAddTasksToJobMutation,
  useTasksBatchDeleteMutation,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSubmitTask2FACodeMutation,
  useUpdateAdminConnectionMutation,
  useUpdateAdminTaskMutation,
  useUpdateMajorJobMutation,
  useRefreshTaskJobStatsMutation,
  useUpdateTaskJobMutation,
  useGetBackendUserStorageFoldersQuery,
  useLazyGetJobMatchesFileContentQuery,
  useDeleteJobMatchesFileMutation,
  usePresignJobMatchesMergeUploadMutation,
  useMergeJobMatchesReportsMutation,
  useLazyGetJobLogsArchiveQuery,
  useClearJobLogsMutation,
  useRunJobNewRowsMutation,
  useRunJobRosterTaskMutation,
  useLazyGetJobRosterQuery,
  useDeleteJobRosterMutation,
  usePresignJobArtifactUploadMutation,
  useReplaceJobMatchesMutation,
  useReplaceJobRosterMutation,
  useLazyGetJobSsnReadinessQuery,
  useLazyGetMajorJobSsnReadinessQuery,
  useLazyGetMajorJobBrowserProfilesQuery,
  useDeleteMajorJobBrowserProfilesMutation,
  useLazyGetJobLogsInventoryQuery,
  useCreateMajorJobSsnJobMutation,
  useUpdateMajorJobSsnJobMutation,
  useDeleteMajorJobSsnJobMutation,
  useDeleteMajorJobSsnFileMutation,
  useLazyGetMajorJobSsnFileContentQuery,
  adminUsersApi,
} from "features/adminUsers/adminUsers";
import type {
  BrowserProfilesInfo,
  JobRosterInfo,
  MatchesFileInfo,
  MatchesSummary,
  SsnReadiness,
} from "features/adminUsers/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { botsManagementWs, BotsManagementWsEvent } from "features/ws/botsManagementWs";
import { ChangeEvent, Fragment, ReactElement, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import useViewAs from "hooks/useViewAs";
import { isSuperAdmin } from "utils/roles";
import { AdminVendorConnection } from "types/interfaces/adminUsers";
import BotsTableScroll, { BOTS_HEADER_CELL_SX, BOTS_BODY_CELL_SX } from "components/botsManagement/BotsTableScroll";
import DeleteJobConfirmDialog from "components/botsManagement/DeleteJobConfirmDialog";
import EmployeeProgressBar from "components/botsManagement/EmployeeProgressBar";
import MetaPill, { type MetaTone } from "components/botsManagement/MetaPill";
import SoftTag from "components/botsManagement/SoftTag";
import ConfirmDialog from "components/common/ConfirmDialog";
import MajorJobWorkspaceSkeleton from "components/botsManagement/MajorJobWorkspaceSkeleton";
import WorkspaceMetaList from "components/botsManagement/WorkspaceMetaList";
import TaskLiveViewDialog from "components/botsManagement/TaskLiveViewDialog";
import AppDialog from "components/common/AppDialog";
import CustomScrollbars from "components/CustomScrollbars";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import BotOptionFields from "components/botsManagement/BotOptionFields";
import BotOptionSummary, {
  botOptionTagText,
  summarizeBotOptions,
} from "components/botsManagement/BotOptionSummary";
import LogsDialog from "components/botsManagement/LogsDialog";
import { LogsScope } from "components/botsManagement/LogsView";
import {
  isLiveViewReady,
  shouldAutoOpenLiveView,
} from "components/botsManagement/liveViewPrompt";
import {
  isTwoFaAutoPhase,
  isTwoFaCodeNeeded,
  isTwoFaCodeSubmitted,
} from "components/botsManagement/twoFaPrompt";
import { buildDataStorageFolderMappingSeed } from "utils/dataStorageNavigation";
import {
  applySkipErrorEmployees,
  extractSkipErrorEmployees,
  readListOption,
} from "utils/additionalOptions";
import { sanitizeEmployeesInput, parseEmployeesNumber } from "utils/employeesNumber";
import {
  ChipColor,
  botWorkStatusChipColor,
  taskRunStatusChipColor,
} from "utils/botStatusColors";

type ConnPlanEditorRow = {
  connection_id: number | "";
  /** Raw digit string while editing (may be ""); parsed in normalizePlans / bulk-add helpers. */
  task_target_count: string;
};

function connPlanTasksDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 4);
}

/** API-ready connection plan row (numeric connection + positive task count). */
type NormalizedConnPlanRow = {
  connection_id: number;
  task_target_count: number;
};

/** Normalize connection plan editor rows for API PATCH (shared by create-job + edit dialog). */
function normalizePlans(rows: ConnPlanEditorRow[]): NormalizedConnPlanRow[] {
  return rows
    .map((r) => {
      const d = connPlanTasksDigitsOnly(r.task_target_count);
      const n = d === "" ? Number.NaN : Math.floor(Number(d));
      return {
        connection_id: r.connection_id,
        task_target_count: n,
      };
    })
    .filter(
      (r): r is { connection_id: number; task_target_count: number } =>
        typeof r.connection_id === "number" &&
        r.connection_id >= 1 &&
        Number.isFinite(r.task_target_count) &&
        r.task_target_count >= 1,
    )
    .map((r) => ({
      connection_id: r.connection_id,
      task_target_count: Math.min(1000, Math.max(1, r.task_target_count)),
    }));
}

const MAJOR_FORM_LISTBOX = { style: { maxHeight: 280 } };

const filterMajorFormVendors = createFilterOptions<string>();
const filterChildDialogBots = createFilterOptions<BotRegistryItem>({
  stringify: (option) =>
    `${option.vendor} ${option.bot_slug} ${option.job_kind} ${option.job_kind}.${option.bot_slug}`,
});

const MAX_BATCH_TASKS = 30;

// The Manage job dialog groups its actions into labeled tiles.
// The job's progress card: chips on the top line, the bar underneath.
const MJ_PROGRESS_CARD_SX = {
  width: "100%",
  alignSelf: "stretch",
  boxSizing: "border-box",
  px: 1.25,
  py: 1,
  mb: 1,
  borderRadius: 1.5,
  border: 1,
  borderColor: "divider",
  bgcolor: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.018)",
} as const;

const MJ_MANAGE_GROUP_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  p: 1.25,
} as const;

const MJ_MANAGE_GROUP_LABEL_SX = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "text.secondary",
} as const;

function connectionIdsForPlanRow(
  pool: number[],
  rows: ConnPlanEditorRow[],
  rowIndex: number,
): number[] {
  const row = rows[rowIndex];
  const cur =
    typeof row?.connection_id === "number" && row.connection_id >= 1 ? row.connection_id : null;
  const taken = new Set(
    rows.flatMap((r, j) =>
      j !== rowIndex && typeof r.connection_id === "number" && r.connection_id >= 1 ? [r.connection_id] : [],
    ),
  );
  return pool.filter((cid) => !taken.has(cid) || cid === cur);
}

function nextUnusedPoolConnectionId(
  poolSortedIds: number[],
  rows: ConnPlanEditorRow[],
): number | undefined {
  const used = new Set(
    rows
      .map((r) => (typeof r.connection_id === "number" ? r.connection_id : null))
      .filter((x): x is number => x != null && x >= 1),
  );
  return poolSortedIds.find((cid) => !used.has(cid));
}

function TabPanel(props: { children?: ReactNode; value: number | string; index: number | string }) {
  const { children, value, index } = props;
  const active = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      style={
        active
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
          : undefined
      }
    >
      {active ? (
        <CustomScrollbars sx={{ minHeight: 0 }} trackGutter>
          <Box sx={{ pt: 2 }}>{children}</Box>
        </CustomScrollbars>
      ) : null}
    </div>
  );
}

/** Eight placeholder rows matching the task-jobs table's 9 columns. */
function MjJobsSkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell padding="checkbox">
            <Skeleton variant="rounded" width={18} height={18} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="70%" />
          </TableCell>
          <TableCell align="center">
            <Skeleton variant="rounded" width={80} height={22} sx={{ mx: "auto" }} />
          </TableCell>
          {[0, 1, 2, 3].map((c) => (
            <TableCell key={c} align="center">
              <Skeleton variant="text" width={32} sx={{ mx: "auto" }} />
            </TableCell>
          ))}
          <TableCell align="center">
            <Skeleton variant="rounded" width={92} height={24} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell padding="checkbox" />
        </TableRow>
      ))}
    </>
  );
}

/** Meta tile for the major-jobs list header; the workspace strip uses MetaPill directly. */
function MajorJobStripItem({ title, value, avatar }: { title: string; value: string; avatar?: boolean }) {
  return (
    <MetaPill
      title={title}
      value={value}
      tone={avatar ? "primary" : "neutral"}
      icon={avatar ? <PersonOutline /> : undefined}
    />
  );
}

function mjKindLabel(kind: string | undefined): string {
  return kind === "upload_bots" ? "Upload" : kind === "delete_bots" ? "Delete" : "Download";
}

function mjKindTone(kind: string | undefined): MetaTone {
  return kind === "upload_bots" ? "success" : kind === "delete_bots" ? "error" : "info";
}

function mjKindIcon(kind: string | undefined) {
  if (kind === "upload_bots") return <CloudUploadIcon />;
  if (kind === "delete_bots") return <DeleteSweepIcon />;
  return <CloudDownloadIcon />;
}

/** The sticky error-mode switch, toned like the tags next to it: warning when
 * on (a run retries current ERRORs only), muted when off. */
function ErrorModeToggle({
  checked,
  disabled,
  onChange,
  tooltip,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={tooltip}>
      <Box
        component="label"
        sx={(theme) => {
          const main = checked ? theme.palette.warning.main : theme.palette.text.secondary;
          const dark = theme.palette.mode === "dark";
          return {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            pl: 1.25,
            pr: 0.25,
            py: 0,
            minHeight: { xs: 40, sm: 32 },
            boxSizing: "border-box",
            borderRadius: 1,
            border: 1,
            borderColor: alpha(main, checked ? (dark ? 0.6 : 0.4) : dark ? 0.35 : 0.2),
            bgcolor: alpha(main, checked ? (dark ? 0.22 : 0.1) : dark ? 0.1 : 0.04),
            color: checked ? main : "text.secondary",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "& svg": { fontSize: 15, color: main },
          };
        }}
      >
        <ReplayTagIcon />
        <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1 }}>
          Process error EEs
        </Typography>
        <Switch
          size="small"
          color="warning"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </Box>
    </Tooltip>
  );
}

// Numbers behind a job's progress bar; total is null while the ranges are provisional.
function mjJobProgressProps(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const totalRaw = stats?.actual_total_employees ?? job.actual_total_employees;
  const inStorage = mjEmployeeFolders(job);
  return {
    success: Number(stats?.success_employees_count ?? 0),
    error: Number(stats?.error_employees_count ?? 0),
    total: mjRosterEmpty(job)
      ? 0
      : mjRangesPending(job) || totalRaw == null
        ? null
        : Number(totalRaw),
    // Delete bots walk the roster, not a folder, so there is nothing to count.
    extra:
      inStorage != null
        ? `${inStorage.toLocaleString()} employee folder(s) in Data Storage`
        : String(job.bot_name ?? "").startsWith("delete_bots.")
          ? undefined
          : "Employee folders in Data Storage: not counted yet - press Refresh",
  };
}



function normalizeBotRegistryPayload(
  data: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
): BotRegistryItem[] {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

function normVendor(v: string | undefined | null): string {
  return (v ?? "").trim().toLowerCase();
}

/** Login line for tables/dropdowns under a major job — vendor omitted (shown on the major job header). */
function connectionPoolRowLabel(all: AdminVendorConnection[], connectionId: number): string {
  const c = all.find((x) => x.id === connectionId);
  if (!c) return "";
  const u = (c.username ?? "").trim();
  return u ? `@${u}` : "-";
}

function mjConnLabelVerbose(all: AdminVendorConnection[], connectionId: number): string {
  const short = connectionPoolRowLabel(all, connectionId);
  return short || "-";
}

/** Compose a 1-based ordinal label for a connection within a major job's pool. */
function mjConnLabelByOrdinal(
  poolIds: number[],
  all: AdminVendorConnection[],
  connectionId: number,
): string {
  const idx = poolIds.indexOf(connectionId);
  const ord = idx >= 0 ? idx + 1 : null;
  const short = mjConnLabelVerbose(all, connectionId);
  if (ord == null) return short;
  return `${ord}. ${short}`;
}

// The dedicated "Document types to delete" block is the only editor of this
// key; the registry-driven BotOptionFields must skip it or two inputs write it.
const DOCS_TYPE_OMIT = ["DOCS_TYPE"];
// Which side the SSN file is spelled for follows the major job's kind, so the
// backend derives it and an operator has nothing to decide here. Mirrors
// _SSN_UPLOAD_FLAG_KEYS in backend/app/modules/tasks/views.py.
const SSN_DERIVED_OMIT = ["IS_SSN_FOR_UPLOAD_ON_PAYCOM", "IS_SSN_FOR_UPLOAD_ON_UKG", "IS_FOR_UPLOAD"];
// The read-only option views skip what dedicated controls already show.
const MJ_OPTION_SUMMARY_OMIT = [...DOCS_TYPE_OMIT, ...SSN_DERIVED_OMIT];
const SSN_BOT_NAME = "ssn_bots.ssn_bot";

/** Parse a stored DOCS_TYPE value the way the bot entrypoint does: strip one
 * pair of matching outer quotes (the API/Postman spelling is
 * DOCS_TYPE='["A"]'), then JSON, then the Python-literal spelling with single
 * quotes. An element that is itself a JSON-encoded list - the double encoding
 * the old reader produced - is flattened, so a corrupted value heals on the
 * next save. Returns null when the value is not a list in any spelling. */
function parseDocTypesValue(value: string): string[] | null {
  let v = (value || "").trim();
  if (v.length >= 2 && v[0] === v[v.length - 1] && (v[0] === "'" || v[0] === '"')) {
    v = v.slice(1, -1).trim();
  }
  const tryJson = (s: string): unknown => {
    try {
      return JSON.parse(s);
    } catch {
      return undefined;
    }
  };
  let parsed = tryJson(v);
  if (parsed === undefined) parsed = tryJson(v.replace(/'/g, '"'));
  if (!Array.isArray(parsed)) return null;
  const flat: string[] = [];
  for (const item of parsed) {
    const text = String(item).trim();
    const inner = text.startsWith("[") ? tryJson(text) : undefined;
    if (Array.isArray(inner)) {
      for (const nested of inner) {
        const nestedText = String(nested).trim();
        if (nestedText) flat.push(nestedText);
      }
    } else if (text) {
      flat.push(text);
    }
  }
  return flat;
}

/** Trim, unwrap pasted JSON lists and dedupe - what a save actually stores. */
function cleanDocTypes(docTypes: string[]): string[] {
  const clean: string[] = [];
  for (const raw of docTypes) {
    const text = raw.trim();
    if (!text) continue;
    const parsed = text.startsWith("[") ? parseDocTypesValue(text) : null;
    for (const item of parsed ?? [text]) {
      if (item && !clean.includes(item)) clean.push(item);
    }
  }
  return clean;
}

function extractDocsTypeFromOptions(additionalOptions: string): { types: string[]; remaining: string } {
  const parts = (additionalOptions || "").split(";").map((s) => s.trim()).filter(Boolean);
  const docsTypePart = parts.find((p) => p.startsWith("DOCS_TYPE="));
  const remaining = parts.filter((p) => !p.startsWith("DOCS_TYPE=")).join(";");
  if (!docsTypePart) return { types: [""], remaining: additionalOptions || "" };
  const value = docsTypePart.slice("DOCS_TYPE=".length);
  const parsed = parseDocTypesValue(value);
  if (parsed) return { types: parsed.length > 0 ? parsed : [""], remaining };
  // Not a list in any known spelling: keep a hand-written bare name, but never
  // a bracketed literal - a raw literal shown as a "type" and re-saved is
  // exactly how the double encoding started (CAL-5923).
  const bare = value.trim().replace(/^['"]|['"]$/g, "").trim();
  return { types: bare && !bare.startsWith("[") ? [bare] : [""], remaining };
}

function buildDeleteAdditionalOptions(baseOpts: string, docTypes: string[]): string {
  const docsTypePart = `DOCS_TYPE=${JSON.stringify(cleanDocTypes(docTypes))}`;
  const { remaining } = extractDocsTypeFromOptions(baseOpts);
  return remaining ? `${remaining};${docsTypePart}` : docsTypePart;
}

function isSSNJob(row: Record<string, unknown>): boolean {
  return String(row.bot_name ?? "").startsWith("ssn_bots.");
}

/** Normalize em/en dashes in labels for display (ASCII hyphen with spaces). */
function displayJobNameShortDash(label: string): string {
  return label.replace(/\s*[\u2013\u2014]\s*/g, " - ");
}

function extractDefaultFolderSlugFromBotName(botName: string): string {
  const parts = botName.split(".");
  return parts[parts.length - 1] || "data";
}


function StatusChipMj({
  label,
  color,
  title,
  onClick,
  icon,
}: {
  label: string;
  color: ChipColor;
  title?: string;
  onClick?: () => void;
  icon?: ReactElement;
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      color={color}
      title={title}
      onClick={onClick}
      icon={icon}
      sx={{
        height: 22,
        maxHeight: 22,
        fontWeight: 600,
        fontSize: "0.6875rem",
        lineHeight: 1,
        ...(onClick ? { cursor: "pointer", textDecoration: "underline" } : {}),
        "& .MuiChip-icon": { ml: 0.5, mr: -0.25, fontSize: "0.85rem" },
        "& .MuiChip-label": { px: 0.65, py: 0 },
      }}
    />
  );
}

/** Task row data as used in accordion (matches connection-tasks admin API + 2FA). */
export type MjConnectionTask = {
  id: number;
  job?: number | null;
  task_status: string;
  bot_work_status: string;
  task2fa_status?: string;
  task2fa_auto_until?: string | null;
  live_view_status?: string;
  live_view_phase?: string;
  started_by?: number | null;
  bot_name: string;
  employee_number_from: number;
  employee_number_to: number;
  connection?: number;
  range_role?: string;
  // Live attempt counters (only grow) and the stored-log snapshot written by
  // the job's artifact scan, attributed by the task's current range.
  successfully_processed_employees_count?: number;
  error_employees_count?: number;
  success_employees_snapshot?: number | null;
  error_employees_snapshot?: number | null;
  retry_error_employees?: boolean;
  additional_options?: string | null;
  browser_profile_id?: number | null;
  started_by_username?: string | null;
  document_type?: string;
  path_to_s3_base_folder?: string;
  use_ssn_match?: boolean;
  upload_as_zip?: boolean;
  unzip_folder_before_uploading?: boolean;
  committed_from?: number | null;
  committed_to?: number | null;
  queue_run_source?: string;
};

const MJ_ACCORDION_ROW_SX = {
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  mb: 1,
  overflow: "hidden",
  transition: "none !important",
  "& .MuiCollapse-root": { transition: "none !important" },
  "&.Mui-expanded": { margin: 0, mb: 1, mt: 0 },
} as const;

const MJ_ACC_SUMMARY_SX = {
  minHeight: 48,
  py: 0,
  "&.Mui-expanded": { minHeight: 48 },
  "& .MuiAccordionSummary-content": {
    alignItems: "flex-start",
    margin: "10px 0 !important",
    "&.Mui-expanded": { margin: "10px 0 !important" },
  },
} as const;

/**
 * Task jobs table columns: [expand][checkbox][name][status][tasks][total][success][error][actions].
 * Fixed pixel widths (name flexes) keep header + collapsible rows perfectly aligned; the table keeps
 * this `minWidth` so the panel scrolls horizontally instead of squeezing the columns.
 */
const MJ_JOBS_TABLE_MIN_WIDTH = 920;
const MJ_TASKS_TABLE_MIN_WIDTH = 1200;
/** Same columns, squeezed - saves ~230px of horizontal travel on a phone. */
const MJ_TASKS_TABLE_MIN_WIDTH_XS = 970;
/** [checkbox][№][status][bot][range][progress][connection][actions] */
const MJ_TASKS_COL_WIDTHS = [44, 50, 150, 150, 110, 240, 300, 200];
const MJ_TASKS_COL_WIDTHS_XS = [40, 44, 132, 132, 92, 190, 200, 176];

type ManageTab = "jobs" | "ssn" | "connections";

// Compact 2FA action button so it doesn't tower over the icon buttons next to it.
const MJ_PASS_2FA_BTN_SX = {
  py: 0.15,
  px: 0.85,
  minWidth: 0,
  fontSize: "0.68rem",
  lineHeight: 1.4,
  "& .MuiButton-startIcon": {
    mr: 0.4,
    "& > *": { fontSize: "0.95rem" },
  },
} as const;

function canRunTaskByStatusMj(status: string) {
  return ["PENDING", "STOPPED", "FINISHED", "ERROR", "CRITICAL_ERROR"].includes(
    String(status ?? "").toUpperCase(),
  );
}

/** Run control is swapped for Stop while the worker is actually running workloads. */
function mjTaskShowsStopInsteadOfRun(status: string) {
  const s = String(status ?? "").toUpperCase();
  // QUEUED belongs here: Stop on a queued task means "leave the line".
  return ["ECS_CONTAINER_STARTED", "PROCESSING", "QUEUED"].includes(s);
}

function mjJobAggregateRunning(job: Record<string, unknown>): boolean {
  /** Use stats only: they aggregate live Task rows. Job.task_status can lag after stop/run. */
  return mjJobStatsTotals(job).running > 0;
}

function mjRosterTaskInFlight(job: Record<string, unknown>): boolean {
  const stats = job.stats as { roster_task?: { in_flight?: boolean } | null } | undefined;
  return stats?.roster_task?.in_flight === true;
}

// Anything of the job working, the roster-only task included - what config
// edits and the error-mode switch have to wait for. Run all deliberately does
// not: employee tasks queue behind a running roster task.
function mjJobAnythingInFlight(job: Record<string, unknown>): boolean {
  return mjJobAggregateRunning(job) || mjRosterTaskInFlight(job);
}

function mjIsUploadJob(job: Record<string, unknown>): boolean {
  return job.bot_kind === "upload_bots" || String(job.bot_name ?? "").startsWith("upload_bots.");
}

function mjSsnReadinessText(readiness: SsnReadiness): string {
  if (readiness.ready) {
    return `SSN match ready: the ${readiness.source_vendor} and ${readiness.destination_vendor} SSN files are in place.`;
  }
  return `Names only: ${readiness.reason}. Matching by name is less accurate - two employees with one name can be confused.`;
}

function mjBrowserProfilesLabel(info: BrowserProfilesInfo): string {
  if (!info.profiles.length) return "none stored";
  const bytes = info.profiles.reduce((acc, p) => acc + (p.size_bytes ?? 0), 0);
  const latest = info.profiles
    .map((p) => (p.last_modified ? new Date(p.last_modified).getTime() : 0))
    .reduce((a, b) => Math.max(a, b), 0);
  const when = latest ? `, updated ${new Date(latest).toLocaleDateString()}` : "";
  return `${info.profiles.length} file(s), ${Math.round(bytes / 1048576)} MB${when}`;
}

function mjRosterInfoLine(info: JobRosterInfo): string {
  const shared = info.sibling_job_ids.length
    ? ` Shared with job(s) ${info.sibling_job_ids.map((id) => `#${id}`).join(", ")}.`
    : "";
  if (info.kind === "matches") {
    const matches = info.files.find((f) => f.filename === "s3_to_site_names_matches.csv");
    if (!matches?.exists) return `Matches: not built yet.${shared}`;
    const when = matches.last_modified
      ? `, updated ${new Date(matches.last_modified).toLocaleString()}`
      : "";
    const manual = info.summary?.manual_upload ? ", uploaded by hand" : "";
    return `Matches: ${matches.rows ?? "?"} row(s)${when}${manual}.${shared}`;
  }
  if (!info.exists) return `Roster: not collected yet.${shared}`;
  const when = info.last_modified ? `, updated ${new Date(info.last_modified).toLocaleString()}` : "";
  return `Roster: ${info.rows ?? "?"} employee(s)${when}.${shared}`;
}

function mjRosterInfoExists(info: JobRosterInfo | null): boolean {
  if (!info) return false;
  return info.kind === "matches" ? info.files.some((f) => f.exists) : info.exists;
}

// Rows the stored roster / Matches file names; 0 for a file that is there but
// empty, null when nothing is stored or the count is unknown.
function mjRosterStoredRows(info: JobRosterInfo | null): number | null {
  if (!info) return null;
  if (info.kind === "matches") {
    const matches = info.files.find((f) => f.filename === "s3_to_site_names_matches.csv");
    return matches?.exists ? matches.rows : null;
  }
  return info.exists ? info.rows : null;
}

// The file a run would reuse: the Matches CSV itself (upload) or the roster.
// The Several / No matches reports do not count - they may outlive a deleted
// Matches CSV, and a run over them still matches again.
function mjRosterMainFileExists(info: JobRosterInfo): boolean {
  if (info.kind === "matches") {
    return info.files.some((f) => f.filename === "s3_to_site_names_matches.csv" && f.exists);
  }
  return info.exists;
}

// A roster-only run would reuse a stored file and change nothing, so the button
// is greyed out while one with employees in it exists. An empty file does not
// count - the bot throws it away and collects again, as the backend's own check
// does. Loaded file facts decide; the job's total is only the fallback before
// they arrive (it is set from the file and cleared when the file is deleted).
function mjRosterAlreadyBuilt(job: Record<string, unknown>, info: JobRosterInfo | null): boolean {
  if (info) {
    const rows = mjRosterStoredRows(info);
    return rows != null ? rows > 0 : mjRosterMainFileExists(info);
  }
  return Number(job.actual_total_employees ?? 0) > 0;
}

function mjRosterBuiltTooltip(job: Record<string, unknown>, info: JobRosterInfo | null): string {
  const what = mjIsUploadJob(job) ? "The Matches file is" : "The roster is";
  const line = info && mjRosterInfoExists(info) ? ` ${mjRosterInfoLine(info)}` : "";
  return `${what} already built.${line} Delete it in Manage job - Files to build it again.`;
}

// The stored file exists but names nobody: the run rebuilds it.
function mjRosterStoredEmptyNote(info: JobRosterInfo | null): string {
  return mjRosterStoredRows(info) === 0
    ? " The stored file is empty and is thrown away - this run collects it again."
    : "";
}

function parseConnectionPlansFromJobRow(
  job: Record<string, unknown>,
): { connection_id: number; task_target_count: number }[] {
  const raw = job.connection_plans;
  if (!Array.isArray(raw)) return [];
  const out: { connection_id: number; task_target_count: number }[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const cid = Number(obj.connection_id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    out.push({
      connection_id: cid,
      task_target_count: Math.max(1, Number(obj.task_target_count) || 1),
    });
  }
  return out;
}

function legacyPrimaryConnectionFromJob(job: Record<string, unknown>): number | null {
  const direct = Number(job.connection_id ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const cr = job.connection;
  if (typeof cr === "number" && Number.isFinite(cr) && cr > 0) return cr;
  if (cr != null && typeof cr === "object") {
    const pid = Number((cr as Record<string, unknown>).id ?? 0);
    if (Number.isFinite(pid) && pid > 0) return pid;
  }
  return null;
}

function mjTasksCountOnConnection(tasks: MjConnectionTask[], connectionId: number): number {
  return tasks.reduce((acc, t) => acc + (Number(t.connection) === connectionId ? 1 : 0), 0);
}

/** Sums numeric per-row "to add" counts grouped by login (planned add dialog). */
function aggregatePlannedAddsByConnection(rows: ConnPlanEditorRow[]): { cid: number; add: number }[] {
  const m = new Map<number, number>();
  for (const r of rows) {
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(r.task_target_count);
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1 || add < 1) continue;
    m.set(cid, (m.get(cid) ?? 0) + add);
  }
  return Array.from(m.entries())
    .map(([cid, add]) => ({ cid, add }))
    .sort((a, b) => a.cid - b.cid);
}

function resolveMjBulkTaskAddPayload(
  planMode: boolean,
  plansSnap: { connection_id: number; task_target_count: number }[],
  poolIds: number[],
  rows: ConnPlanEditorRow[],
  currentTasks: MjConnectionTask[],
  jobTotalRemain: number,
  connections: AdminVendorConnection[],
):
  | { error: string }
  | { planMode: false; legacyCount: number }
  | { planMode: true; batches: { connection_id: number; count: number }[] } {
  if (jobTotalRemain < 1) {
    return { error: `This job already has ${MAX_BATCH_TASKS} tasks (maximum).` };
  }

  if (!planMode) {
    if (rows.length !== 1) {
      return { error: "This job expects a single add row." };
    }
    const d0 = connPlanTasksDigitsOnly(String(rows[0]?.task_target_count ?? ""));
    const n = d0 === "" ? Number.NaN : Math.floor(Number(d0));
    const parsed = Number.isFinite(n) ? n : 0;
    if (parsed < 1) return { error: "Add at least one task." };
    if (parsed > jobTotalRemain) return { error: `At most ${jobTotalRemain} more task(s) fit on this job.` };
    return { planMode: false, legacyCount: parsed };
  }

  // Plan rows are treated as advisory hints in this UI: when a row asks for
  // more tasks than the recorded quota, the backend auto-raises the quota for
  // that connection. The pool set (logins available to this major job) is
  // still enforced so users cannot attach tasks to logins outside the pool.
  const poolOk = poolIds.length ? new Set(poolIds) : null;
  // Mark unused variable to keep the helper signature backwards compatible.
  void plansSnap;
  void currentTasks;
  void connections;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(String(r.task_target_count ?? ""));
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1) {
      return { error: `Row ${i + 1}: choose a login.` };
    }
    if (poolOk && !poolOk.has(cid)) {
      return { error: `Row ${i + 1}: login outside the mapped set.` };
    }
    if (add < 1) {
      return { error: `Row ${i + 1}: enter at least 1 task to add.` };
    }
  }

  const batches = aggregatePlannedAddsByConnection(rows).map(({ cid, add }) => ({
    connection_id: cid,
    count: add,
  }));
  if (!batches.length) {
    return { error: "Pick logins and how many tasks to add on each row." };
  }

  const sum = batches.reduce((acc, b) => acc + b.count, 0);
  if (sum > jobTotalRemain) {
    return { error: `Total adds (${sum}) exceed the ${MAX_BATCH_TASKS}-task job limit (${jobTotalRemain} left).` };
  }

  return { planMode: true, batches };
}

function mjJobDisplayName(job: Record<string, unknown>): string {
  return displayJobNameShortDash(String(job.name ?? ""));
}

function mjEmployeeStatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "—";
}

type MjCountersSource = "live" | "snapshot";

function mjEmployeeStatsFromJob(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const total = stats?.actual_total_employees ?? job.actual_total_employees;
  const success = stats?.success_employees_count;
  const error = stats?.error_employees_count;
  const snapshotAt =
    typeof stats?.stats_snapshot_updated_at === "string"
      ? (stats.stats_snapshot_updated_at as string)
      : null;
  // "live": the tasks' attempt counters while the job works; "snapshot": the
  // deduplicated count from the stored logs, taken at snapshotAt.
  const source: MjCountersSource = stats?.counters_source === "snapshot" ? "snapshot" : "live";
  const hasAny =
    total != null ||
    (success != null && Number(success) > 0) ||
    (error != null && Number(error) > 0);
  if (!hasAny) {
    return { total: "—", success: "—", error: "—", snapshotAt, source };
  }
  return {
    total: mjEmployeeStatValue(total),
    success: mjEmployeeStatValue(success ?? 0),
    error: mjEmployeeStatValue(error ?? 0),
    snapshotAt,
    source,
  };
}

function mjRangeNotice(job: Record<string, unknown>): string {
  const notice = (
    job as { stats?: { range_coverage?: { notice?: string } } }
  ).stats?.range_coverage?.notice;
  return typeof notice === "string" ? notice.trim() : "";
}

// The roster was collected and holds nobody (0 matches). Different from a
// roster not collected yet, which is total == null.
function mjRosterEmpty(job: Record<string, unknown>): boolean {
  const stats = job.stats as
    | { range_coverage?: { roster_empty?: boolean }; actual_total_employees?: unknown }
    | undefined;
  return (
    stats?.range_coverage?.roster_empty === true ||
    stats?.actual_total_employees === 0 ||
    job.actual_total_employees === 0
  );
}

// Bounds that name nobody: the bootstrap guess before the first roster
// collection, or an empty roster. Rendered as "-" everywhere.
function mjRangesPending(job: Record<string, unknown>): boolean {
  return (
    (job.auto_recalculate_ranges !== false && job.actual_total_employees == null) ||
    mjRosterEmpty(job)
  );
}

function mjRosterEmptyWarning(job: Record<string, unknown>): string {
  if (!mjRosterEmpty(job)) return "";
  const warning = (
    job as { stats?: { range_coverage?: { warning?: string } } }
  ).stats?.range_coverage?.warning;
  return typeof warning === "string" && warning.trim()
    ? warning.trim()
    : "Warning: the roster is empty (0 matches) - the job has nobody to process.";
}

function mjMatchesSummaryHeadline(summary: MatchesSummary): string {
  if (summary.algorithm === "manual_upload" || summary.manual_upload) {
    const manual = summary.manual_upload;
    const when = manual?.uploaded_at ? ` on ${new Date(manual.uploaded_at).toLocaleString()}` : "";
    const who = manual?.uploaded_by ? ` by ${manual.uploaded_by}` : "";
    const file = manual?.file_name ? ` (${manual.file_name})` : "";
    return `Matches file uploaded by hand: ${summary.matched_rows} row(s)${when}${who}${file}.`;
  }
  const algorithm = summary.algorithm === "ssn" ? "SSN comparison" : "names comparison";
  const requested = summary.ssn_match_requested ? "SSN match requested" : "SSN match not requested";
  return (
    `${summary.matched_folders} of ${summary.s3_folders} employee folder(s) matched ` +
    `(${summary.matched_rows} row(s)), ${summary.several} with several matches, ` +
    `${summary.no_matches} without a match. Algorithm: ${algorithm} (${requested}).`
  );
}

function mjMatchesSummaryLines(summary: MatchesSummary): string[] {
  const lines: string[] = [];
  if (summary.ssn_fallback_reason) lines.push(`${summary.ssn_fallback_reason}.`);
  if (summary.top_reason_label) lines.push(`Top reason: ${summary.top_reason_label}.`);
  const breakdown = Object.entries(summary.breakdown ?? {})
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${key.replace(/_/g, " ")}: ${count}`);
  if (breakdown.length) lines.push(`Breakdown: ${breakdown.join(", ")}.`);
  for (const [role, file] of Object.entries(summary.ssn_files ?? {})) {
    const when = file.last_modified ? new Date(file.last_modified).toLocaleString() : "unknown date";
    const shapes = file.employee_id_shapes?.length
      ? `, employee_id looks like ${file.employee_id_shapes.join(", ")}`
      : "";
    lines.push(
      `${role} SSN file (${file.vendor}): ${file.ssn_count} SSN(s), ${file.account_count} account(s), ${when}${shapes}.`,
    );
  }
  if (summary.folder_id_shapes?.length) {
    lines.push(`Folder ids look like ${summary.folder_id_shapes.join(", ")}.`);
  }
  for (const hint of summary.hints ?? []) lines.push(hint);
  if (summary.created_at) {
    const by = summary.task_id ? ` by task #${summary.task_id}` : "";
    lines.push(`Built ${new Date(summary.created_at).toLocaleString()}${by}.`);
  }
  return lines;
}


function mjUncoveredRows(job: Record<string, unknown>): {
  rows: number;
  suggested: number;
} {
  const coverage = (
    job as {
      stats?: {
        range_coverage?: { uncovered_rows?: number; suggested_new_tasks?: number };
      };
    }
  ).stats?.range_coverage;
  return {
    rows: Number(coverage?.uncovered_rows ?? 0),
    suggested: Math.max(1, Number(coverage?.suggested_new_tasks ?? 1)),
  };
}


function mjRangeChipLabel(job: Record<string, unknown>): string {
  if (mjRangesPending(job)) {
    return "Range -";
  }
  const from = Number(job.employee_number_from ?? 0);
  const to = Number(job.employee_number_to ?? 0);
  // An upper bound below the lower one is a real state: more tasks than
  // employees leaves the extra ones with nothing to process.
  return from > to ? "Range empty" : `Range ${from}-${to}`;
}


function mjWhenAgo(iso: string): string | null {
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return null;
  const min = Math.round((Date.now() - ts.getTime()) / 60000);
  let when: string;
  if (min < 1) when = "just now";
  else if (min < 60) when = `${min} min ago`;
  else if (min < 60 * 24) when = `${Math.round(min / 60)} h ago`;
  else when = `${Math.round(min / (60 * 24))} d ago`;
  return `${when} (${ts.toLocaleString()})`;
}

// Where the job's Success / Errors come from right now. Live counters move with
// the bots' events; the stored-log count replaces them when the queue drains.
function mjStatsSnapshotTooltip(
  snapshotAt: string | null | undefined,
  source: MjCountersSource = "snapshot",
): string {
  const exactAt = snapshotAt ? mjWhenAgo(snapshotAt) : null;
  if (source === "live") {
    return (
      "Live attempt counters while the job works: they only grow, and a retry counts again. " +
      "The exact count from the stored logs replaces them when the queue drains" +
      (exactAt ? ` (last exact count ${exactAt}).` : ".")
    );
  }
  if (!snapshotAt) return "Live task counters. Refresh to recount from the stored logs.";
  if (!exactAt) return "Stats are based on stored logs.";
  return `Counted from the stored logs, each employee once. Refreshed ${exactAt}.`;
}

// The progress bar's tooltip: the bar and the Success / Errors cells share one story.
function mjJobCountersTooltip(employeeStats: {
  snapshotAt: string | null;
  source: MjCountersSource;
}): string {
  const lead =
    employeeStats.source === "live"
      ? ""
      : "Employees with a status record, each once, over the whole job. ";
  return `${lead}${mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}`;
}

// A dot the operator can read the socket by: green while events arrive, amber
// while the panel polls instead. The heartbeat behind it is verified through the
// channel layer, so "on" means the whole path works, not just an open socket.
function LiveUpdatesIndicator({ connected }: { connected: boolean }) {
  return (
    <Tooltip
      title={
        connected
          ? "Live updates on: statuses and counters arrive over the websocket as the bots report them."
          : "Live updates off: the websocket is down, so the panel polls every 8 s while tasks run and every 32 s otherwise."
      }
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pr: 1, cursor: "help" }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: connected ? "success.main" : "warning.main",
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          {connected ? "Live" : "Polling"}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function mjArtifactRangeWarning(warning: string | undefined | null): string | null {
  const trimmed = (warning ?? "").trim();
  if (!trimmed) return null;
  if (/employees_info\.json has not been created/i.test(trimmed)) return null;
  return trimmed;
}


function mjJobStatsTotals(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  // Queued tasks count as running: the job is mid-flight and must offer Stop.
  const running =
    Number(stats?.running_tasks ?? 0) + Number(stats?.queued_tasks ?? 0);
  const total = Number(stats?.total_tasks ?? 0);
  return { running, total };
}

function normalizeRawTaskToMj(t: Record<string, unknown>, fallbackJobId: number): MjConnectionTask {
  const jobVal = t.job;
  const jid =
    typeof jobVal === "number"
      ? jobVal
      : jobVal != null && typeof jobVal === "object" && "id" in (jobVal as object)
        ? Number((jobVal as { id?: unknown }).id)
        : fallbackJobId;
  const connVal = t.connection;
  const cid =
    typeof connVal === "number"
      ? connVal
      : connVal != null && typeof connVal === "object" && "id" in (connVal as object)
        ? Number((connVal as { id?: unknown }).id)
        : undefined;
  return {
    id: Number(t.id),
    job: Number.isFinite(jid) ? jid : fallbackJobId,
    task_status: String(t.task_status ?? ""),
    bot_work_status: String(t.bot_work_status ?? ""),
    task2fa_status: typeof t.task2fa_status === "string" ? t.task2fa_status : "not_requested",
    task2fa_auto_until: typeof t.task2fa_auto_until === "string" ? t.task2fa_auto_until : null,
    live_view_status: typeof t.live_view_status === "string" ? t.live_view_status : "not_requested",
    live_view_phase: typeof t.live_view_phase === "string" ? t.live_view_phase : "",
    started_by: typeof t.started_by === "number" ? t.started_by : null,
    bot_name: String(t.bot_name ?? ""),
    employee_number_from: Number(t.employee_number_from ?? 0),
    employee_number_to: Number(t.employee_number_to ?? 0),
    connection: cid,
    range_role: typeof t.range_role === "string" ? t.range_role : "EMPLOYEES",
    successfully_processed_employees_count: Number(t.successfully_processed_employees_count ?? 0),
    error_employees_count: Number(t.error_employees_count ?? 0),
    success_employees_snapshot: numberOrNull(t.success_employees_snapshot),
    error_employees_snapshot: numberOrNull(t.error_employees_snapshot),
    retry_error_employees: t.retry_error_employees === true,
    additional_options: typeof t.additional_options === "string" ? t.additional_options : null,
    browser_profile_id: numberOrNull(t.browser_profile_id),
    started_by_username: typeof t.started_by_username === "string" ? t.started_by_username : null,
    document_type: typeof t.document_type === "string" ? t.document_type : "",
    path_to_s3_base_folder: typeof t.path_to_s3_base_folder === "string" ? t.path_to_s3_base_folder : "",
    use_ssn_match: t.use_ssn_match !== false,
    upload_as_zip: t.upload_as_zip === true,
    unzip_folder_before_uploading: t.unzip_folder_before_uploading === true,
    committed_from: numberOrNull(t.committed_from),
    committed_to: numberOrNull(t.committed_to),
    queue_run_source: typeof t.queue_run_source === "string" ? t.queue_run_source : "",
  };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function mjTimeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return "";
  const min = Math.round((Date.now() - ts.getTime()) / 60000);
  const when =
    min < 1
      ? "just now"
      : min < 60
        ? `${min} min ago`
        : min < 60 * 24
          ? `${Math.round(min / 60)} h ago`
          : `${Math.round(min / (60 * 24))} d ago`;
  return `${when} (${ts.toLocaleString()})`;
}

const MJ_TASK_LIVE_STATUSES = ["ECS_CONTAINER_STARTED", "PROCESSING"];

// Per-task numbers: the stored-log snapshot once the task is not working, the
// live attempt counters while it is. The snapshot is written when the job's
// queue drains or on Refresh - the same moment as the job row's numbers.
function mjTaskEmployeeProgress(
  task: MjConnectionTask,
  jobSnapshotAt: string | null | undefined,
  rangePending = false,
) {
  const rosterOnly = String(task.range_role ?? "").toUpperCase() === "ROSTER_ONLY";
  // Bootstrap bounds are a guess, not a size: no denominator until the roster is in.
  const rangeSize =
    rosterOnly || task.employee_number_from > task.employee_number_to
      ? 0
      : rangePending
        ? null
        : task.employee_number_to - task.employee_number_from + 1;
  const running = MJ_TASK_LIVE_STATUSES.includes(
    String(task.task_status ?? "").toUpperCase(),
  );
  const hasSnapshot =
    task.success_employees_snapshot != null && task.error_employees_snapshot != null;
  const useSnapshot = hasSnapshot && !running;
  const success = Number(
    useSnapshot ? task.success_employees_snapshot : task.successfully_processed_employees_count ?? 0,
  );
  const error = Number(
    useSnapshot ? task.error_employees_snapshot : task.error_employees_count ?? 0,
  );
  const when = mjTimeAgo(jobSnapshotAt);
  const tooltip = useSnapshot
    ? `Counted from the stored logs, each employee once, over the range this task covers now; an error a later run completed counts as a success.${when ? ` Refreshed ${when}.` : ""}`
    : "Live attempt counters - they only grow, and a retry counts again. The stored-log numbers appear when the job's queue drains or after Refresh.";
  return { success, error, processed: success + error, rangeSize, rosterOnly, useSnapshot, tooltip };
}

// Employee folders under the job's data folder, as Data Storage knows them
// (the index answers; S3 is listed only for a folder the index does not know).
function mjEmployeeFolders(job: Record<string, unknown>): number | null {
  const stats = job.stats as { employee_folders?: unknown } | undefined;
  return numberOrNull(stats?.employee_folders);
}

// Options stored on a job or task, labelled the way Edit job labels them.
function mjBotOptionTags(raw: unknown, specs: BotOptionSpec[]) {
  return summarizeBotOptions(specs, typeof raw === "string" ? raw : "", MJ_OPTION_SUMMARY_OMIT)
    .filter((line) => line.set)
    .map((line) => {
      const full = botOptionTagText(line);
      const label = full.length > 56 ? `${full.slice(0, 53)}...` : full;
      return (
        <SoftTag
          key={line.key}
          mono={!line.declared}
          icon={<TuneIcon />}
          label={label}
          title={line.help ? `${full}. ${line.help}` : full}
        />
      );
    });
}

// The two switches of Edit job -> Upload options: they decide what actually
// lands on the portal (one archive per employee, and whether a stored .zip is
// unpacked first), so an upload job wears them next to its SSN mode. Both are
// job columns, not additional_options - the option summary never shows them.
function mjUploadFlagTags(job: Record<string, unknown>) {
  if (!mjIsUploadJob(job)) return null;
  const asZip = job.upload_as_zip === true;
  const unzipFirst = job.unzip_folder_before_uploading === true;
  return (
    <>
      <SoftTag
        tone={asZip ? "info" : "neutral"}
        icon={<FolderZipIcon />}
        label={`Upload as ZIP: ${asZip ? "on" : "off"}`}
        title="On: the employee's files go to the portal as one archive. Off: file by file. Edit job -> Upload options."
      />
      <SoftTag
        tone={unzipFirst ? "info" : "neutral"}
        icon={<UnarchiveIcon />}
        label={`Unzip before uploading: ${unzipFirst ? "on" : "off"}`}
        title="On: an archive stored in Data Storage is unpacked and its files are uploaded. Off: the archive is uploaded as it is. Edit job -> Upload options."
      />
    </>
  );
}

// What a task is about, for the info tooltip in its row.
function mjTaskDetailLines(task: MjConnectionTask, specs: BotOptionSpec[]): string[] {
  const lines: string[] = [];
  const kind = String(task.bot_name ?? "").split(".")[0];
  const options = String(task.additional_options ?? "");
  if (task.document_type) lines.push(`Document type: ${task.document_type}`);
  if (kind === "upload_bots" && task.path_to_s3_base_folder) {
    lines.push(`Source folder: ${task.path_to_s3_base_folder}`);
  }
  if (kind === "delete_bots") {
    const docs = readListOption(options, "DOCS_TYPE");
    if (docs.length) lines.push(`Document types to delete: ${docs.join(", ")}`);
  }
  if (kind === "upload_bots") {
    lines.push(`SSN match: ${task.use_ssn_match === false ? "off" : "on"}`);
    lines.push(`Upload as ZIP: ${task.upload_as_zip ? "on" : "off"}`);
    lines.push(`Unzip before uploading: ${task.unzip_folder_before_uploading ? "on" : "off"}`);
  }
  const sticky = extractSkipErrorEmployees(options).processErrors;
  lines.push(
    `Error mode: ${
      task.retry_error_employees ? "on (automatic error pass)" : sticky ? "on (job switch)" : "off"
    }`,
  );
  if (task.browser_profile_id != null) lines.push(`Browser profile: ${task.browser_profile_id}`);
  if (task.started_by_username) lines.push(`Started by: ${task.started_by_username}`);
  if (task.queue_run_source) lines.push(`Run source: ${task.queue_run_source}`);
  for (const line of summarizeBotOptions(specs, options, MJ_OPTION_SUMMARY_OMIT)) {
    if (line.set) lines.push(botOptionTagText(line));
  }
  return lines;
}

function botNameFromRegistry(row: BotRegistryItem | { vendor: string; job_kind: string; bot_slug: string }) {
  return `${row.job_kind}.${row.bot_slug}`;
}

function getErr(error: unknown, fallback: string): string {
  const stringify = (data: unknown): string | undefined => {
    if (data == null || data === "") return undefined;
    if (typeof data === "string") return data;
    if (typeof data === "number" || typeof data === "boolean") return String(data);
    if (Array.isArray(data)) {
      const parts = data.map((x) => stringify(x)).filter(Boolean) as string[];
      return parts.length ? parts.join(" ") : undefined;
    }
    if (typeof data === "object") {
      const o = data as Record<string, unknown>;
      for (const key of ["detail", "message", "error"]) {
        const inner = o[key];
        const s = stringify(inner);
        if (s) return s;
      }
      const parts: string[] = [];
      for (const [k, v] of Object.entries(o)) {
        if (k === "detail" || k === "message" || k === "error") continue;
        const s = stringify(v);
        if (s) parts.push(`${k}: ${s}`);
      }
      if (parts.length) return parts.join("; ");
    }
    return undefined;
  };
  const wrapped = error as { data?: unknown } | undefined;
  return stringify(wrapped?.data) || fallback;
}

export type MajorJobsPanelProps = {
  userId: number;
  userLabel?: string;
  connections: AdminVendorConnection[];
  connectionsLoading?: boolean;
  connectionsError?: boolean;
  onRefreshConnections: () => Promise<unknown>;
  /** Full-page workspace for one major job (opened via route). */
  detailMajorJobId?: number | null;
  onExitMajorDetail?: () => void;
  /** Opens major job workspace - typically navigate to `/bots-management/:userId/major-job/:id`. */
  onOpenMajorDetail?: (majorJobId: number) => void;
  /** Switches the page to the standalone Connections workspace for this user. */
  onOpenConnectionsScreen?: () => void;
};

/** Major jobs + task jobs for one Django user (vendor-scoped pools). */
export default function MajorJobsPanel({
  userId,
  userLabel,
  connections,
  connectionsLoading = false,
  connectionsError = false,
  onRefreshConnections,
  detailMajorJobId = null,
  onExitMajorDetail,
  onOpenMajorDetail,
  onOpenConnectionsScreen,
}: MajorJobsPanelProps) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useAppNavigate();
  const location = useLocation();
  const { profile: botsProfile, readOnly: botsReadOnly, realProfile } = useViewAs();
  const canEditBots = isSuperAdmin(botsProfile) && !botsReadOnly;
  // The initiator comparison and the live-view button follow the REAL
  // operator, never the Client View impersonation: the backend URL gate
  // (TaskLiveViewStatusSerializer._viewer_may_control) works the same way.
  const myPlatformUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  const canOpenLiveView = isSuperAdmin(realProfile);
  const { data: majorsRaw, refetch, isFetching: majorsFetching } = useGetMajorJobsQuery(userId, {
    refetchOnMountOrArgChange: true,
  });
  const majors = useMemo(() => {
    if (majorsRaw == null) return [];
    if (Array.isArray(majorsRaw)) return majorsRaw;
    const r = (majorsRaw as { results?: MajorJob[] }).results;
    return Array.isArray(r) ? r : [];
  }, [majorsRaw]);
  const [createMajorJob, { isLoading: isCreating }] = useCreateMajorJobMutation();
  const [updateMajorJob, { isLoading: isSavingMajor }] = useUpdateMajorJobMutation();
  const [deleteMajorJob, { isLoading: isDeletingMajor }] = useDeleteMajorJobMutation();

  const [createConnectionMutation, { isLoading: isMpcCreating }] =
    useCreateBackendUserBotConnectionMutation();
  const [updateConnectionMutation, { isLoading: isMpcUpdating }] =
    useUpdateAdminConnectionMutation();
  const [deleteConnectionMutation, { isLoading: isMpcDeleting }] =
    useDeleteAdminConnectionMutation();

  const [selectedMajorIds, setSelectedMajorIds] = useState<number[]>([]);
  const [activeMajorId, setActiveMajorId] = useState<number | null>(null);

  const [majorEditOpen, setMajorEditOpen] = useState(false);
  const [majorEditTarget, setMajorEditTarget] = useState<MajorJob | null>(null);
  const [majName, setMajName] = useState("");
  const [majVendor, setMajVendor] = useState("");
  const [majKind, setMajKind] = useState("download_bots");
  const [majorFormError, setMajorFormError] = useState<string | null>(null);
  const [majorRowDeleteTarget, setMajorRowDeleteTarget] = useState<MajorJob | null>(null);
  const [majorBulkDeleteOpen, setMajorBulkDeleteOpen] = useState(false);

  const { data: majorFormAllRegistry } = useGetBotRegistryQuery(
    { isActive: true, pageSize: 500 },
    { skip: !majorEditOpen },
  );
  const registryVendorsBase = useMemo(() => {
    const rows = normalizeBotRegistryPayload(majorFormAllRegistry);
    return Array.from(new Set(rows.map((r) => r.vendor))).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [majorFormAllRegistry]);

  const majorFormVendorOptions = useMemo(() => {
    const v = majVendor.trim();
    if (!v) return registryVendorsBase;
    const hit = registryVendorsBase.some((x) => x.toLowerCase() === v.toLowerCase());
    if (!hit) return [...registryVendorsBase, v].sort((a, b) => a.localeCompare(b));
    return registryVendorsBase;
  }, [registryVendorsBase, majVendor]);

  const { data: childJobsData, refetch: refetchChildJobs } = useGetTaskJobsQuery(
    // include=tasks embeds every job's task rows, so this one request also
    // feeds the accordions - there is no per-job detail prefetch any more.
    { userId, majorJobId: activeMajorId ?? undefined, includeTasks: true },
    // Refetch on remount so returning from a task detail shows fresh task-job data.
    { skip: activeMajorId == null, refetchOnMountOrArgChange: true },
  );
  const childJobs = useMemo(() => {
    if (Array.isArray(childJobsData)) return childJobsData;
    const r = (childJobsData as { results?: unknown[] } | undefined)?.results;
    return Array.isArray(r) ? r : [];
  }, [childJobsData]);
  const childJobsLoading = childJobsData === undefined;

  /** Safe refresh — `refetch()` throws if the subscribed query was skipped (no active major yet). */
  const refreshChildJobsList = useCallback(async () => {
    const majorJobId = activeMajorId ?? detailMajorJobId ?? null;
    if (majorJobId == null) return;
    try {
      // The args must stay identical to the hook's above: a different arg
      // object is a different cache entry, and the hook would never see it.
      await dispatch(
        adminUsersApi.endpoints.getTaskJobs.initiate(
          { userId, majorJobId, includeTasks: true },
          { forceRefetch: true },
        ),
      ).unwrap();
    } catch {
      // ignore — list may not be mounted yet
    }
  }, [activeMajorId, detailMajorJobId, userId, dispatch]);

  const safeRefetchChildJobs = useCallback(async () => {
    if (activeMajorId == null) {
      await refreshChildJobsList();
      return;
    }
    try {
      await refetchChildJobs();
    } catch {
      await refreshChildJobsList();
    }
  }, [activeMajorId, refetchChildJobs, refreshChildJobsList]);

  const [createTasksBatch] = useCreateTasksBatchMutation();
  const [updateTaskJob, { isLoading: isMjEditJobSaving }] = useUpdateTaskJobMutation();
  const [refreshTaskJobStats] = useRefreshTaskJobStatsMutation();
  const [mjProcessErrorsSavingJobId, setMjProcessErrorsSavingJobId] = useState<
    number | null
  >(null);
  const [mjRowBatchBusyJobId, setMjRowBatchBusyJobId] = useState<number | null>(null);

  // Sticky mode, not the one-shot pass the Edit job dialog arms: while it is on,
  // every run of this job - Run all or a single task - carries
  // SKIP_ERROR_EMPLOYEES=False, so employees that errored before are retried.
  // The job PATCH copies additional_options down to all of its tasks.
  const toggleJobProcessErrors = async (
    jobId: number,
    rawOptions: string,
    processErrors: boolean,
  ) => {
    setMjProcessErrorsSavingJobId(jobId);
    try {
      const { remaining } = extractSkipErrorEmployees(rawOptions);
      await updateTaskJob({
        jobId,
        body: {
          additional_options: applySkipErrorEmployees(remaining, processErrors),
        },
      }).unwrap();
      // The row renders the list row merged with the cached job detail, and the
      // detail wins - without this the switch snaps back to its old position.
      // Silent: the PATCH already landed, so a failed follow-up read must not
      // claim the change failed.
      await refreshMjJobDetailRef.current(jobId, false, true);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not change 'Process error EEs'."),
      }));
    } finally {
      setMjProcessErrorsSavingJobId(null);
    }
  };
  const [runJobNewRows] = useRunJobNewRowsMutation();
  const [runJobRosterTask] = useRunJobRosterTaskMutation();
  const [mjRosterTaskBusyJobId, setMjRosterTaskBusyJobId] = useState<number | null>(null);
  // Which job the "Build roster only?" confirmation is open for.
  const [mjRosterConfirmJobId, setMjRosterConfirmJobId] = useState<number | null>(null);
  const [mjRosterExistsPrompt, setMjRosterExistsPrompt] = useState<{
    jobId: number;
    files: string[];
    message: string;
  } | null>(null);
  const [fetchJobRoster] = useLazyGetJobRosterQuery();
  const [deleteJobRosterMut] = useDeleteJobRosterMutation();
  const [presignArtifactUpload] = usePresignJobArtifactUploadMutation();
  const [replaceJobMatchesMut] = useReplaceJobMatchesMutation();
  const [replaceJobRosterMut] = useReplaceJobRosterMutation();
  const [fetchJobSsnReadiness] = useLazyGetJobSsnReadinessQuery();
  const [fetchMajorSsnReadiness] = useLazyGetMajorJobSsnReadinessQuery();
  const [fetchBrowserProfiles] = useLazyGetMajorJobBrowserProfilesQuery();
  const [deleteBrowserProfilesMut] = useDeleteMajorJobBrowserProfilesMutation();
  // The stored file of the job open in Manage job, and the one-line notice
  // after it was deleted or replaced.
  // Stored roster / Matches file facts per job: Manage job reads them, and
  // the Build roster only buttons are greyed out while such a file exists.
  const [mjRosterInfoByJobId, setMjRosterInfoByJobId] = useState<Record<number, JobRosterInfo>>({});
  const [mjRosterInfoLoading, setMjRosterInfoLoading] = useState(false);
  const [mjRosterNotice, setMjRosterNotice] = useState<string | null>(null);
  const [mjRosterDeletePrompt, setMjRosterDeletePrompt] = useState<{ jobId: number } | null>(null);
  const [mjRosterDeleting, setMjRosterDeleting] = useState(false);
  const [mjArtifactUpload, setMjArtifactUpload] = useState<{
    jobId: number;
    purpose: "matches" | "roster";
    file: File | null;
    step: "idle" | "uploading" | "ready" | "replacing" | "done" | "error";
    stagingKey: string | null;
    error: string | null;
    reindex: { message: string; committed: number[] } | null;
    confirmReindex: boolean;
    result: string | null;
  } | null>(null);
  const [mjBrowserProfiles, setMjBrowserProfiles] = useState<BrowserProfilesInfo | null>(null);
  const [mjBrowserProfilesPrompt, setMjBrowserProfilesPrompt] = useState(false);
  const [mjBrowserProfilesBusy, setMjBrowserProfilesBusy] = useState(false);
  const [mjBrowserProfilesError, setMjBrowserProfilesError] = useState<string | null>(null);
  // SSN readiness per upload job (fetched when a job row is expanded or managed),
  // and the "run by names?" question asked before a run that would build matches.
  const [mjSsnReadinessByJobId, setMjSsnReadinessByJobId] = useState<Record<number, SsnReadiness>>({});
  const mjSsnReadinessRequestedRef = useRef<Set<number>>(new Set());
  const [mjSsnRunPrompt, setMjSsnRunPrompt] = useState<{
    readiness: SsnReadiness;
    label: string;
    proceed: () => Promise<void> | void;
  } | null>(null);
  const [childSsnReadiness, setChildSsnReadiness] = useState<SsnReadiness | null>(null);
  const [mjEditSsnReadiness, setMjEditSsnReadiness] = useState<SsnReadiness | null>(null);
  const [mjNewRowsRunningJobId, setMjNewRowsRunningJobId] = useState<number | null>(null);
  // The vendor's own limit on parallel sessions is the operator's to know, so
  // the number of tasks is asked for rather than derived.
  const [mjNewRowsJob, setMjNewRowsJob] = useState<{
    id: number;
    rows: number;
  } | null>(null);
  const [mjNewRowsTaskCount, setMjNewRowsTaskCount] = useState("1");
  const runNewRosterRows = async (jobId: number, taskCount: number) => {
    setMjNewRowsJob(null);
    setMjNewRowsRunningJobId(jobId);
    try {
      await runJobNewRows({ jobId, taskCount }).unwrap();
      await refreshMjJobDetailRef.current(jobId, false, true);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not run the new roster rows."),
      }));
    } finally {
      setMjNewRowsRunningJobId(null);
    }
  };

  const mjRunRosterTask = async (jobId: number, force = false) => {
    setMjRosterConfirmJobId(null);
    setMjRosterExistsPrompt(null);
    setMjRosterTaskBusyJobId(jobId);
    setMjJobActionAlertsByJobId((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    try {
      await runJobRosterTask({ jobId, force }).unwrap();
      await refreshMjJobDetailRef.current(jobId, true, true);
      await reloadMjAccordionTasksRef.current(jobId);
    } catch (e) {
      const data = (e as { data?: { code?: string; files?: string[]; message?: string } })
        ?.data;
      if (data?.code === "roster_exists") {
        setMjRosterExistsPrompt({
          jobId,
          files: data.files ?? [],
          message: data.message ?? "The roster is already built.",
        });
      } else {
        setMjJobActionAlertsByJobId((prev) => ({
          ...prev,
          [jobId]: getErr(e, "Could not start the roster task."),
        }));
      }
    } finally {
      setMjRosterTaskBusyJobId(null);
    }
  };

  const mjJobRowById = (jobId: number): Record<string, unknown> | null => {
    const raw = childJobs.find((r) => Number((r as Record<string, unknown>).id) === jobId);
    if (!raw) return null;
    return { ...(raw as Record<string, unknown>), ...(mjJobDetailById[jobId] ?? {}) };
  };

  const loadMjRosterInfo = useCallback(
    async (jobId: number) => {
      setMjRosterInfoLoading(true);
      try {
        const info = await fetchJobRoster(jobId).unwrap();
        setMjRosterInfoByJobId((prev) => ({ ...prev, [jobId]: info }));
        if (info.kind === "matches") {
          try {
            const readiness = await fetchJobSsnReadiness(jobId).unwrap();
            setMjSsnReadinessByJobId((prev) => ({ ...prev, [jobId]: readiness }));
          } catch {
            // Readiness is advisory; the file line still renders.
          }
        }
      } catch {
        setMjRosterInfoByJobId((prev) => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
      } finally {
        setMjRosterInfoLoading(false);
      }
    },
    [fetchJobRoster, fetchJobSsnReadiness],
  );

  const confirmMjRosterDelete = async () => {
    const jobId = mjRosterDeletePrompt?.jobId;
    if (jobId == null) return;
    setMjRosterDeleting(true);
    try {
      const res = await deleteJobRosterMut(jobId).unwrap();
      setMjRosterDeletePrompt(null);
      setMjManageJobError(null);
      const shared = res.sibling_job_ids.length
        ? ` Job(s) ${res.sibling_job_ids.map((id) => `#${id}`).join(", ")} read the same folder and start over too.`
        : "";
      setMjRosterNotice(
        `${res.deleted_keys.length} file(s) deleted. The next run collects the roster again.${shared}`,
      );
      await refreshMjJobDetailRef.current(jobId, true, true);
      await loadMjRosterInfo(jobId);
      if (mjRosterInfo?.info.kind === "matches") await loadMatchesFiles(jobId);
    } catch (e) {
      setMjRosterDeletePrompt(null);
      setMjManageJobError(getErr(e, "Could not delete the stored file."));
    } finally {
      setMjRosterDeleting(false);
    }
  };

  const openMjArtifactUpload = (jobId: number, purpose: "matches" | "roster") =>
    setMjArtifactUpload({
      jobId,
      purpose,
      file: null,
      step: "idle",
      stagingKey: null,
      error: null,
      reindex: null,
      confirmReindex: false,
      result: null,
    });

  const handleMjArtifactFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file || !mjArtifactUpload) return;
    const { jobId, purpose } = mjArtifactUpload;
    setMjArtifactUpload((prev) =>
      prev ? { ...prev, file, step: "uploading", error: null, stagingKey: null, result: null } : prev,
    );
    try {
      const presign = await presignArtifactUpload({
        jobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
        purpose,
      }).unwrap();
      const response = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);
      setMjArtifactUpload((prev) => (prev ? { ...prev, step: "ready", stagingKey: presign.key } : prev));
    } catch (err) {
      setMjArtifactUpload((prev) =>
        prev ? { ...prev, step: "error", error: getErr(err, "Upload failed.") } : prev,
      );
    }
  };

  const confirmMjArtifactReplace = async () => {
    const state = mjArtifactUpload;
    if (!state?.stagingKey) return;
    setMjArtifactUpload((prev) => (prev ? { ...prev, step: "replacing", error: null } : prev));
    try {
      const body = {
        jobId: state.jobId,
        staging_key: state.stagingKey,
        confirm_reindex: state.confirmReindex,
      };
      const result =
        state.purpose === "matches"
          ? await replaceJobMatchesMut({ ...body, file_name: state.file?.name }).unwrap()
          : await replaceJobRosterMut(body).unwrap();
      setMjArtifactUpload((prev) =>
        prev
          ? {
              ...prev,
              step: "done",
              stagingKey: null,
              result: `${result.rows} row(s) written. The job now covers ${
                result.actual_total_employees ?? "-"
              } employee(s).`,
            }
          : prev,
      );
      await refreshMjJobDetailRef.current(state.jobId, true, true);
      await loadMjRosterInfo(state.jobId);
      if (state.purpose === "matches") await loadMatchesFiles(state.jobId);
    } catch (err) {
      const data = (
        err as { data?: { code?: string; message?: string; committed_tasks?: number[] } }
      )?.data;
      if (data?.code === "confirm_reindex_required") {
        setMjArtifactUpload((prev) =>
          prev
            ? {
                ...prev,
                step: "ready",
                reindex: { message: data.message ?? "", committed: data.committed_tasks ?? [] },
              }
            : prev,
        );
        return;
      }
      // The staged upload is consumed by the attempt: a retry needs the file again.
      setMjArtifactUpload((prev) =>
        prev
          ? { ...prev, step: "error", stagingKey: null, error: getErr(err, "Replace failed.") }
          : prev,
      );
    }
  };

  const loadMjBrowserProfiles = useCallback(
    async (majorJobId: number) => {
      try {
        setMjBrowserProfiles(await fetchBrowserProfiles(majorJobId).unwrap());
      } catch {
        setMjBrowserProfiles(null);
      }
    },
    [fetchBrowserProfiles],
  );

  const confirmMjBrowserProfilesDelete = async () => {
    if (!activeMajor) return;
    setMjBrowserProfilesBusy(true);
    try {
      const res = await deleteBrowserProfilesMut({ majorJobId: activeMajor.id }).unwrap();
      setMjBrowserProfiles(res);
      setMjBrowserProfilesPrompt(false);
      setMjBrowserProfilesError(null);
    } catch (e) {
      setMjBrowserProfilesPrompt(false);
      setMjBrowserProfilesError(getErr(e, "Could not delete the browser profile."));
    } finally {
      setMjBrowserProfilesBusy(false);
    }
  };

  // Before a run that would build the matches file by name, ask. A run that
  // reuses an existing Matches file matches nothing, so it is not asked.
  const mjSsnGuardedRun = async (
    job: Record<string, unknown> | null | undefined,
    label: string,
    proceed: () => Promise<void> | void,
  ) => {
    if (!job || !mjIsUploadJob(job)) {
      await proceed();
      return;
    }
    const jobId = Number(job.id);
    let readiness: SsnReadiness | null = mjSsnReadinessByJobId[jobId] ?? null;
    try {
      readiness = await fetchJobSsnReadiness(jobId).unwrap();
      const fresh = readiness;
      setMjSsnReadinessByJobId((prev) => ({ ...prev, [jobId]: fresh }));
    } catch {
      // Unknown readiness must not block a run.
    }
    if (readiness && readiness.algorithm === "names" && readiness.matches_file_exists === false) {
      setMjSsnRunPrompt({ readiness, label, proceed });
      return;
    }
    await proceed();
  };

  const [taskJobsBatchDelete, { isLoading: isDeletingJobsBulk }] =
    useTaskJobsBatchDeleteMutation();

  const [triggerJobLogsArchive, { isFetching: isMjLogsArchiveLoading }] =
    useLazyGetJobLogsArchiveQuery();
  const [clearJobLogsMut, { isLoading: isMjClearLogsLoading }] = useClearJobLogsMutation();
  const [fetchJobLogsInventory] = useLazyGetJobLogsInventoryQuery();
  const [mjClearLogsJobId, setMjClearLogsJobId] = useState<number | null>(null);
  // The job's log folder is shared with every other job of the same document type, so
  // the dialog has to say what is in there before anything is deleted.
  const [mjClearLogsInventory, setMjClearLogsInventory] = useState<{
    own: number;
    shared: number;
  } | null>(null);
  const [mjClearLogsIncludeShared, setMjClearLogsIncludeShared] = useState(false);

  const [deleteTaskJobMut, { isLoading: isMjDeletingJob }] = useDeleteTaskJobMutation();
  const [deleteTaskMutation, { isLoading: isDeletingNestedTask }] = useDeleteTaskMutation();
  const [tasksBatchDeleteNestMut, { isLoading: isMjNestedTasksBatchDeleting }] = useTasksBatchDeleteMutation();
  const [addTasksToJobMutation, { isLoading: isAddingTasksToJob }] = useAddTasksToJobMutation();
  const [runTaskMutation, { isLoading: isRunTaskBusy }] = useRunTaskMutation();
  const [stopTaskMutation, { isLoading: isStopTaskBusy }] = useStopTaskMutation();
  const [updateAdminTaskMut, { isLoading: isUpdatingTaskRow }] = useUpdateAdminTaskMutation();
  const [runTasksBatchMut, { isLoading: isMjBatchRunning }] = useRunTasksBatchMutation();
  const [stopTasksBatchMut, { isLoading: isMjBatchStopping }] = useStopTasksBatchMutation();
  const [submitTask2FACodeMut, { isLoading: isSubmit2FAMj }] = useSubmitTask2FACodeMutation();
  const [expandedMjJobAccordion, setExpandedMjJobAccordion] = useState<Record<number, boolean>>({});
  const [mjAccordionTasksByJob, setMjAccordionTasksByJob] = useState<Record<number, MjConnectionTask[]>>({});
  const [mjAccordionLoadingJobId, setMjAccordionLoadingJobId] = useState<number | null>(null);
  /** Timestamp (ms) of last successful accordion tasks fetch — used for 30-second cache. */
  const [mjAccordionCacheTs, setMjAccordionCacheTs] = useState<Record<number, number>>({});
  // The prefetch below reads freshness from a callback that outlives its render,
  // so it needs the live value, not the one captured back then. Written together
  // with the state, never through an effect: a prefetch answer can land between
  // a reload's setState and the following render.
  const mjAccordionCacheTsRef = useRef<Record<number, number>>({});
  const markMjAccordionFresh = useCallback((jobId: number) => {
    const now = Date.now();
    mjAccordionCacheTsRef.current = { ...mjAccordionCacheTsRef.current, [jobId]: now };
    setMjAccordionCacheTs((prev) => ({ ...prev, [jobId]: now }));
  }, []);
  /** Task ID that is currently being run or stopped (for per-row spinner). */
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  /** Coverage / employees-info warnings keyed by task job id (from API stats). */
  const [mjJobRangeWarningsByJobId, setMjJobRangeWarningsByJobId] = useState<Record<number, string>>({});
  /** Run/stop/delete errors shown inside each job accordion row. */
  const [mjJobActionAlertsByJobId, setMjJobActionAlertsByJobId] = useState<Record<number, string>>({});
  // Advisory answers to a row action, e.g. "queued behind the roster task".
  const [mjJobNoticesByJobId, setMjJobNoticesByJobId] = useState<Record<number, string>>({});
  const [mjRefreshingJobIds, setMjRefreshingJobIds] = useState<Record<number, boolean>>({});
  const [mjJobDetailById, setMjJobDetailById] = useState<Record<number, Record<string, unknown>>>({});
  const [mjWsConnected, setMjWsConnected] = useState(() => botsManagementWs.isConnected());
  const mjWsDebounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const expandedMjJobAccordionRef = useRef<Record<number, boolean>>({});
  const mjAccordionTasksByJobRef = useRef<Record<number, MjConnectionTask[]>>({});
  const reloadMjAccordionTasksRef = useRef<(jobId: number) => Promise<MjConnectionTask[] | null>>(
    async () => null,
  );
  const refreshMjJobDetailRef = useRef<
    (jobId: number, refreshList?: boolean, silent?: boolean) => Promise<void>
  >(
    async () => undefined,
  );
  /** Job IDs with an accordion reload currently in flight — dedupes rapid open/close/open. */
  const mjAccordionInFlightRef = useRef<Set<number>>(new Set());
  // Jobs counted on their first open this session (no stats snapshot yet).
  const mjAutoScannedJobsRef = useRef<Set<number>>(new Set());
  const userIdRef = useRef(userId);
  const activeMajorForWsRef = useRef<number | null>(null);
  // The first "connected" after the page load is not a reconnect: the data on
  // screen came from REST moments earlier, so it needs no catch-up refetch.
  const hasSeenWsConnectedRef = useRef(botsManagementWs.isConnected());
  /** Latest reload sequence per job, so an out-of-order response is dropped. */
  const mjAccordionSeqRef = useRef<Record<number, number>>({});
  const safeRefetchChildJobsRef = useRef<() => Promise<void>>(async () => undefined);

  const [mjManageJobId, setMjManageJobId] = useState<number | null>(null);
  const mjRosterInfo =
    mjManageJobId != null && mjRosterInfoByJobId[mjManageJobId]
      ? { jobId: mjManageJobId, info: mjRosterInfoByJobId[mjManageJobId] }
      : null;
  const [mjManageJobDetail, setMjManageJobDetail] = useState<Record<string, unknown> | null>(null);
  const [mjManageJobError, setMjManageJobError] = useState<string | null>(null);
  const [mjManageJobNotice, setMjManageJobNotice] = useState<string | null>(null);

  const [mjAddTasksOpen, setMjAddTasksOpen] = useState(false);
  const [mjAddTasksJobId, setMjAddTasksJobId] = useState<number | null>(null);
  const [mjAddTasksLoading, setMjAddTasksLoading] = useState(false);
  const [mjBulkAddRows, setMjBulkAddRows] = useState<ConnPlanEditorRow[]>([
    { connection_id: "", task_target_count: "1" },
  ]);
  const [mjAddPlanConnPool, setMjAddPlanConnPool] = useState<number[]>([]);
  const [mjAddTasksErr, setMjAddTasksErr] = useState<string | null>(null);

  const [mjDeleteJobId, setMjDeleteJobId] = useState<number | null>(null);

  const [mjEditJobOpen, setMjEditJobOpen] = useState(false);
  const [mjEditJobId, setMjEditJobId] = useState<number | null>(null);
  const [mjEditBaselineFrom, setMjEditBaselineFrom] = useState(0);
  const [mjEditJobName, setMjEditJobName] = useState("");
  const [mjEditBotName, setMjEditBotName] = useState("");
  const [mjEditEmployeeTo, setMjEditEmployeeTo] = useState("");
  const [mjEditDocumentType, setMjEditDocumentType] = useState("");
  const [mjEditDocumentTypes, setMjEditDocumentTypes] = useState<string[]>([" "]);
  const [mjEditAdditionalOptions, setMjEditAdditionalOptions] = useState("");
  const [mjEditProcessErrors, setMjEditProcessErrors] = useState(false);
  const [mjEditSeparateProfiles, setMjEditSeparateProfiles] = useState(false);
  const [mjEditUploadAsZip, setMjEditUploadAsZip] = useState(false);
  const [mjEditUnzipBefore, setMjEditUnzipBefore] = useState(false);
  const [mjEditUseSsnMatch, setMjEditUseSsnMatch] = useState(true);
  const [mjEditSourcePath, setMjEditSourcePath] = useState("");
  const [mjEditJobError, setMjEditJobError] = useState<string | null>(null);

  const [mjDeleteSingleTaskId, setMjDeleteSingleTaskId] = useState<number | null>(null);
  /** Task ids selected inside a child job accordion (for batch delete). */
  const [mjNestedTaskSelectionByJob, setMjNestedTaskSelectionByJob] = useState<Record<number, number[]>>({});
  const [mjBatchDeleteNestDialog, setMjBatchDeleteNestDialog] = useState<{ jobId: number; ids: number[] } | null>(null);
  const [twoFaMjTask, setTwoFaMjTask] = useState<MjConnectionTask | null>(null);
  // Captcha the bot cannot solve: the operator solves it in the bot's browser.
  const [liveViewMjTaskId, setLiveViewMjTaskId] = useState<number | null>(null);
  // Tasks whose window the operator closed by hand - the derived rule below
  // would otherwise put it straight back up on the next poll.
  const [liveViewMjDismissed, setLiveViewMjDismissed] = useState<number[]>([]);
  const [twoFaMjCode, setTwoFaMjCode] = useState("");
  const [twoFaMjErr, setTwoFaMjErr] = useState<string | null>(null);
  // Task ids where the operator forced manual 2FA entry during the auto phase.
  const [twoFaManualOverrideIds, setTwoFaManualOverrideIds] = useState<Set<number>>(
    () => new Set(),
  );

  // The Logs popup: the viewer owns its own fetching, filters and refreshing, so
  // the panel only has to say whose logs to show.
  const [logsScope, setLogsScope] = useState<LogsScope | null>(null);
  const [logsTitle, setLogsTitle] = useState<string>("");

  // Error popup (clickable CRITICAL_ERROR status chip)
  const [mjErrorPopupTask, setMjErrorPopupTask] = useState<{ jobId: number; task: MjConnectionTask } | null>(null);

  // Matches tab (inside Manage Job dialog, upload_bots only)
  const [mjManageTab, setMjManageTab] = useState(0);
  const [matchesFiles, setMatchesFiles] = useState<MatchesFileInfo[] | null>(null);
  const [matchesSummary, setMatchesSummary] = useState<MatchesSummary | null>(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  // CSV viewer popup
  const [matchesViewOpen, setMatchesViewOpen] = useState(false);
  const [matchesViewFilename, setMatchesViewFilename] = useState("");
  const [matchesViewRows, setMatchesViewRows] = useState<string[][]>([]);
  const [matchesViewLoading, setMatchesViewLoading] = useState(false);
  // Delete confirmation
  const [matchesDeleteFilename, setMatchesDeleteFilename] = useState<string | null>(null);
  const [matchesDeleting, setMatchesDeleting] = useState(false);
  const [matchesSelectedFiles, setMatchesSelectedFiles] = useState<string[]>([]);
  const [matchesMergeOpen, setMatchesMergeOpen] = useState(false);
  const [matchesMergeFile, setMatchesMergeFile] = useState<File | null>(null);
  const [matchesMergeStagingKey, setMatchesMergeStagingKey] = useState<string | null>(null);
  const [matchesMergeStep, setMatchesMergeStep] = useState<
    "idle" | "uploading" | "ready" | "merging" | "error"
  >("idle");
  const [matchesMergeError, setMatchesMergeError] = useState<string | null>(null);
  const [matchesMergeSuccess, setMatchesMergeSuccess] = useState<string | null>(null);
  const [triggerGetMatchesFileContent] = useLazyGetJobMatchesFileContentQuery();
  const [deleteMatchesFileMut] = useDeleteJobMatchesFileMutation();
  const [presignMatchesMergeMut] = usePresignJobMatchesMergeUploadMutation();
  const [mergeMatchesReportsMut] = useMergeJobMatchesReportsMutation();

  // ── SSN Job tab ────────────────────────────────────────────────────────────
  type SsnJobData = {
    job: Record<string, unknown> | null;
    task: Record<string, unknown> | null;
    ssn_file_exists: boolean | null;
  };
  const [ssnJobData, setSsnJobData] = useState<SsnJobData | null>(null);
  const [ssnJobLoading, setSsnJobLoading] = useState(false);
  const [ssnJobError, setSsnJobError] = useState<string | null>(null);
  const [ssnJobSelectedConnId, setSsnJobSelectedConnId] = useState<number | null>(null);
  const [ssnJobCreating, setSsnJobCreating] = useState(false);
  const [ssnJobDeleting, setSsnJobDeleting] = useState(false);
  const [ssnJobFileDeleting, setSsnJobFileDeleting] = useState(false);
  const [ssnJobDeleteConfirmOpen, setSsnJobDeleteConfirmOpen] = useState(false);
  const [ssnAccordionExpanded, setSsnAccordionExpanded] = useState(false);
  const [createSsnJobMut] = useCreateMajorJobSsnJobMutation();
  const [updateSsnJobMut] = useUpdateMajorJobSsnJobMutation();
  const [deleteSsnJobMut] = useDeleteMajorJobSsnJobMutation();
  const [ssnAdditionalOptions, setSsnAdditionalOptions] = useState("");
  const [ssnOptionsSaving, setSsnOptionsSaving] = useState(false);
  const [ssnJobDialogOpen, setSsnJobDialogOpen] = useState(false);
  const [ssnJobDialogMode, setSsnJobDialogMode] = useState<"create" | "edit">("create");
  const [ssnJobDialogError, setSsnJobDialogError] = useState<string | null>(null);
  // What the local editor was last filled from, so a silent refresh cannot wipe
  // an edit in progress while a real change on the server still lands.
  const ssnOptionsSourceRef = useRef<string | null>(null);
  const [deleteSsnFileMut] = useDeleteMajorJobSsnFileMutation();
  const [triggerGetSsnFileContent] = useLazyGetMajorJobSsnFileContentQuery();
  const [ssnFileViewOpen, setSsnFileViewOpen] = useState(false);
  const [ssnFileViewLoading, setSsnFileViewLoading] = useState(false);
  const [ssnFileViewContent, setSsnFileViewContent] = useState<string | null>(null);
  const ssnJobDataRef = useRef<SsnJobData | null>(null);
  const loadSsnJobRef = useRef<(majorJobId: number, opts?: { silent?: boolean }) => Promise<void>>(async () => {});
  const loadMatchesFilesRef = useRef<(jobId: number) => Promise<void>>(async () => {});
  const ssnMajorIdRef = useRef<number | null>(null);

  const loadSsnJob = async (majorJobId: number, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setSsnJobLoading(true);
      setSsnJobError(null);
      setSsnJobData(null);
    }
    try {
      const res = await dispatch(
        adminUsersApi.endpoints.getMajorJobSsnJob.initiate(majorJobId, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      const next = res as SsnJobData;
      setSsnJobData((prev) => {
        if (prev?.ssn_file_exists !== next.ssn_file_exists) void refetch();
        return next;
      });
    } catch (e: unknown) {
      if (!silent) {
        const msg = (e as { data?: { message?: string } })?.data?.message ?? "Failed to load SSN job.";
        setSsnJobError(msg);
      }
    } finally {
      if (!silent) setSsnJobLoading(false);
    }
  };

  /** Inline draft connection IDs for tasks shown in the per-job accordion table.
   *  Shape: { [taskId]: pendingConnectionId }. A row shows a Save button while
   *  the draft differs from the task's persisted connection. */
  const [taskConnDraftById, setTaskConnDraftById] = useState<Record<number, number>>({});
  const [taskConnSavingId, setTaskConnSavingId] = useState<number | null>(null);
  const [taskConnSaveErrById, setTaskConnSaveErrById] = useState<Record<number, string>>({});

  const setTaskConnDraft = useCallback((taskId: number, cid: number) => {
    setTaskConnDraftById((prev) => ({ ...prev, [taskId]: cid }));
    setTaskConnSaveErrById((prev) => {
      if (!prev[taskId]) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const clearTaskConnDraft = useCallback((taskId: number) => {
    setTaskConnDraftById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setTaskConnSaveErrById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const saveTaskConn = useCallback(
    async (jobId: number, task: MjConnectionTask, newCid: number) => {
      if (!canEditBots) return;
      setTaskConnSavingId(task.id);
      try {
        await updateAdminTaskMut({ taskId: task.id, body: { connection: newCid } }).unwrap();
        setMjAccordionTasksByJob((prev) => {
          const list = prev[jobId];
          if (!list) return prev;
          return {
            ...prev,
            [jobId]: list.map((t) => (t.id === task.id ? { ...t, connection: newCid } : t)),
          };
        });
        clearTaskConnDraft(task.id);
      } catch (e) {
        setTaskConnSaveErrById((prev) => ({
          ...prev,
          [task.id]: getErr(e, "Failed to move the task to the selected connection."),
        }));
      } finally {
        setTaskConnSavingId(null);
      }
    },
    [canEditBots, updateAdminTaskMut, clearTaskConnDraft],
  );

  const mjJobActionBusy =
    isAddingTasksToJob ||
    isRunTaskBusy ||
    isStopTaskBusy ||
    isDeletingNestedTask ||
    isMjNestedTasksBatchDeleting ||
    isMjBatchRunning ||
    isMjBatchStopping ||
    isSubmit2FAMj;

  const openJobLogs = (jobId: number, jobName?: string) => {
    setLogsScope({ kind: "job", jobId });
    setLogsTitle(`Logs - ${jobName || `Job #${jobId}`}`);
  };

  const openTaskLogs = (taskId: number) => {
    setLogsScope({ kind: "task", taskId });
    setLogsTitle(`Logs - Task #${taskId}`);
  };

  // --- Matches files helpers ---

  const loadMatchesFiles = async (jobId: number) => {
    setMatchesLoading(true);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    try {
      const data = await dispatch(
        adminUsersApi.endpoints.getJobMatchesFiles.initiate(jobId, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
      setMatchesFiles(data.files);
      setMatchesSummary(data.summary);
    } catch {
      setMatchesError("Failed to load matches files.");
    } finally {
      setMatchesLoading(false);
    }
  };

  const openMatchesFileViewer = async (jobId: number, filename: string) => {
    setMatchesViewFilename(filename);
    setMatchesViewRows([]);
    setMatchesViewOpen(true);
    setMatchesViewLoading(true);
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      // Parse CSV: split by lines, then by comma
      const lines = res.content.split(/\r?\n/).filter((l) => l.trim() !== "");
      const parsed = lines.map((line) => {
        // Simple CSV parse: handle quoted fields
        const cols: string[] = [];
        let cur = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === "," && !inQuotes) {
            cols.push(cur);
            cur = "";
          } else {
            cur += ch;
          }
        }
        cols.push(cur);
        return cols;
      });
      setMatchesViewRows(parsed);
    } catch {
      setMatchesViewRows([["(failed to load file content)"]]);
    } finally {
      setMatchesViewLoading(false);
    }
  };

  const downloadMatchesFile = async (jobId: number, filename: string) => {
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      const blob = new Blob([res.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const openSsnFileViewer = async (majorJobId: number) => {
    setSsnFileViewOpen(true);
    setSsnFileViewLoading(true);
    setSsnFileViewContent(null);
    try {
      const res = await triggerGetSsnFileContent(majorJobId, false).unwrap();
      let pretty = res.content;
      try {
        pretty = JSON.stringify(JSON.parse(res.content), null, 2);
      } catch {
        /* keep raw content when it is not valid JSON */
      }
      setSsnFileViewContent(pretty);
    } catch {
      setSsnFileViewContent("(failed to load file content)");
    } finally {
      setSsnFileViewLoading(false);
    }
  };

  const downloadSsnFile = async (majorJobId: number) => {
    try {
      const res = await triggerGetSsnFileContent(majorJobId, false).unwrap();
      const blob = new Blob([res.content], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || "ssn_info.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const confirmDeleteMatchesFile = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesDeleteFilename || jobId == null) return;
    setMatchesDeleting(true);
    try {
      await deleteMatchesFileMut({ jobId, filename: matchesDeleteFilename }).unwrap();
      setMatchesDeleteFilename(null);
      await loadMatchesFiles(jobId);
    } catch {
      // keep dialog open on error
    } finally {
      setMatchesDeleting(false);
    }
  };

  const confirmBatchDeleteMatchesFiles = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesSelectedFiles.length) return;
    setMatchesDeleting(true);
    try {
      await Promise.all(
        matchesSelectedFiles.map((filename) => deleteMatchesFileMut({ jobId, filename }).unwrap()),
      );
      setMatchesSelectedFiles([]);
      await loadMatchesFiles(jobId);
    } catch {
      // keep selection visible so user can retry
    } finally {
      setMatchesDeleting(false);
    }
  };

  const resetMatchesMergeState = () => {
    setMatchesMergeOpen(false);
    setMatchesMergeFile(null);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("idle");
    setMatchesMergeError(null);
  };

  const openMatchesMergeDialog = () => {
    setMatchesMergeFile(null);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("idle");
    setMatchesMergeError(null);
    setMatchesMergeOpen(true);
  };

  const handleMatchesMergeFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!canEditBots) return;
    const file = e.target.files?.[0];
    if (!file || mjManageJobId == null) return;
    e.target.value = "";

    setMatchesMergeFile(file);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("uploading");
    setMatchesMergeError(null);

    try {
      const presign = await presignMatchesMergeMut({
        jobId: mjManageJobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }).unwrap();

      const uploadResponse = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      setMatchesMergeStagingKey(presign.key);
      setMatchesMergeStep("ready");
    } catch (err: unknown) {
      setMatchesMergeStep("error");
      const msg =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : "Failed to upload file.");
      setMatchesMergeError(msg);
    }
  };

  const confirmMatchesMerge = async () => {
    if (!canEditBots) return;
    if (!matchesMergeStagingKey || mjManageJobId == null) return;
    setMatchesMergeStep("merging");
    setMatchesMergeError(null);
    try {
      const res = await mergeMatchesReportsMut({
        jobId: mjManageJobId,
        staging_key: matchesMergeStagingKey,
      }).unwrap();
      const skipped = res.rows_skipped
        ? ` ${res.rows_skipped} row${res.rows_skipped === 1 ? "" : "s"} already present, skipped.`
        : "";
      const hint = res.rows_added
        ? " Use 'Run merged EEs' to run just these, or Run all to run the whole job."
        : "";
      setMatchesMergeSuccess(
        `${res.rows_added} row${res.rows_added === 1 ? "" : "s"} added to Matches.${skipped}${hint}`,
      );
      resetMatchesMergeState();
      await loadMatchesFiles(mjManageJobId);
    } catch (err: unknown) {
      setMatchesMergeStep("ready");
      const msg =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : "Merge failed.");
      setMatchesMergeError(msg);
    }
  };

  const mjToggleNestedTaskSelect = useCallback((jobId: number, taskId: number, checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const cur = prev[jobId] ?? [];
      const next = checked ? Array.from(new Set([...cur, taskId])) : cur.filter((x) => x !== taskId);
      const out = { ...prev };
      if (next.length) out[jobId] = next;
      else delete out[jobId];
      return out;
    });
  }, []);

  const mjToggleAllNestedTasks = useCallback((jobId: number, taskRows: MjConnectionTask[], checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const out = { ...prev };
      if (!checked || !taskRows.length) delete out[jobId];
      else out[jobId] = taskRows.map((t) => t.id);
      return out;
    });
  }, []);

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [taskJobListName, setTaskJobListName] = useState("");
  const [childPlans, setChildPlans] = useState<ConnPlanEditorRow[]>([]);
  const [childBotName, setChildBotName] = useState("");
  const [childAdditionalOptions, setChildAdditionalOptions] = useState("");
  const [childProcessErrors, setChildProcessErrors] = useState(false);
  const [childSeparateProfiles, setChildSeparateProfiles] = useState(false);
  const [childDocumentType, setChildDocumentType] = useState("");
  const [childDocumentTypes, setChildDocumentTypes] = useState<string[]>([" "]);
  const [childUploadAsZip, setChildUploadAsZip] = useState(false);
  const [childUnzipBefore, setChildUnzipBefore] = useState(false);
  const [childUseSsnMatch, setChildUseSsnMatch] = useState(true);
  // path_to_s3_base_folder for upload bots — chosen via folder picker
  const [childSourcePath, setChildSourcePath] = useState("");
  const [childError, setChildError] = useState<string | null>(null);

  const [selectedChildJobIds, setSelectedChildJobIds] = useState<number[]>([]);

  const taskJobRowIdsOnPage = useMemo(
    () =>
      childJobs
        .filter((raw) => !isSSNJob(raw as Record<string, unknown>))
        .map((raw) => Number((raw as Record<string, unknown>).id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [childJobs],
  );

  const allTaskJobRowsSelected =
    taskJobRowIdsOnPage.length > 0 &&
    taskJobRowIdsOnPage.every((id) => selectedChildJobIds.includes(id));
  const taskJobRowsSelectIndeterminate =
    taskJobRowIdsOnPage.some((id) => selectedChildJobIds.includes(id)) && !allTaskJobRowsSelected;

  const toggleSelectAllTaskJobRows = useCallback(
    (checked: boolean) => {
      setSelectedChildJobIds((prev) => {
        if (checked) {
          return Array.from(new Set([...prev, ...taskJobRowIdsOnPage]));
        }
        const drop = new Set(taskJobRowIdsOnPage);
        return prev.filter((id) => !drop.has(id));
      });
    },
    [taskJobRowIdsOnPage],
  );

  // Folder picker dialog — shared between Create and Edit task job dialogs for upload bots
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerTarget, setFolderPickerTarget] = useState<"create" | "edit">("create");
  const [pickerSelectedFolder, setPickerSelectedFolder] = useState<number | null>(null);
  const [pickerSelectedSub, setPickerSelectedSub] = useState<string | null>(null);

  const [majConnCreateOpen, setMajConnCreateOpen] = useState(false);
  const [mcUsername, setMcUsername] = useState("");
  const [mcHost, setMcHost] = useState("");
  const [mcClientCode, setMcClientCode] = useState("");
  const [mcPassword, setMcPassword] = useState("");
  const [mcShowPw, setMcShowPw] = useState(false);
  const [mcError, setMcError] = useState<string | null>(null);

  const [majConnEdit, setMajConnEdit] = useState<AdminVendorConnection | null>(null);
  const [meUsername, setMeUsername] = useState("");
  const [meHost, setMeHost] = useState("");
  const [meClientCode, setMeClientCode] = useState("");
  const [mePassword, setMePassword] = useState("");
  const [meShowPw, setMeShowPw] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);

  const [selectedPoolConnectionIds, setSelectedPoolConnectionIds] = useState<number[]>([]);
  const [deleteConnConfirm, setDeleteConnConfirm] = useState<number | null>(null);
  /** Per-major-job pool: selected connection rows for batch delete. */
  const [poolConnSelectedIds, setPoolConnSelectedIds] = useState<number[]>([]);
  const [poolConnBulkDeleteOpen, setPoolConnBulkDeleteOpen] = useState(false);
  const [poolConnBulkDeleting, setPoolConnBulkDeleting] = useState(false);

  // Active manage-dialog tab. Stable string ids (not positional indices) so the
  // absent SSN tab on delete_bots can't collide with Connections. Persisted in URL.
  const tabFromUrl = useMemo<ManageTab>(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return t === "connections" ? "connections" : t === "ssn" ? "ssn" : "jobs";
  }, [location.search]);
  const [manageTab, setManageTab] = useState<ManageTab>(tabFromUrl);
  useEffect(() => {
    setManageTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleManageTabChange = (_e: React.SyntheticEvent, v: ManageTab) => {
    const params = new URLSearchParams(location.search);
    if (v === "jobs") {
      params.delete("tab");
    } else {
      params.set("tab", v);
    }
    navigate({ search: params.toString() }, { replace: true });
    // SSN data loads from the tab's own effect, so every way in behaves alike.
  };

  const activeMajor = useMemo(
    () => majors.find((m) => m.id === activeMajorId) ?? null,
    [majors, activeMajorId],
  );

  // Reset to Task jobs tab when switching to a delete_bots major job (no SSN tab there)
  useEffect(() => {
    if (activeMajor?.bot_kind === "delete_bots" && manageTab === "ssn") {
      const params = new URLSearchParams(location.search);
      params.delete("tab");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [activeMajor?.id, activeMajor?.bot_kind, manageTab, location.search, navigate]);

  /** True if at least one child task of this major job is running OR already downloaded >0 employees.
   *  Used to lock destructive edits (vendor / bot kind / job rewiring). */
  const majorHasRunningOrProgress = useMemo(() => {
    if (!activeMajor) return false;
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      if (mjJobAnythingInFlight(row)) return true;
      const stats = row.stats as Record<string, unknown> | undefined;
      const processed =
        Number(stats?.success_employees_count ?? 0) +
        Number(stats?.error_employees_count ?? 0);
      if (processed > 0) return true;
    }
    return false;
  }, [activeMajor, childJobs]);

  /** True if any child job currently has at least one running task. */
  const majorHasRunningTasks = useMemo(() => {
    if (!activeMajor) return false;
    return childJobs.some((raw) =>
      mjJobAnythingInFlight(raw as Record<string, unknown>),
    );
  }, [activeMajor, childJobs]);

  /** Auto-close the 2FA dialog as soon as the underlying task no longer needs a code.
   *  This handles WS updates: when the bot acknowledges the code, `task2fa_status`
   *  flips away from pending/ready and the dialog disappears immediately. */
  useEffect(() => {
    if (twoFaMjTask == null) return;
    const jobId =
      typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) ? twoFaMjTask.job : null;
    if (jobId == null) return;
    const tasksInJob = mjAccordionTasksByJob[jobId] ?? [];
    const fresh = tasksInJob.find((t) => t.id === twoFaMjTask.id);
    if (fresh && !shouldShowPass2FAMj(fresh)) {
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      setTwoFaMjErr(null);
    }
  }, [mjAccordionTasksByJob, twoFaMjTask]);

  const isDetailMode = detailMajorJobId != null;
  const majorsStillLoading = majorsRaw === undefined || majorsFetching;
  const detailMajorNotFound =
    isDetailMode &&
    !majorsStillLoading &&
    detailMajorJobId != null &&
    !majors.some((m) => m.id === detailMajorJobId);

  /** All bot connections for this user that match the active major's vendor (pool is kept in sync on the server). */
  const poolConnIdsForMajor = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    return connections
      .filter((c) => normVendor(c.vendor) === want)
      .map((c) => c.id)
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Logins explicitly attached to this automation (`MajorJobConnection` / included_connection_ids), vendor-filtered.
   * Task job PATCH only accepts plan rows whose connection_id is listed here unless we merge first — see save flows.
   */
  const majorIncludedConnIds = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return Array.from(new Set((activeMajor.included_connection_ids ?? []).filter((id) => id >= 1)))
      .filter((id) => vendorIds.has(id))
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Dropdown pool for allocating tasks to logins: every vendor-matching login for this user is
   * eligible. Picking one that isn't yet attached to the major job triggers
   * ``ensureMajorIncludesConnectionIds`` on save, which adds it to the pool.
   *
   * Linked rows from ``MajorJobConnection`` come first so operators see the "primary" ones at
   * the top of the dropdown, and we append the rest sorted by id.
   */
  const majorConnPickerIds = useMemo(() => {
    if (!activeMajor) return [];
    const linked = majorIncludedConnIds;
    const rest = poolConnIdsForMajor.filter((cid) => !linked.includes(cid));
    return [...linked, ...rest];
  }, [activeMajor, majorIncludedConnIds, poolConnIdsForMajor]);

  const nextUnusedChildPlanConnId = useMemo(
    () => nextUnusedPoolConnectionId(majorConnPickerIds, childPlans),
    [majorConnPickerIds, childPlans],
  );

  const ensureMajorIncludesConnectionIds = useCallback(
    async (connectionIds: number[]) => {
      if (!canEditBots) return;
      if (!activeMajor) return;
      const uniq = Array.from(new Set(connectionIds.filter((id) => id >= 1))).sort((a, b) => a - b);
      if (!uniq.length) return;
      const cur = activeMajor.included_connection_ids ?? [];
      const missing = uniq.filter((id) => !cur.includes(id));
      if (!missing.length) return;
      const merged = Array.from(new Set([...cur, ...uniq])).sort((a, b) => a - b);
      await updateMajorJob({
        majorJobId: activeMajor.id,
        invalidateUserId: userId,
        body: { included_connection_ids: merged },
      }).unwrap();
      await refetch();
    },
    [canEditBots, activeMajor, updateMajorJob, userId, refetch],
  );

  /** Stale IDs still stored on the major (e.g. before the next PATCH) or wrong-vendor rows. */
  const poolOrphans = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorMatchIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return (activeMajor.included_connection_ids ?? []).filter((cid) => !vendorMatchIds.has(cid));
  }, [activeMajor, connections]);

  /** 1-based index of the task job in this major job's list (API order); not Django task job id. */
  const mjEditJobOrdinal = useMemo(() => {
    if (mjEditJobId == null) return null;
    const idx = childJobs.findIndex((raw) => Number((raw as Record<string, unknown>).id) === mjEditJobId);
    return idx >= 0 ? idx + 1 : null;
  }, [mjEditJobId, childJobs]);

  /** 1-based index of a deleted job target (for confirmation copy). */
  const mjDeleteJobOrdinal = useMemo(() => {
    if (mjDeleteJobId == null) return null;
    const idx = childJobs.findIndex(
      (raw) => Number((raw as Record<string, unknown>).id) === mjDeleteJobId,
    );
    return idx >= 0 ? idx + 1 : null;
  }, [mjDeleteJobId, childJobs]);

  /** 1-based index of a task within its parent job's task list. */
  const taskOrdinalInJob = useCallback(
    (jobId: number, taskId: number) => {
      const tasks = mjAccordionTasksByJob[jobId] ?? [];
      const idx = tasks.findIndex((t) => t.id === taskId);
      return idx >= 0 ? idx + 1 : null;
    },
    [mjAccordionTasksByJob],
  );

  const { data: childRegistryBots } = useGetBotRegistryQuery(
    {
      vendor: activeMajor?.vendor ?? undefined,
      jobKind: activeMajor?.bot_kind ?? undefined,
      isActive: true,
      pageSize: 120,
    },
    // Fetched as soon as a major job is active: the Add/Edit dialogs render
    // their option fields from this registry, and fetching it only on open
    // left the first open without specs.
    { skip: !activeMajor?.vendor },
  );

  // Storage folders for the selected user — used by the upload-bot source folder picker.
  const { data: storageFoldersData } = useGetBackendUserStorageFoldersQuery(userId, {
    skip: !folderPickerOpen,
  });
  const storageFolders = storageFoldersData?.folders ?? [];
  const normalizedChildBots = useMemo(
    () => normalizeBotRegistryPayload(childRegistryBots),
    [childRegistryBots],
  );
  const botOptionSpecs = (botName: string) =>
    normalizedChildBots.find((b) => botNameFromRegistry(b) === botName)?.metadata
      ?.options ?? [];
  // The child registry is scoped to the major job's own kind, which is never
  // ssn_bots - the SSN bot's options need their own lookup.
  const { data: ssnRegistryBots } = useGetBotRegistryQuery(
    {
      vendor: activeMajor?.vendor ?? undefined,
      jobKind: "ssn_bots",
      isActive: true,
      pageSize: 50,
    },
    { skip: !activeMajor?.vendor || manageTab !== "ssn" },
  );
  const ssnOptionSpecs = useMemo(
    () =>
      normalizeBotRegistryPayload(ssnRegistryBots).find(
        (b) => botNameFromRegistry(b) === SSN_BOT_NAME,
      )?.metadata?.options ?? [],
    [ssnRegistryBots],
  );
  const ssnServerOptions = String(ssnJobData?.job?.additional_options ?? "");
  const ssnOptionsDirty = ssnAdditionalOptions !== ssnServerOptions;
  // Advisory only: the backend decides whether the edit is allowed and answers
  // 409 with the reason, so this warns without trying to mirror that rule.
  const ssnTaskLooksBusy = mjTaskShowsStopInsteadOfRun(
    String(ssnJobData?.task?.task_status ?? ""),
  );

  const openSsnJobDialog = (
    mode: "create" | "edit",
    connectionId?: number | null,
  ) => {
    setSsnJobDialogMode(mode);
    setSsnJobDialogError(null);
    if (mode === "create" && connectionId != null) {
      setSsnJobSelectedConnId(connectionId);
    }
    setSsnJobDialogOpen(true);
  };

  /** Create or save from the dialog; the 409 the backend answers when the job
   *  has tasks in flight explains itself, so it is shown as it comes. */
  const submitSsnJobDialog = async () => {
    if (!activeMajor || !canEditBots) return;
    const creating = ssnJobDialogMode === "create";
    if (creating) setSsnJobCreating(true);
    else setSsnOptionsSaving(true);
    setSsnJobDialogError(null);
    try {
      const res = creating
        ? await createSsnJobMut({
            majorJobId: activeMajor.id,
            connection_id: ssnJobSelectedConnId ?? undefined,
            additional_options: ssnAdditionalOptions,
          }).unwrap()
        : await updateSsnJobMut({
            majorJobId: activeMajor.id,
            additional_options: ssnAdditionalOptions,
          }).unwrap();
      setSsnJobData(res as SsnJobData);
      setSsnJobDialogOpen(false);
      if (creating) setSsnAccordionExpanded(true);
    } catch (e: unknown) {
      setSsnJobDialogError(
        (e as { data?: { message?: string } })?.data?.message ??
          (creating ? "Failed to create SSN job." : "Failed to save SSN job."),
      );
    } finally {
      setSsnJobCreating(false);
      setSsnOptionsSaving(false);
    }
  };
  const mjEffectiveManagedJob = useMemo((): Record<string, unknown> | null => {
    if (mjManageJobId == null) return null;
    if (
      mjManageJobDetail != null &&
      Number((mjManageJobDetail as Record<string, unknown>).id) === mjManageJobId
    ) {
      return mjManageJobDetail as Record<string, unknown>;
    }
    const fromList = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === mjManageJobId);
    return (fromList as Record<string, unknown> | undefined) ?? null;
  }, [mjManageJobId, mjManageJobDetail, childJobs]);

  /** Full S3 path built from the current picker selection. */
  const pickerBuiltPath = useMemo(() => {
    if (!pickerSelectedFolder) return "";
    const folder = storageFolders.find((f) => f.id === pickerSelectedFolder);
    if (!folder) return "";
    const prefix = (folder.folder_prefix ?? "").replace(/\/$/, "");
    return pickerSelectedSub ? `${prefix}/${pickerSelectedSub}` : prefix;
  }, [pickerSelectedFolder, pickerSelectedSub, storageFolders]);

  const openFolderPicker = (target: "create" | "edit", currentPath?: string) => {
    setFolderPickerTarget(target);
    setPickerSelectedSub(null);
    // Pre-select folder matching the current path
    if (currentPath) {
      const matched = storageFolders.find(
        (f) => currentPath.startsWith((f.folder_prefix ?? "").replace(/\/$/, "")),
      );
      setPickerSelectedFolder(matched?.id ?? null);
      if (matched) {
        const rest = currentPath.slice((matched.folder_prefix ?? "").replace(/\/$/, "").length).replace(/^\//, "");
        const sub = rest.split("/")[0];
        if (sub && matched.subfolders.includes(sub)) setPickerSelectedSub(sub);
      }
    } else {
      setPickerSelectedFolder(null);
    }
    setFolderPickerOpen(true);
  };

  const confirmFolderPicker = () => {
    if (!pickerBuiltPath) return;
    if (folderPickerTarget === "create") {
      setChildSourcePath(pickerBuiltPath);
    } else {
      setMjEditSourcePath(pickerBuiltPath);
    }
    setFolderPickerOpen(false);
  };

  /** 1-based index of the currently managed job in this major's list (UI-only label). */
  const mjEffectiveManagedJobOrdinal = useMemo(() => {
    if (!mjEffectiveManagedJob) return null;
    const idx = childJobs.findIndex(
      (raw) => Number((raw as Record<string, unknown>).id) === Number(mjEffectiveManagedJob.id),
    );
    return idx >= 0 ? idx + 1 : null;
  }, [mjEffectiveManagedJob, childJobs]);

  /** True when the currently-being-edited task job has any task running OR any
   *  downloaded employees. Used to lock destructive edits on the job. */
  const mjEditJobHasRunningOrProgress = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    if (!raw) return false;
    if (mjJobAnythingInFlight(raw)) return true;
    const stats = raw.stats as Record<string, unknown> | undefined;
    const processed =
      Number(stats?.success_employees_count ?? 0) + Number(stats?.error_employees_count ?? 0);
    return processed > 0;
  }, [mjEditJobId, childJobs]);

  const mjEditJobHasRunningTasks = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    return Boolean(raw && mjJobAnythingInFlight(raw));
  }, [mjEditJobId, childJobs]);

  /** Employees already processed (success + error) - any progress locks bot type. */
  const mjEditJobProcessedEmployees = useMemo(() => {
    if (mjEditJobId == null) return 0;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    const stats = raw?.stats as Record<string, unknown> | undefined;
    const fromTotal = Number(stats?.processed_employees_count);
    if (Number.isFinite(fromTotal)) return Math.max(0, fromTotal);
    const suc = Number(stats?.success_employees_count ?? 0) || 0;
    const err = Number(stats?.error_employees_count ?? 0) || 0;
    return suc + err;
  }, [mjEditJobId, childJobs]);

  const mjEditJobBotLocked = mjEditJobProcessedEmployees > 0;

  // While the bot fetches the code automatically (auto_until in the future) the
  // manual button stays hidden; the "Auto 2FA" chip lets the operator force it.
  const twoFaOf = (task: MjConnectionTask) => ({
    status: task.task2fa_status,
    auto_until: task.task2fa_auto_until,
  });

  const isAuto2FAPhaseMj = (task: MjConnectionTask) =>
    isTwoFaAutoPhase(twoFaOf(task), twoFaManualOverrideIds.has(task.id));

  const shouldShowAuto2FAMj = (task: MjConnectionTask) =>
    task.bot_work_status === "2FA" && isAuto2FAPhaseMj(task);

  // A served live view means the operator can step into this bot's browser -
  // shown only to those the backend would actually hand the URL to, so a
  // Calibrate Manager is not offered a window that never connects.
  const shouldShowLiveViewMj = (task: MjConnectionTask) =>
    canOpenLiveView && isLiveViewReady(task.live_view_status, task.bot_work_status);

  // Derived from the rows rather than from the websocket event: the bot
  // publishes live_view.updated on every change of its session, so opening on
  // the event put the window up during the login and again after it was over.
  useEffect(() => {
    const rows = Object.values(mjAccordionTasksByJob).flat();
    if (liveViewMjTaskId != null) {
      const open = rows.find((task) => task.id === liveViewMjTaskId);
      if (
        open &&
        !isLiveViewReady(open.live_view_status, open.bot_work_status)
      ) {
        setLiveViewMjTaskId(null);
      }
      return;
    }
    if (!canOpenLiveView) return;
    const needsHuman = rows.find(
      (task) =>
        shouldAutoOpenLiveView(
          task.live_view_status,
          task.live_view_phase,
          task.bot_work_status,
          task.started_by,
          myPlatformUserId,
        ) && !liveViewMjDismissed.includes(task.id),
    );
    if (needsHuman) setLiveViewMjTaskId(needsHuman.id);
  }, [
    mjAccordionTasksByJob,
    liveViewMjTaskId,
    liveViewMjDismissed,
    canOpenLiveView,
    myPlatformUserId,
  ]);

  // A finished session lets the next one open by itself again.
  useEffect(() => {
    if (liveViewMjDismissed.length === 0) return;
    const rows = Object.values(mjAccordionTasksByJob).flat();
    const stillOpen = liveViewMjDismissed.filter((id) =>
      rows.some(
        (task) =>
          task.id === id &&
          isLiveViewReady(task.live_view_status, task.bot_work_status),
      ),
    );
    if (stillOpen.length !== liveViewMjDismissed.length) {
      setLiveViewMjDismissed(stillOpen);
    }
  }, [mjAccordionTasksByJob, liveViewMjDismissed]);

  // Only while the code is genuinely missing: a stored code ('ready') means a
  // reader already found it and the bot is typing it in.
  const shouldShowPass2FAMj = (task: MjConnectionTask) =>
    isTwoFaCodeNeeded(
      task.bot_work_status,
      twoFaOf(task),
      twoFaManualOverrideIds.has(task.id),
    );

  const shouldShowCodeSentMj = (task: MjConnectionTask) =>
    task.bot_work_status === "2FA" && isTwoFaCodeSubmitted(twoFaOf(task));

  const reloadMjAccordionTasks = useCallback(
    async (jobId: number) => {
      // Reloads overlap (poll + websocket event + the action the operator just
      // took), and a slow older response used to overwrite the fresh statuses -
      // the row then looked unchanged until a manual page refresh.
      const seq = (mjAccordionSeqRef.current[jobId] ?? 0) + 1;
      mjAccordionSeqRef.current[jobId] = seq;
      try {
        const raw = await dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
        ).unwrap();
        if (mjAccordionSeqRef.current[jobId] !== seq) return null;
        setMjJobDetailById((prev) => ({
          ...prev,
          [jobId]: raw as Record<string, unknown>,
        }));
        const warnRaw = (
          raw as { stats?: { range_coverage?: { warning?: string } } }
        ).stats?.range_coverage?.warning;
        const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
        setMjJobRangeWarningsByJobId((prev) => {
          const n = { ...prev };
          if (warn) n[jobId] = warn;
          else delete n[jobId];
          return n;
        });
        const tasksUnknown = raw.tasks as unknown;
        const list = Array.isArray(tasksUnknown)
          ? (tasksUnknown as Record<string, unknown>[])
          : [];
        const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
        setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
        markMjAccordionFresh(jobId);
        return mapped;
      } catch {
        return null;
      }
    },
    [dispatch, markMjAccordionFresh],
  );

  const ACCORDION_CACHE_TTL_MS = 30_000; // 30 seconds

  // Seed the accordion caches from the list response: with include=tasks the
  // list carries every job's task rows, so one request feeds the rows AND the
  // accordions (the old per-job detail prefetch fanned out N parallel GETs on
  // every load). Bumping the sequence drops any older in-flight detail read,
  // and mjJobDetailById must be written too - the row renders the detail over
  // the list row wherever one exists, so a stale detail would shadow this.
  useEffect(() => {
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      const jobId = Number(row.id);
      if (!jobId) continue;
      const tasksUnknown = row.tasks as unknown;
      if (!Array.isArray(tasksUnknown)) continue;
      mjAccordionSeqRef.current[jobId] = (mjAccordionSeqRef.current[jobId] ?? 0) + 1;
      const mapped = (tasksUnknown as Record<string, unknown>[]).map((t) =>
        normalizeRawTaskToMj(t, jobId),
      );
      setMjJobDetailById((prev) => ({ ...prev, [jobId]: row }));
      setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
      markMjAccordionFresh(jobId);
      if (mjManageJobId === jobId) setMjManageJobDetail(row);

      const warnRaw = (row as { stats?: { range_coverage?: { warning?: string } } })
        .stats?.range_coverage?.warning;
      const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
      setMjJobRangeWarningsByJobId((prev) => {
        const n = { ...prev };
        if (warn) n[jobId] = warn;
        else delete n[jobId];
        return n;
      });
    }
  }, [childJobs, markMjAccordionFresh, mjManageJobId]);

  const handleMjAccordionExpanded = (_event: SyntheticEvent, jobId: number, expanded: boolean) => {
    setExpandedMjJobAccordion((prev) => ({ ...prev, [jobId]: expanded }));
    if (!expanded) return;

    const cachedAt = mjAccordionCacheTs[jobId] ?? 0;
    const hasData = Boolean(mjAccordionTasksByJob[jobId]);
    const isFresh = hasData && Date.now() - cachedAt < ACCORDION_CACHE_TTL_MS;

    void loadMjRosterInfo(jobId);
    // A job that was never counted (no stats snapshot) is counted on its first
    // open, so the S3 folder count and the log-based numbers show up without a
    // Refresh click. Later refreshes stay manual: they read every archive.
    const row = mjJobRowById(jobId);
    if (
      row &&
      mjEmployeeStatsFromJob(row).snapshotAt == null &&
      !mjAutoScannedJobsRef.current.has(jobId)
    ) {
      mjAutoScannedJobsRef.current.add(jobId);
      void refreshMjJobRow(jobId);
    }
    // Fresh cache — render instantly, nothing to fetch.
    if (isFresh) return;
    // A reload is already running for this job — don't stack another (rapid open/close/open).
    if (mjAccordionInFlightRef.current.has(jobId)) return;

    // Block with the spinner ONLY when there is nothing to show yet. When stale
    // data exists we render it immediately and refresh silently in the background.
    if (!hasData) setMjAccordionLoadingJobId(jobId);

    mjAccordionInFlightRef.current.add(jobId);
    void reloadMjAccordionTasks(jobId).finally(() => {
      mjAccordionInFlightRef.current.delete(jobId);
      setMjAccordionLoadingJobId((cur) => (cur === jobId ? null : cur));
    });
  };

  /** `silent` suppresses the error banner: used by callers whose own write already
   *  succeeded, so a failed follow-up read must not claim the change failed. Two
   *  quick toggles also race two `subscribe: false` reads of the same job and the
   *  loser rejects, which is exactly the case that used to raise a false alarm. */
  const refreshMjJobDetail = async (
    jobId: number,
    refreshList = false,
    silent = false,
  ) => {
    setMjRefreshingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      const details = (await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
      ).unwrap()) as Record<string, unknown> | undefined;
      // unwrap() of an unsubscribed initiate resolves to undefined (not an error)
      // when a concurrent tag invalidation evicts the entry before the state read.
      // The GET itself succeeded, so keep the previous detail and fall through to
      // the list refetch instead of raising a false "failed" banner.
      if (!details) {
        if (refreshList) await safeRefetchChildJobs();
        return;
      }
      setMjJobDetailById((prev) => ({
        ...prev,
        [jobId]: details,
      }));
      const statsRaw = (
        details as { stats?: { range_coverage?: { warning?: string } } }
      ).stats?.range_coverage?.warning;
      const stats = mjArtifactRangeWarning(typeof statsRaw === "string" ? statsRaw : null);
      setMjJobRangeWarningsByJobId((prev) => {
        const n = { ...prev };
        if (stats) n[jobId] = stats;
        else delete n[jobId];
        return n;
      });
      if (mjManageJobId === jobId) setMjManageJobDetail(details as Record<string, unknown>);
      const tasksUnknown = (details as { tasks?: unknown }).tasks;
      const list = Array.isArray(tasksUnknown) ? (tasksUnknown as Record<string, unknown>[]) : [];
      const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
      setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
      markMjAccordionFresh(jobId);
      if (refreshList) await safeRefetchChildJobs();
    } catch (e) {
      if (silent) return;
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Failed to refresh task job."),
      }));
    } finally {
      setMjRefreshingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  /** The row's Refresh button: rescan the stored logs, then show the result.
   *
   * Reading the job no longer scans S3 - that is what makes the panel's polling
   * affordable - so Refresh is the one place that asks for a rescan. It is also
   * what the Success/Error tooltip already promises ("Refreshed from stored logs
   * N min ago"). refresh-stats persists the snapshot and answers with the
   * serialized job, so its response is what the row should render.
   */
  const refreshMjJobRow = async (jobId: number) => {
    setMjRefreshingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      const rescanned = await refreshTaskJobStats(jobId).unwrap();
      setMjJobDetailById((prev) => ({
        ...prev,
        [jobId]: { ...(prev[jobId] ?? {}), ...(rescanned as Record<string, unknown>) },
      }));
      if (mjManageJobId === jobId) {
        setMjManageJobDetail((prev) => ({
          ...(prev ?? {}),
          ...(rescanned as Record<string, unknown>),
        }));
      }
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Failed to refresh task job."),
      }));
    } finally {
      setMjRefreshingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  useEffect(() => {
    expandedMjJobAccordionRef.current = expandedMjJobAccordion;
  }, [expandedMjJobAccordion]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    activeMajorForWsRef.current = activeMajorId ?? detailMajorJobId ?? null;
  }, [activeMajorId, detailMajorJobId]);

  // Deep link: /major-job/N?job=M lands with job M expanded. The task page's
  // "Back to job" uses it so the operator returns to the job they came from
  // instead of a collapsed list. Keyed on the loaded list, not just the URL:
  // the mount-time reset below would otherwise wipe an expansion set before
  // the rows exist.
  useEffect(() => {
    const raw = new URLSearchParams(location.search).get("job");
    const jobId = raw == null || raw === "" ? null : Number(raw);
    if (jobId == null || !Number.isFinite(jobId)) return;
    if (!childJobs.some((row) => Number((row as Record<string, unknown>).id) === jobId)) {
      return;
    }
    setExpandedMjJobAccordion((prev) => (prev[jobId] ? prev : { ...prev, [jobId]: true }));
  }, [location.search, childJobs]);

  useEffect(() => {
    mjAccordionTasksByJobRef.current = mjAccordionTasksByJob;
  }, [mjAccordionTasksByJob]);

  useEffect(() => {
    reloadMjAccordionTasksRef.current = reloadMjAccordionTasks;
  }, [reloadMjAccordionTasks]);

  useEffect(() => {
    refreshMjJobDetailRef.current = refreshMjJobDetail;
  });

  useEffect(() => {
    safeRefetchChildJobsRef.current = safeRefetchChildJobs;
  }, [safeRefetchChildJobs]);

  useEffect(() => { ssnJobDataRef.current = ssnJobData; }, [ssnJobData]);

  // Refill the editor only when the stored value actually changed: the SSN tab
  // reloads silently on every websocket event, and refilling on each of those
  // would delete whatever the operator is typing.
  useEffect(() => {
    const jobId = ssnJobData?.job?.id ?? "none";
    const source = `${jobId}|${ssnServerOptions}`;
    if (ssnOptionsSourceRef.current === source) return;
    ssnOptionsSourceRef.current = source;
    setSsnAdditionalOptions(ssnServerOptions);
  }, [ssnJobData, ssnServerOptions]);
  useEffect(() => { ssnMajorIdRef.current = activeMajorId ?? detailMajorJobId ?? null; }, [activeMajorId, detailMajorJobId]);
  useEffect(() => { loadSsnJobRef.current = loadSsnJob; });

  // The SSN tab owns its data: a click is not the only way to land on it (a
  // shared ?tab=ssn link, a reload, or switching major jobs while it is open).
  // Goes through the refs so a re-created loadSsnJob cannot re-trigger this.
  useEffect(() => {
    if (manageTab !== "ssn") return;
    if (!activeMajor || activeMajor.bot_kind === "delete_bots") return;
    void loadSsnJobRef.current(activeMajor.id, {
      silent: ssnJobDataRef.current?.job?.id != null,
    });
  }, [manageTab, activeMajor?.id, activeMajor?.bot_kind]);

  useEffect(() => { loadMatchesFilesRef.current = loadMatchesFiles; });

  // Same for the Matches tab of the manage dialog: it used to load only from
  // the Tabs onChange and only when the list was still null, so reopening the
  // dialog after a delete or a merge showed the previous file list.
  useEffect(() => {
    if (mjManageTab !== 1 || mjManageJobId == null) return;
    void loadMatchesFilesRef.current(mjManageJobId);
  }, [mjManageTab, mjManageJobId]);

  useEffect(() => {
    // Coalesce, never postpone: a batch emits an event per task, and restarting
    // the timer on every one of them (a plain debounce) let a busy job push its
    // refetch back indefinitely - the table then sat frozen until the 15s guard
    // poll. The first event of a window schedules the reload; the rest ride it.
    const scheduleMjWsAction = (key: string, action: () => void, delayMs: number) => {
      const timers = mjWsDebounceTimersRef.current;
      if (timers[key]) return;
      timers[key] = setTimeout(() => {
        delete timers[key];
        action();
      }, delayMs);
    };

    // An event that names a foreign user or a foreign major job is dropped
    // without a request - the group is a broadcast to every open panel, so
    // most of what arrives here during busy hours is other people's runs. A
    // payload with the fields missing (an old bot image publishes bare ids)
    // stays conservative: refetch. New jobs of this major job need no special
    // case - their events carry this major's id and the list refetch finds
    // them.
    const eventConcernsThisPage = (userIdRaw: unknown, majorIdRaw: unknown): boolean => {
      const eventUserId = userIdRaw == null ? null : Number(userIdRaw);
      if (eventUserId != null && eventUserId !== Number(userIdRef.current)) return false;
      const eventMajorId = majorIdRaw == null ? null : Number(majorIdRaw);
      const shownMajorId = activeMajorForWsRef.current;
      if (eventMajorId != null && shownMajorId != null && eventMajorId !== shownMajorId) {
        return false;
      }
      return true;
    };

    const reloadSsnIfMatch = (matchJobId: number | null, matchTaskId: number | null) => {
      const ssnData = ssnJobDataRef.current;
      const majorId = ssnMajorIdRef.current;
      if (!ssnData || majorId == null) return;
      const ssnJobId = ssnData.job != null ? Number((ssnData.job as Record<string, unknown>).id ?? 0) : null;
      const ssnTaskId = ssnData.task != null ? Number((ssnData.task as Record<string, unknown>).id ?? 0) : null;
      const matches =
        (matchJobId != null && ssnJobId != null && matchJobId === ssnJobId) ||
        (matchTaskId != null && ssnTaskId != null && matchTaskId === ssnTaskId);
      if (matches) {
        scheduleMjWsAction(`ssn-job-reload-${majorId}`, () => void loadSsnJobRef.current(majorId, { silent: true }), 600);
      }
    };

    // Every refresh funnels into ONE coalesced list refetch: with
    // include=tasks the list response updates the job rows and the accordion
    // task rows alike, so a burst of N events costs one request per window
    // instead of a per-job detail fan-out plus a list refetch.
    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      const jobId = payload.job?.job_id;
      if (payload.event === "job.updated" && jobId != null) {
        reloadSsnIfMatch(Number(jobId), null);
        if (!eventConcernsThisPage(payload.job?.user_id, payload.job?.major_job_id)) return;
        scheduleMjWsAction("child-jobs", () => void safeRefetchChildJobsRef.current(), 700);
        return;
      }

      const taskId = payload.task?.task_id;
      if (taskId == null) return;

      const eventName = payload.event ?? "task.updated";
      if (
        eventName === "task2fa.updated" ||
        eventName === "live_view.updated" ||
        eventName === "task.updated"
      ) {
        reloadSsnIfMatch(null, Number(taskId));
        if (!eventConcernsThisPage(payload.task?.user_id, payload.task?.major_job_id)) return;
        scheduleMjWsAction("child-jobs", () => void safeRefetchChildJobsRef.current(), 700);
      }
    });

    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      setMjWsConnected(connected);
      if (!connected) return;
      if (!hasSeenWsConnectedRef.current) {
        // The first open right after the page load: the data on screen came
        // from REST moments ago, so refetching it now would be the duplicate
        // the ticket measured. Only a REconnect needs a catch-up refetch.
        hasSeenWsConnectedRef.current = true;
        return;
      }
      scheduleMjWsAction("ws-reconnect", () => void safeRefetchChildJobsRef.current(), 1000);
    });

    return () => {
      unsubscribeEvents();
      unsubscribeStatus();
      Object.values(mjWsDebounceTimersRef.current).forEach((timer) => clearTimeout(timer));
      mjWsDebounceTimersRef.current = {};
    };
  }, []);

  /**
   * Polling exists for one case only: the WebSocket is not delivering. The
   * client reports "connected" solely while its heartbeat is answered, and
   * the pong rides the same channel layer as the events - so a socket that is
   * open but mute (stale Daphne, dead Redis) flips to disconnected within the
   * heartbeat timeout and this fallback takes over. While the socket is
   * healthy there is NO polling at all: the backend publishes throttled
   * progress events, so even the employee counters move without one.
   */
  useEffect(() => {
    if (mjWsConnected) return undefined;
    if (activeMajorId == null && detailMajorJobId == null) return undefined;
    const TERMINAL_STATUSES = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"];
    // One list request per tick (include=tasks carries the accordions too).
    // A page with a running task refreshes every tick; a fully idle one only
    // needs to notice that something started, every 4th tick. An empty tasks
    // map means the first load has not landed yet - treat it as active.
    let tick = 0;
    const interval = window.setInterval(() => {
      tick += 1;
      const jobLists = Object.values(mjAccordionTasksByJobRef.current);
      const anyActive =
        !jobLists.length ||
        jobLists.some((tasks) =>
          tasks.some(
            (t) => !TERMINAL_STATUSES.includes((t.task_status || "").toUpperCase()),
          ),
        );
      if (anyActive || tick % 4 === 0) void safeRefetchChildJobsRef.current();
    }, 8000);
    return () => window.clearInterval(interval);
  }, [mjWsConnected, activeMajorId, detailMajorJobId]);

  const openMjManage = (jobId: number) => {
    setMjManageJobError(null);
    setMjManageJobId(jobId);
    // Reset Matches tab state
    setMjManageTab(0);
    setMatchesFiles(null);
    setMatchesSummary(null);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    setMatchesMergeSuccess(null);
    resetMatchesMergeState();
    const snap = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === jobId);
    setMjManageJobDetail(snap ? (snap as Record<string, unknown>) : null);
    void reloadMjAccordionTasks(jobId);
    void dispatch(
      adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
    )
      .unwrap()
      .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
      .catch(() => {});
  };

  // Coming back from Data Storage: ?manage=<jobId> reopens the dialog the
  // operator left from, then drops out of the URL. Waits for the job list -
  // the mount-time reset effects would otherwise wipe the dialog right back.
  const mjManageRestoredRef = useRef(false);
  useEffect(() => {
    if (mjManageRestoredRef.current) return;
    const params = new URLSearchParams(location.search);
    const manage = params.get("manage");
    if (!manage) {
      mjManageRestoredRef.current = true;
      return;
    }
    const jobId = Number(manage);
    const known = childJobs.some(
      (raw) => Number((raw as Record<string, unknown>).id) === jobId,
    );
    if (!Number.isFinite(jobId) || jobId <= 0 || !known) return;
    mjManageRestoredRef.current = true;
    openMjManage(jobId);
    params.delete("manage");
    navigate({ search: params.toString() }, { replace: true });
  }, [childJobs]);

  const openMjAddTasksDialog = async () => {
    if (mjManageJobId == null) return;
    const jid = mjManageJobId;
    let snap: Record<string, unknown> | null = null;
    setMjAddTasksLoading(true);
    try {
      const raw = await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      setMjManageJobDetail(raw as Record<string, unknown>);
      snap = raw as Record<string, unknown>;
    } catch {
      snap =
        (mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === jid
          ? (mjManageJobDetail as Record<string, unknown>)
          : null) ??
        ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === jid) ?? null) as Record<
          string,
          unknown
        > | null);
    }

    if (snap != null) {
      const plans = parseConnectionPlansFromJobRow(snap);
      if (plans.length) {
        const pool = Array.from(new Set(plans.map((p) => p.connection_id))).sort((a, b) => a - b);
        setMjAddPlanConnPool(pool);
        const head = pool[0];
        setMjBulkAddRows([{ connection_id: head != null ? head : "", task_target_count: "1" }]);
      } else {
        setMjAddPlanConnPool([]);
        const lc =
          legacyPrimaryConnectionFromJob(snap) ?? (majorConnPickerIds[0] != null ? majorConnPickerIds[0] : "");
        const cid = typeof lc === "number" && lc >= 1 ? lc : "";
        setMjBulkAddRows([{ connection_id: cid, task_target_count: "1" }]);
      }
    } else {
      setMjAddPlanConnPool([]);
      const fb = majorConnPickerIds[0];
      setMjBulkAddRows([{ connection_id: fb != null ? fb : "", task_target_count: "1" }]);
    }

    setMjAddTasksJobId(jid);
    setMjAddTasksErr(null);
    void reloadMjAccordionTasks(jid);
    setMjAddTasksOpen(true);
    setMjAddTasksLoading(false);
  };

  const handleMjAddTasksConfirmed = async () => {
    if (!canEditBots) return;
    if (mjAddTasksJobId == null) return;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];

    const manageDetailSnap =
      mjManageJobDetail != null && Number(mjManageJobDetail.id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawTasksOnDetail = manageDetailSnap
      ? ((manageDetailSnap as Record<string, unknown>).tasks as unknown)
      : undefined;
    const arrFromDetail =
      rawTasksOnDetail &&
      Array.isArray(rawTasksOnDetail) &&
      (currentTasks.length === 0 || (rawTasksOnDetail as unknown[]).length >= currentTasks.length)
        ? (rawTasksOnDetail as Record<string, unknown>[])
        : null;

    if (arrFromDetail && arrFromDetail.length > 0) {
      currentTasks = arrFromDetail.map((t) => normalizeRawTaskToMj(t, mjAddTasksJobId));
    }

    const rawJobSnapshot =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (manageDetailSnap as Record<string, unknown> | null) ??
      ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null);

    const plansSnap = rawJobSnapshot ? parseConnectionPlansFromJobRow(rawJobSnapshot) : [];
    const planMode = plansSnap.length > 0;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentTasks.length);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    if ("error" in resolved) {
      setMjAddTasksErr(resolved.error);
      return;
    }

    setMjAddTasksErr(null);
    const jid = mjAddTasksJobId;
    try {
      if (resolved.planMode) {
        for (const batch of resolved.batches) {
          await addTasksToJobMutation({
            jobId: jid,
            count: batch.count,
            connection_id: batch.connection_id,
          }).unwrap();
        }
      } else {
        await addTasksToJobMutation({
          jobId: jid,
          count: resolved.legacyCount,
        }).unwrap();
      }

      if (mjAddPlanConnPool.length) {
        const h = mjAddPlanConnPool[0];
        setMjBulkAddRows([{ connection_id: h!, task_target_count: "1" }]);
      } else {
        setMjBulkAddRows((prev) => [{ connection_id: prev[0]?.connection_id ?? "", task_target_count: "1" }]);
      }

      setMjAddTasksOpen(false);
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jid]) await reloadMjAccordionTasks(jid);
      void dispatch(adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }))
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjAddTasksErr(getErr(e, "Could not add tasks."));
    }
  };

  const mjRunTaskRow = async (jobId: number, task: MjConnectionTask) => {
    if (!canEditBots) return;
    setBusyTaskId(task.id);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      // One call: the backend resets a finished run itself. Two calls used to
      // move the row first, so a refused start left it reset under an error.
      await runTaskMutation(task.id).unwrap();
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not run task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === task.id ? null : cur));
    }
  };

  const mjStopTaskRow = async (jobId: number, taskId: number) => {
    if (!canEditBots) return;
    setBusyTaskId(taskId);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      const report = await stopTaskMutation(taskId).unwrap();
      if (report.outcome === "failed") {
        setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: report.message }));
      }
      if (twoFaMjTask?.id === taskId) {
        setTwoFaMjTask(null);
        setTwoFaMjCode("");
        setTwoFaMjErr(null);
      }
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not stop task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === taskId ? null : cur));
    }
  };

  /**
   * The one task row of this panel: used by the job accordions and by the SSN
   * job, which used to carry its own ~210-line copy. The copy had already
   * fallen behind - a CRITICAL_ERROR status was clickable (opens the error and
   * a rerun) in the accordion but dead in the SSN row.
   *
   * A local renderer rather than a separate component on purpose: the row reads
   * a dozen pieces of this panel's state (connection drafts, busy ids, 2FA
   * visibility, permissions) and threading all of it through props would just
   * move the duplication into a prop list.
   */
  useEffect(() => {
    setMjRosterNotice(null);
    if (mjManageJobId == null) return;
    void loadMjRosterInfo(mjManageJobId);
  }, [mjManageJobId, loadMjRosterInfo]);

  useEffect(() => {
    setMjBrowserProfilesError(null);
    if (activeMajor?.id && isDetailMode) void loadMjBrowserProfiles(activeMajor.id);
    else setMjBrowserProfiles(null);
  }, [activeMajor?.id, isDetailMode, loadMjBrowserProfiles]);

  // SSN readiness of the upload jobs whose rows are open.
  useEffect(() => {
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      const id = Number(row.id);
      if (
        !expandedMjJobAccordion[id] ||
        !mjIsUploadJob(row) ||
        mjSsnReadinessRequestedRef.current.has(id)
      ) {
        continue;
      }
      mjSsnReadinessRequestedRef.current.add(id);
      fetchJobSsnReadiness(id)
        .unwrap()
        .then((readiness) =>
          setMjSsnReadinessByJobId((prev) => ({ ...prev, [id]: readiness })),
        )
        .catch(() => mjSsnReadinessRequestedRef.current.delete(id));
    }
  }, [expandedMjJobAccordion, childJobs, fetchJobSsnReadiness]);

  // The dialogs say up front whether the job as configured would match by SSN.
  useEffect(() => {
    if (!childDialogOpen || !activeMajor || activeMajor.bot_kind !== "upload_bots") {
      setChildSsnReadiness(null);
      return;
    }
    const path = childSourcePath.trim();
    if (!path) {
      setChildSsnReadiness(null);
      return;
    }
    const majorJobId = activeMajor.id;
    const timer = setTimeout(() => {
      fetchMajorSsnReadiness({ majorJobId, path, useSsnMatch: childUseSsnMatch })
        .unwrap()
        .then(setChildSsnReadiness)
        .catch(() => setChildSsnReadiness(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [childDialogOpen, childSourcePath, childUseSsnMatch, activeMajor, fetchMajorSsnReadiness]);

  useEffect(() => {
    if (mjEditJobId == null || !activeMajor || activeMajor.bot_kind !== "upload_bots") {
      setMjEditSsnReadiness(null);
      return;
    }
    const path = mjEditSourcePath.trim();
    if (!path) {
      setMjEditSsnReadiness(null);
      return;
    }
    const majorJobId = activeMajor.id;
    const timer = setTimeout(() => {
      fetchMajorSsnReadiness({ majorJobId, path, useSsnMatch: mjEditUseSsnMatch })
        .unwrap()
        .then(setMjEditSsnReadiness)
        .catch(() => setMjEditSsnReadiness(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [mjEditJobId, mjEditSourcePath, mjEditUseSsnMatch, activeMajor, fetchMajorSsnReadiness]);

  const renderMjTaskRow = ({
    task,
    jobId,
    index,
    showCheckbox = false,
    showRange = false,
    rangePending = false,
    showProgress = false,
    jobSnapshotAt = null,
    showDelete = false,
    selected = false,
    onToggleSelect,
  }: {
    task: MjConnectionTask;
    jobId: number;
    index: number | string;
    showCheckbox?: boolean;
    showRange?: boolean;
    /** Bootstrap ranges: the real slices arrive with the first roster. */
    rangePending?: boolean;
    /** Processed/Success/Errors cell; jobSnapshotAt dates the stored-log numbers. */
    showProgress?: boolean;
    jobSnapshotAt?: string | null;
    showDelete?: boolean;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
  }) => {
    const rosterOnlyTask = String(task.range_role ?? "").toUpperCase() === "ROSTER_ONLY";
    const currentCid = task.connection ?? null;
    const draftCid = taskConnDraftById[task.id];
    const selectVal =
      typeof draftCid === "number" ? draftCid : typeof currentCid === "number" ? currentCid : "";
    const dirty = typeof draftCid === "number" && draftCid !== currentCid;
    const taskIsRunning = mjTaskShowsStopInsteadOfRun(task.task_status);
    const saving = taskConnSavingId === task.id;
    const saveErr = taskConnSaveErrById[task.id];
    const busy = busyTaskId === task.id;
    const statusText = String(task.task_status ?? "").toUpperCase();
    const isErrorChip = statusText === "CRITICAL_ERROR";

    return (
      <TableRow key={task.id}>
        {showCheckbox && (
          <TableCell padding="checkbox" align="center">
            <Checkbox
              size="small"
              checked={selected}
              onChange={(e) => onToggleSelect?.(e.target.checked)}
            />
          </TableCell>
        )}
        <TableCell align="center">{index}</TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <StatusChipMj
              label={task.task_status}
              color={taskRunStatusChipColor(task.task_status)}
              onClick={isErrorChip ? () => setMjErrorPopupTask({ jobId, task }) : undefined}
              icon={isErrorChip ? <ErrorOutlineIcon /> : undefined}
              title={isErrorChip ? "Click to view the error and rerun the task" : undefined}
            />
          </Box>
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <StatusChipMj
              label={task.bot_work_status}
              color={botWorkStatusChipColor(task.bot_work_status)}
            />
          </Box>
        </TableCell>
        {showRange && (
          <TableCell
            align="center"
            sx={{ verticalAlign: "middle", fontVariantNumeric: "tabular-nums" }}
          >
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              <Typography variant="body2" color="text.secondary" component="span">
                {rosterOnlyTask
                  ? "roster only"
                  : rangePending
                    ? "-"
                    : task.employee_number_from > task.employee_number_to
                      ? "empty"
                      : `${task.employee_number_from}-${task.employee_number_to}`}
              </Typography>
              <Tooltip
                title={
                  <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                    {mjTaskDetailLines(task, botOptionSpecs(String(task.bot_name ?? ""))).join("\n")}
                  </Box>
                }
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
                  aria-label="Task details"
                />
              </Tooltip>
            </Stack>
          </TableCell>
        )}
        {showProgress &&
          (() => {
            const progress = mjTaskEmployeeProgress(task, jobSnapshotAt, rangePending);
            return (
              <TableCell align="left" sx={{ verticalAlign: "middle", px: 1.5 }}>
                <EmployeeProgressBar
                  success={progress.success}
                  error={progress.error}
                  total={progress.rosterOnly ? null : progress.rangeSize}
                  pendingLabel={progress.rosterOnly ? "roster only - processes nobody" : "range pending"}
                  tooltip={progress.tooltip}
                  onErrorsClick={() => openTaskLogs(task.id)}
                />
              </TableCell>
            );
          })()}
        <TableCell align="center" sx={{ verticalAlign: "top", py: 1.25 }}>
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              justifyContent="center"
              flexWrap="wrap"
              sx={{ maxWidth: "100%" }}
            >
              <Tooltip
                title={taskIsRunning ? "Stop the task first to move it to another login" : ""}
              >
                <TextField
                  select
                  size="small"
                  value={selectVal}
                  onChange={(ev) => {
                    const v = Number(ev.target.value);
                    if (Number.isFinite(v) && v > 0) setTaskConnDraft(task.id, v);
                  }}
                  disabled={taskIsRunning || saving || !majorConnPickerIds.length || !canEditBots}
                  sx={{
                    width: { xs: 164, sm: 220 },
                    maxWidth: "100%",
                    flexShrink: 0,
                    "& .MuiInputBase-input": { textAlign: "left" },
                    "& .MuiSelect-select": {
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    },
                  }}
                  error={Boolean(saveErr)}
                  helperText={saveErr ?? ""}
                  FormHelperTextProps={{ sx: { mx: 0, textAlign: "left" } }}
                >
                  {majorConnPickerIds.map((cid, ix) => (
                    <MenuItem key={cid} value={cid}>
                      {`${ix + 1}. ${connectionPoolRowLabel(connections, cid) || "-"}`}
                    </MenuItem>
                  ))}
                  {typeof currentCid === "number" && !majorConnPickerIds.includes(currentCid) && (
                    <MenuItem value={currentCid}>
                      {connectionPoolRowLabel(connections, currentCid) || "-"}
                    </MenuItem>
                  )}
                </TextField>
              </Tooltip>
              {dirty ? (
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Save new connection">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={saving || taskIsRunning || !canEditBots}
                        onClick={() => void saveTaskConn(jobId, task, draftCid)}
                      >
                        {saving ? "..." : "Save"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Discard">
                    <span>
                      <Button
                        size="small"
                        onClick={() => clearTaskConnDraft(task.id)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%", flexWrap: "nowrap" }}
          >
            <Tooltip title="Open task workspace">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Task details"
                  onClick={() => {
                    const back = encodeURIComponent(location.pathname + location.search);
                    navigate(
                      `/bots-management/${userId}/${task.connection ?? ""}/task/${task.id}?back=${back}`,
                    );
                  }}
                >
                  <ReadMoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Logs of this task">
              <span>
                <IconButton
                  size="small"
                  aria-label="Task logs"
                  onClick={() => openTaskLogs(task.id)}
                >
                  <ArticleOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {shouldShowAuto2FAMj(task) && (
              <Tooltip title="The bot is entering the 2FA code automatically. Click to enter it manually.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<CircularProgress size={12} color="warning" />}
                    onClick={() =>
                      setTwoFaManualOverrideIds((prev) => new Set(prev).add(task.id))
                    }
                  >
                    Auto 2FA...
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowCodeSentMj(task) && (
              <Tooltip title="The code is in - the bot is entering it">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<CircularProgress size={12} color="warning" />}
                    disabled
                  >
                    Code sent
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowLiveViewMj(task) && (
              <Tooltip title="Open the bot's browser - watch it, or solve a captcha in it">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<ImageSearchIcon fontSize="small" />}
                    onClick={() => setLiveViewMjTaskId(task.id)}
                  >
                    Live browser
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowPass2FAMj(task) && (
              <Tooltip title="Enter the 2FA code">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<VpnKeyOutlinedIcon fontSize="small" />}
                    onClick={() => {
                      setMjAccordionTasksByJob((prev) =>
                        prev[jobId] ? prev : { ...prev, [jobId]: [task] },
                      );
                      setTwoFaMjTask(task);
                      setTwoFaMjCode("");
                      setTwoFaMjErr(null);
                    }}
                  >
                    Pass 2FA
                  </Button>
                </span>
              </Tooltip>
            )}
            {taskIsRunning ? (
              <Tooltip title="Stop task">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={mjJobActionBusy || !canEditBots}
                    onClick={() => void mjStopTaskRow(jobId, task.id)}
                    aria-label="Stop task"
                  >
                    {busy ? <CircularProgress size={14} color="error" /> : <StopIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Run task">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={
                      mjJobActionBusy || !canRunTaskByStatusMj(task.task_status) || !canEditBots
                    }
                    onClick={() =>
                      void mjSsnGuardedRun(mjJobRowById(jobId), "Run task", () =>
                        mjRunTaskRow(jobId, task),
                      )
                    }
                    aria-label="Run task"
                  >
                    {busy ? (
                      <CircularProgress size={14} color="success" />
                    ) : (
                      <PlayArrowIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {showDelete && (
              <Tooltip title={taskIsRunning ? "Stop the task before deleting" : "Delete task"}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Delete task"
                    disabled={taskIsRunning || !canEditBots}
                    onClick={() => setMjDeleteSingleTaskId(task.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  /** `jobIdArg` comes from the task-jobs list; without it this drives the Manage job dialog. */
  const mjRunJob = async (jobIdArg?: number) => {
    if (!canEditBots) return;
    const jobId = jobIdArg ?? mjManageJobId;
    if (jobId == null) return;
    const inline = jobIdArg != null;
    if (inline) {
      setMjRowBatchBusyJobId(jobId);
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      setMjJobNoticesByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
    } else {
      setMjManageJobError(null);
      setMjManageJobNotice(null);
    }
    try {
      const report = await runTasksBatchMut({ job_id: jobId }).unwrap();
      await refreshMjJobDetail(jobId, true);
      // 200 with nothing started: the batch waits behind a task that holds
      // the queue (typically the roster task) - say so, it is not an error.
      if (!report.started_task_ids?.length && report.queued_task_ids?.length) {
        if (inline) setMjJobNoticesByJobId((prev) => ({ ...prev, [jobId]: report.message }));
        else setMjManageJobNotice(report.message);
      }
    } catch (e) {
      const message = getErr(e, "Failed to run job.");
      if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: message }));
      else setMjManageJobError(message);
    } finally {
      if (inline) setMjRowBatchBusyJobId(null);
    }
  };

  const mjStopJob = async (jobIdArg?: number) => {
    if (!canEditBots) return;
    const jobId = jobIdArg ?? mjManageJobId;
    if (jobId == null) return;
    const inline = jobIdArg != null;
    if (inline) {
      setMjRowBatchBusyJobId(jobId);
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
    } else setMjManageJobError(null);
    try {
      const report = await stopTasksBatchMut({ job_id: jobId }).unwrap();
      await refreshMjJobDetail(jobId, true);
      // A 200 no longer means every container obeyed: the ones ECS refused are
      // named in the report, and staying silent about them is what had
      // operators pressing Stop again (CAL-5949).
      if (report.failed?.length) {
        if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: report.message }));
        else setMjManageJobError(report.message);
      }
    } catch (e) {
      const message = getErr(e, "Failed to stop job.");
      if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: message }));
      else setMjManageJobError(message);
    } finally {
      if (inline) setMjRowBatchBusyJobId(null);
    }
  };

  const handleMjSubmit2FACode = async () => {
    if (!canEditBots) return;
    if (twoFaMjTask == null) return;
    const submittedTaskId = twoFaMjTask.id;
    const trimmed = twoFaMjCode.trim();
    if (!trimmed) {
      setTwoFaMjErr("2FA code is required.");
      return;
    }
    let jobIdMaybe: number | null = null;
    if (typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) && twoFaMjTask.job > 0) {
      jobIdMaybe = twoFaMjTask.job;
    }
    setTwoFaMjErr(null);
    try {
      await submitTask2FACodeMut({ taskId: twoFaMjTask.id, code: trimmed }).unwrap();
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      await safeRefetchChildJobs();
      if (jobIdMaybe != null && expandedMjJobAccordion[jobIdMaybe]) {
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const mapped = await reloadMjAccordionTasks(jobIdMaybe);
          const currentTask = mapped?.find((task) => task.id === submittedTaskId);
          if (!currentTask || !shouldShowPass2FAMj(currentTask)) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
    } catch (e) {
      setTwoFaMjErr(getErr(e, "Could not submit 2FA code."));
    }
  };

  const mjConfirmDeleteNestedTask = async () => {
    if (!canEditBots) return;
    const tid = mjDeleteSingleTaskId;
    if (tid == null) return;
    const matched = Object.entries(mjAccordionTasksByJob).find(([, ta]) => ta.some((t) => t.id === tid));
    const aid = matched ? Number(matched[0]) : undefined;
    try {
      await deleteTaskMutation(tid).unwrap();
      setMjDeleteSingleTaskId(null);
      if (aid != null && aid > 0) {
        setMjNestedTaskSelectionByJob((prev) => {
          const cur = prev[aid];
          if (!cur?.length) return prev;
          const next = cur.filter((x) => x !== tid);
          const out = { ...prev };
          if (next.length) out[aid] = next;
          else delete out[aid];
          return out;
        });
      }
      if (twoFaMjTask?.id === tid) {
        setTwoFaMjTask(null);
        setTwoFaMjCode("");
        setTwoFaMjErr(null);
      }
      await safeRefetchChildJobs();
      if (aid != null && aid > 0) await reloadMjAccordionTasks(aid);
    } catch (e) {
      if (aid != null && aid > 0) {
        setMjJobActionAlertsByJobId((prev) => ({
          ...prev,
          [aid]: getErr(e, "Could not delete task."),
        }));
      }
    }
  };

  const mjConfirmBatchDeleteNestedTasks = async () => {
    if (!canEditBots) return;
    const pack = mjBatchDeleteNestDialog;
    if (pack == null || !pack.ids.length) return;
    const jobId = pack.jobId;
    try {
      await tasksBatchDeleteNestMut({ task_ids: pack.ids }).unwrap();
      setMjBatchDeleteNestDialog(null);
      setMjNestedTaskSelectionByJob((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jobId]) await reloadMjAccordionTasks(jobId);
      if (mjManageJobId === jobId) {
        void dispatch(adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }))
          .unwrap()
          .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
          .catch(() => {});
      }
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not delete tasks."),
      }));
    }
  };
  const mjConfirmDeleteJob = async () => {
    if (!canEditBots) return;
    const delId = mjDeleteJobId;
    if (delId == null) return;
    setMjManageJobError(null);
    try {
      await deleteTaskJobMut({ jobId: delId }).unwrap();
      setMjDeleteJobId(null);
      setMjManageJobId(null);
      setMjManageJobDetail(null);
      setMjAccordionTasksByJob((prev) => {
        const next = { ...prev };
        delete next[delId];
        return next;
      });
      await safeRefetchChildJobs();
      await refetch();
    } catch (e) {
      setMjManageJobError(getErr(e, "Failed to delete job."));
    }
  };

  const handleMjViewJobFiles = (job: Record<string, unknown>) => {
    const prefix = String(job.data_storage_prefix ?? job.path_to_s3_base_folder ?? "").trim();
    const rootPrefix = String(job.data_storage_root_prefix ?? "").trim();
    const rootDisplay = String(job.data_storage_root_display_name ?? "").trim();
    const segs = prefix.split("/").filter(Boolean);
    let leaf = segs.length ? (segs[segs.length - 1] ?? "") : "";
    if (!leaf || leaf === "data") {
      leaf = extractDefaultFolderSlugFromBotName(String(job.bot_name ?? ""));
    }
    const folderMappingSeed =
      rootPrefix && rootDisplay
        ? buildDataStorageFolderMappingSeed(rootDisplay, rootPrefix, prefix)
        : undefined;
    // The header's "Back to Bots Management" button returns here; ?manage=
    // reopens the same Manage job dialog.
    window.sessionStorage.setItem(
      "botsReturnTo",
      `${location.pathname}${mjManageJobId != null ? `?manage=${mjManageJobId}` : location.search}`,
    );
    navigate("/data-storage", {
      state: {
        initialPrefix: prefix,
        initialFname: leaf || mjJobDisplayName(job),
        ...(folderMappingSeed ? { folderMappingSeed } : {}),
        fromBotsManagement: true,
      },
    });
  };

  const handleMjDownloadJobLogs = async (jobId: number, job?: Record<string, unknown>) => {
    setMjManageJobError(null);
    try {
      const blob = await triggerJobLogsArchive(jobId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sanitize = (s: string) =>
        s.replace(/^@/, "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
      const client = sanitize((userLabel ?? "").trim() || `user-${userId}`);
      const jobName = sanitize(job ? mjJobDisplayName(job) : `job-${jobId}`);
      a.download = `${client}_${jobName}.zip` || `job-${jobId}-logs.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMjManageJobError(getErr(e, "No logs found for this job yet."));
    }
  };

  const openMjClearLogs = async (jobId: number) => {
    setMjClearLogsJobId(jobId);
    setMjClearLogsIncludeShared(false);
    setMjClearLogsInventory(null);
    try {
      const inventory = await fetchJobLogsInventory(jobId).unwrap();
      setMjClearLogsInventory({
        own: inventory.own,
        shared: inventory.shared,
      });
    } catch {
      // The counts are advice, not a gate - the dialog still works without them.
      setMjClearLogsInventory(null);
    }
  };

  const handleMjClearJobLogs = async () => {
    if (!canEditBots) return;
    if (mjClearLogsJobId == null) return;
    const jobId = mjClearLogsJobId;
    setMjManageJobError(null);
    try {
      await clearJobLogsMut({
        jobId,
        includeShared: mjClearLogsIncludeShared,
      }).unwrap();
      setMjClearLogsJobId(null);
      if (expandedMjJobAccordionRef.current[jobId]) {
        void reloadMjAccordionTasksRef.current(jobId);
      }
      // Stats are reset server-side together with the logs — refresh the rows.
      await safeRefetchChildJobs();
    } catch (e) {
      setMjClearLogsJobId(null);
      setMjManageJobError(getErr(e, "Failed to clear logs."));
    }
  };

  const openMjEditJobDialogFor = (j: Record<string, unknown> | null) => {
    if (j == null || activeMajor == null) return;
    const jobId = Number(j.id);
    if (!jobId) return;
    setMjEditJobError(null);
    setMjEditJobId(jobId);
    setMjEditBaselineFrom(Number(j.employee_number_from ?? 0));
    setMjEditJobName(mjJobDisplayName(j));
    setMjEditBotName(String(j.bot_name ?? ""));
    setMjEditEmployeeTo(String(j.employee_number_to ?? "0"));
    setMjEditDocumentType(String(j.document_type ?? "") || "");
    const rawAddOpts = String(j.additional_options ?? "") || "";
    if (activeMajor.bot_kind === "delete_bots") {
      const { types, remaining } = extractDocsTypeFromOptions(rawAddOpts);
      setMjEditDocumentTypes(types);
      setMjEditAdditionalOptions(remaining);
      setMjEditProcessErrors(Boolean(j.auto_process_error_employees));
    } else {
      // SKIP_ERROR_EMPLOYEES is kept as it is: it carries the job's sticky
      // "Process error employees" mode, and dropping it here used to switch that
      // mode off on any save. BotOptionFields already hides it from the raw list.
      setMjEditAdditionalOptions(rawAddOpts);
      setMjEditProcessErrors(Boolean(j.auto_process_error_employees));
    }
    setMjEditSeparateProfiles(Boolean(j.use_separate_browser_profiles));
    setMjEditUploadAsZip(Boolean(j.upload_as_zip));
    setMjEditUnzipBefore(Boolean(j.unzip_folder_before_uploading));
    setMjEditUseSsnMatch(j.use_ssn_match !== false);
    setMjEditSourcePath(String(j.path_to_s3_base_folder ?? ""));
    setMjEditJobOpen(true);
  };

  const openMjEditJobDialog = () => openMjEditJobDialogFor(mjEffectiveManagedJob);

  const saveMjEditJob = async () => {
    if (!canEditBots) return;
    if (mjEditJobId == null || activeMajor == null) return;
    setMjEditJobError(null);
    const employeeTo = parseEmployeesNumber(mjEditEmployeeTo);
    if (employeeTo == null) {
      setMjEditJobError("Employees to must be a positive integer.");
      return;
    }
    if (employeeTo < mjEditBaselineFrom) {
      setMjEditJobError("Employee To must be >= Employee From.");
      return;
    }
    const snapshotId = mjEditJobId;
    const updatedBot = mjEditBotName.trim();
    if (!updatedBot) {
      setMjEditJobError("Bot is required.");
      return;
    }
    const isUploadEdit = activeMajor.bot_kind === "upload_bots";
    const isDeleteEdit = activeMajor.bot_kind === "delete_bots";
    if (isUploadEdit && !mjEditDocumentType.trim()) {
      setMjEditJobError("Document type is required.");
      return;
    }
    if (isDeleteEdit) {
      // Validate what the save would actually store, not the raw inputs: a
      // row holding only "[]" is non-empty text but an empty list.
      if (!cleanDocTypes(mjEditDocumentTypes).length) {
        setMjEditJobError("At least one document type is required.");
        return;
      }
    }
    const editAddOpts = isDeleteEdit
      ? buildDeleteAdditionalOptions(mjEditAdditionalOptions.trim(), mjEditDocumentTypes) || null
      : mjEditAdditionalOptions.trim() || null;
    try {
      await updateTaskJob({
        jobId: snapshotId,
        body: {
          name: mjEditJobName.trim(),
          employee_number_from: mjEditBaselineFrom,
          employee_number_to: employeeTo,
          major_job: activeMajor.id,
          bot_name: updatedBot,
          additional_options: editAddOpts,
          auto_process_error_employees: mjEditProcessErrors,
          use_separate_browser_profiles: mjEditSeparateProfiles,
          ...(isUploadEdit
            ? {
                document_type: mjEditDocumentType.trim(),
                upload_as_zip: mjEditUploadAsZip,
                unzip_folder_before_uploading: mjEditUnzipBefore,
                path_to_s3_base_folder: mjEditSourcePath.trim() || undefined,
              }
            : isDeleteEdit
              ? { document_type: "", path_to_s3_base_folder: "" }
              : { document_type: "" }),
          use_ssn_match: mjEditUseSsnMatch,
        },
      }).unwrap();

      setMjAccordionTasksByJob((prev) => {
        delete prev[snapshotId];
        return { ...prev };
      });
      await safeRefetchChildJobs();
      await refetch();
      if (mjManageJobId === snapshotId) {
        void dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(snapshotId, { subscribe: false, forceRefetch: true }),
        )
          .unwrap()
          .then((r) => setMjManageJobDetail(r as Record<string, unknown>))
          .catch(() => {});
      }
      setMjEditJobOpen(false);
      if (expandedMjJobAccordion[snapshotId]) await reloadMjAccordionTasks(snapshotId);
    } catch (e) {
      setMjEditJobError(getErr(e, "Failed to save task job."));
    }
  };

  useEffect(() => {
    setSelectedMajorIds([]);
    setActiveMajorId(null);
    setSelectedChildJobIds([]);
    setExpandedMjJobAccordion({});
    setMjAccordionTasksByJob({});
    setMjJobRangeWarningsByJobId({});
    setMjJobActionAlertsByJobId({});
    setMjNestedTaskSelectionByJob({});
    setMjBatchDeleteNestDialog(null);
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
  }, [userId]);

  useEffect(() => {
    setSelectedChildJobIds([]);
    setPoolConnSelectedIds([]);
    if (activeMajorId != null && !majors.some((m) => m.id === activeMajorId)) setActiveMajorId(null);
  }, [majors, activeMajorId]);

  // Drop any selected connection ids that no longer belong to this major job's
  // pool (e.g. a connection was deleted elsewhere or vendor switched).
  useEffect(() => {
    setPoolConnSelectedIds((prev) => prev.filter((id) => poolConnIdsForMajor.includes(id)));
  }, [poolConnIdsForMajor]);

  useEffect(() => {
    if (detailMajorJobId == null) return;
    if (majorsStillLoading) return;
    if (!majors.some((m) => m.id === detailMajorJobId)) return;
    setActiveMajorId(detailMajorJobId);
  }, [detailMajorJobId, majors, majorsStillLoading]);

  const toggleMajorCheckbox = (id: number, checked: boolean) => {
    setSelectedMajorIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const majorIdsOnPage = useMemo(() => majors.map((m) => m.id), [majors]);
  const allMajorsOnPageSelected =
    majorIdsOnPage.length > 0 && majorIdsOnPage.every((id) => selectedMajorIds.includes(id));
  const someMajorsOnPageSelected = majorIdsOnPage.some((id) => selectedMajorIds.includes(id));

  const toggleSelectAllMajorsOnPage = (checked: boolean) => {
    setSelectedMajorIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...majorIdsOnPage]));
      }
      const drop = new Set(majorIdsOnPage);
      return prev.filter((id) => !drop.has(id));
    });
  };

  const openCreateMajor = () => {
    setMajorEditTarget(null);
    setMajName("");
    setMajVendor("");
    setMajKind("download_bots");
    setMajorFormError(null);
    setMajorEditOpen(true);
  };

  const openEditMajor = (m: MajorJob) => {
    const src = majors.find((x) => x.id === m.id) ?? m;
    setMajorEditTarget(src);
    setMajName(src.name);
    setMajVendor(src.vendor);
    setMajKind(src.bot_kind);
    setMajorFormError(null);
    setMajorEditOpen(true);
  };

  const saveMajorForm = async () => {
    if (!canEditBots) return;
    if (!majName.trim()) {
      setMajorFormError("Name is required.");
      return;
    }
    if (!majVendor.trim()) {
      setMajorFormError("Vendor is required.");
      return;
    }
    try {
      if (majorEditTarget) {
        await updateMajorJob({
          majorJobId: majorEditTarget.id,
          invalidateUserId: userId,
          body: {
            name: majName.trim(),
            vendor: majVendor.trim(),
            bot_kind: majKind,
          },
        }).unwrap();
      } else {
        await createMajorJob({
          userId,
          name: majName.trim(),
          vendor: majVendor.trim(),
          bot_kind: majKind,
          included_connection_ids: [],
        }).unwrap();
      }
      await refetch();
      setMajorEditOpen(false);
    } catch {
      setMajorFormError("Failed to save major job.");
    }
  };

  const confirmRemoveMajor = async (m: MajorJob) => {
    if (!canEditBots) return;
    try {
      await deleteMajorJob({ majorJobId: m.id, invalidateUserId: userId }).unwrap();
      if (activeMajorId === m.id) setActiveMajorId(null);
      if (detailMajorJobId === m.id) onExitMajorDetail?.();
      setMajorRowDeleteTarget(null);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const confirmBulkDeleteMajors = async () => {
    if (!canEditBots) return;
    if (!selectedMajorIds.length) return;
    try {
      for (const mid of selectedMajorIds) {
        await deleteMajorJob({ majorJobId: mid, invalidateUserId: userId }).unwrap();
        if (detailMajorJobId === mid) onExitMajorDetail?.();
      }
      setSelectedMajorIds([]);
      setActiveMajorId(null);
      setMajorBulkDeleteOpen(false);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const applyPool = async (nextIds: number[]) => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    await updateMajorJob({
      majorJobId: activeMajor.id,
      invalidateUserId: userId,
      body: { included_connection_ids: Array.from(new Set(nextIds)).sort((a, b) => a - b) },
    }).unwrap();
    await refetch();
  };

  const linkExistingToMajor = async (connectionId: number) => {
    if (!activeMajor) return;
    const c = connections.find((x) => x.id === connectionId);
    if (!c || normVendor(c.vendor) !== normVendor(activeMajor.vendor)) return;
    const next = Array.from(new Set([...(activeMajor.included_connection_ids ?? []), connectionId]));
    await applyPool(next);
  };

  const openMajConnCreate = () => {
    if (!activeMajor) return;
    setMcUsername("");
    setMcHost("");
    setMcClientCode("");
    setMcPassword("");
    setMcShowPw(false);
    setMcError(null);
    setMajConnCreateOpen(true);
  };

  const submitMajConnCreate = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    setMcError(null);
    if (!mcUsername.trim()) {
      setMcError("Username is required.");
      return;
    }
    try {
      const raw = await createConnectionMutation({
        userId,
        body: {
          vendor: activeMajor.vendor.trim(),
          username: mcUsername.trim(),
          host: mcHost.trim(),
          client_code: mcClientCode.trim(),
          password: mcPassword,
          port: "9223",
        },
      }).unwrap();
      const newId = typeof raw.message?.data?.id === "number" ? raw.message.data.id : null;
      await onRefreshConnections();
      setMajConnCreateOpen(false);
      if (newId != null) await linkExistingToMajor(newId);
      // Created from inside the New task job form: slot the fresh login in.
      if (newId != null && childDialogOpen) {
        setChildError(null);
        setChildPlans((prev) =>
          prev.length === 0 || prev[0].connection_id == null
            ? [{ connection_id: newId, task_target_count: "1" }, ...prev.slice(1)]
            : prev,
        );
      }
    } catch (e) {
      setMcError(getErr(e, "Failed to create connection."));
    }
  };

  const openMajConnEdit = (c: AdminVendorConnection) => {
    setMajConnEdit(c);
    setMeUsername(c.username || "");
    setMeHost(c.host || "");
    setMeClientCode(c.client_code || "");
    setMePassword(c.password || "");
    setMeShowPw(false);
    setMeError(null);
  };

  const submitMajConnEdit = async () => {
    if (!canEditBots) return;
    if (!majConnEdit) return;
    setMeError(null);
    if (!meUsername.trim()) {
      setMeError("Username is required.");
      return;
    }
    try {
      await updateConnectionMutation({
        connectionId: majConnEdit.id,
        body: {
          vendor: majConnEdit.vendor,
          username: meUsername.trim(),
          host: meHost.trim() || null,
          client_code: meClientCode.trim() || null,
          password: mePassword,
          port: "9223",
        },
      }).unwrap();
      await onRefreshConnections();
      setMajConnEdit(null);
    } catch (e) {
      setMeError(getErr(e, "Failed to update connection."));
    }
  };

  const deleteConnectionHard = async (cid: number) => {
    if (!canEditBots) return;
    try {
      await deleteConnectionMutation(cid).unwrap();
      await onRefreshConnections();
      setDeleteConnConfirm(null);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const toggleChildCheckbox = (id: number, checked: boolean) => {
    setSelectedChildJobIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  /** Confirmation dialog state for the bulk "Delete selected task jobs" action. */
  const [bulkChildDeleteOpen, setBulkChildDeleteOpen] = useState(false);

  const openBulkDeleteChildren = () => {
    if (!selectedChildJobIds.length) return;
    setBulkChildDeleteOpen(true);
  };

  const confirmBulkDeleteChildren = async () => {
    if (!canEditBots) return;
    if (!selectedChildJobIds.length) {
      setBulkChildDeleteOpen(false);
      return;
    }
    try {
      await taskJobsBatchDelete({ job_ids: selectedChildJobIds }).unwrap();
      setSelectedChildJobIds([]);
      setExpandedMjJobAccordion({});
      setMjAccordionTasksByJob({});
      await safeRefetchChildJobs();
      await refetch();
    } catch {
      /* keep dialog open on error so the user notices via the alerts strip */
    } finally {
      setBulkChildDeleteOpen(false);
    }
  };

  const openCreateChildJob = () => {
    if (!activeMajor) return;
    if (!majorConnPickerIds.length) {
      setChildError(
        `No bot logins exist for vendor "${activeMajor.vendor}" on this user yet. ` +
          'Use the "New connection" button below to create one without leaving this form.',
      );
      setChildDialogOpen(true);
      return;
    }
    setChildError(null);
    setTaskJobListName("");
    const firstConn = majorConnPickerIds[0];
    setChildPlans([{ connection_id: firstConn, task_target_count: "1" }]);
    setChildBotName("");
    setChildAdditionalOptions("");
    setChildProcessErrors(false);
    setChildSeparateProfiles(false);
    setChildDocumentType("");
    setChildDocumentTypes([""]);
    setChildUploadAsZip(false);
    setChildUnzipBefore(false);
    setChildUseSsnMatch(true);
    setChildSourcePath("");
    setChildDialogOpen(true);
  };

  const createChildSubmit = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    if (!childBotName.trim()) {
      setChildError("Bot is required.");
      return;
    }
    if (!taskJobListName.trim()) {
      setChildError("Task Job Name is required.");
      return;
    }
    const newTaskJobName = taskJobListName.trim().slice(0, 255);
    const connPlans = normalizePlans(childPlans);
    if (!connPlans.length) {
      setChildError("Add at least one connection row with a task count.");
      return;
    }
    const totalTasks = connPlans.reduce((acc, r) => acc + r.task_target_count, 0);
    if (totalTasks < 1) {
      setChildError("Total tasks must be >= 1.");
      return;
    }
    if (totalTasks > MAX_BATCH_TASKS) {
      setChildError(`Total tasks (${totalTasks}) exceeds the per-job limit of ${MAX_BATCH_TASKS}.`);
      return;
    }
    const vnormCreate = normVendor(activeMajor.vendor);
    for (const r of connPlans) {
      const c = connections.find((x) => x.id === r.connection_id);
      if (!c || normVendor(c.vendor) !== vnormCreate) {
        setChildError(`Connection vendor mismatch for ${activeMajor.vendor}.`);
        return;
      }
    }
    const isUploadKind = activeMajor.bot_kind === "upload_bots";
    const isDeleteKind = activeMajor.bot_kind === "delete_bots";
    if (isUploadKind && !childDocumentType.trim()) {
      setChildError("Document type is required.");
      return;
    }
    if (isDeleteKind) {
      // Validate what the save would actually store, not the raw inputs: a
      // row holding only "[]" is non-empty text but an empty list.
      if (!cleanDocTypes(childDocumentTypes).length) {
        setChildError("At least one document type is required.");
        return;
      }
    }
    if (isUploadKind && !childSourcePath.trim()) {
      setChildError("Source folder is required for upload bots. Click Browse to select one.");
      return;
    }
    const docType = isUploadKind ? childDocumentType.trim() : "";
    const folderSlug = extractDefaultFolderSlugFromBotName(childBotName.trim());
    const defaultPath = isUploadKind
      ? childSourcePath.trim()
      : isDeleteKind
        ? ""
        : `users/${userId}/${activeMajor.vendor}/data/${folderSlug}`;
    const addOpts = isDeleteKind
      ? buildDeleteAdditionalOptions(childAdditionalOptions.trim(), childDocumentTypes) || null
      : childAdditionalOptions.trim() || null;
    try {
      await ensureMajorIncludesConnectionIds(connPlans.map((r) => r.connection_id));
      const primaryConn = connPlans[0].connection_id;
      const primaryCount = connPlans[0].task_target_count;
      const res = await createTasksBatch({
        connection: primaryConn,
        bot_name: childBotName.trim(),
        document_type: docType,
        task_status: "PENDING",
        bot_work_status: "NOT_LOGGED_IN",
        path_to_s3_base_folder: defaultPath,
        browser_profile_id: 1,
        upload_as_zip: isUploadKind ? childUploadAsZip : false,
        unzip_folder_before_uploading: isUploadKind ? childUnzipBefore : false,
        use_ssn_match: childUseSsnMatch,
        tasks_number: primaryCount,
        employee_number_from: 0,
        use_separate_profiles: childSeparateProfiles,
        create_job: true,
        job_name: newTaskJobName,
        major_job_id: activeMajor.id,
        additional_options: addOpts,
        auto_process_error_employees: childProcessErrors,
      }).unwrap();
      const jobId = Number((res as { job?: { id?: unknown } }).job?.id);
      if (!jobId || Number.isNaN(jobId)) throw new Error("missing job id");
      await updateTaskJob({
        jobId,
        body: {
          name: newTaskJobName,
          major_job: activeMajor.id,
          connection_plans: connPlans,
          additional_options: addOpts,
        },
      }).unwrap();
      for (let i = 1; i < connPlans.length; i++) {
        const extra = connPlans[i];
        if (extra.task_target_count < 1) continue;
        await addTasksToJobMutation({
          jobId,
          count: extra.task_target_count,
          connection_id: extra.connection_id,
        }).unwrap();
      }
      await safeRefetchChildJobs();
      await refetch();
      setChildDialogOpen(false);
    } catch (e) {
      setChildError(getErr(e, "Unable to create task job. Check inputs and try again."));
    }
  };

  const mjAddTasksDialogStats = useMemo(() => {
    if (!mjAddTasksOpen || mjAddTasksJobId == null) return null;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];
    const det =
      mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawDt = det ? ((det as Record<string, unknown>).tasks as unknown) : undefined;
    if (
      rawDt &&
      Array.isArray(rawDt) &&
      rawDt.length > 0 &&
      rawDt.length >= currentTasks.length
    ) {
      currentTasks = (rawDt as Record<string, unknown>[]).map((t) =>
        normalizeRawTaskToMj(t, mjAddTasksJobId),
      );
    }

    const jRec =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (det ? (det as Record<string, unknown>) : null) ??
      (((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null));

    const plansSnap = jRec ? parseConnectionPlansFromJobRow(jRec) : [];
    const planMode = plansSnap.length > 0;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentTasks.length);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    // Block adding only on global 30-cap or running tasks. Plan-quota errors
    // surfaced by the resolver are no longer enforced - the backend auto-raises
    // per-connection quotas, so the only hard frontend rule is "<=30 total".
    const jobHasRunningTasks = jRec ? mjJobAnythingInFlight(jRec) : false;
    const overGlobalLimit =
      "error" in resolved && /total adds .* exceed/i.test(resolved.error);
    const planLevelOnly = "error" in resolved && !overGlobalLimit;

    const addBlockingReason = overGlobalLimit
      ? resolved.error
      : jobHasRunningTasks
        ? "Stop all tasks of this task job before adding new ones."
        : null;

    const planLevelHint = planLevelOnly ? (resolved as { error: string }).error : null;

    const totalHelp = `${currentTasks.length} / ${MAX_BATCH_TASKS} tasks total - up to ${jobTotalRemain} more allowed on this job.`;

    const countHint =
      jobTotalRemain < 1
        ? `This task job is at its ${MAX_BATCH_TASKS} task limit.`
        : `Up to ${jobTotalRemain} more task(s) can be added.`;

    return {
      plansSnap,
      totalHelp,
      planMode,
      legacyMultiLoginHint: null as string | null,
      addBlockingReason,
      planLevelHint,
      countHint,
      jobHasRunningTasks,
    };
  }, [
    mjAddTasksOpen,
    mjAddTasksJobId,
    mjAccordionTasksByJob,
    mjEffectiveManagedJob,
    mjManageJobDetail,
    childJobs,
    mjBulkAddRows,
    mjAddPlanConnPool,
    majorConnPickerIds,
    connections,
  ]);

  const closeMjManage = () => {
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
    setMatchesMergeSuccess(null);
    resetMatchesMergeState();
  };

  if (isDetailMode && detailMajorNotFound) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
        <Alert severity="warning">This automation was not found (or is not visible for this user).</Alert>
        {onExitMajorDetail && (
          <Button variant="outlined" onClick={onExitMajorDetail}>
            Back to Major Jobs list
          </Button>
        )}
      </Box>
    );
  }

  if (!isDetailMode && majorsStillLoading && majors.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isDetailMode ? "Major job workspace" : "Major jobs"}
          </Typography>
          {!isDetailMode && (
            // Hidden on phones — the mobile user switcher above already names the user.
            <Box sx={{ mt: 0.75, display: { xs: "none", sm: "block" } }}>
              <MajorJobStripItem
                title="User"
                value={userLabel?.trim() ? userLabel.trim() : `ID ${userId}`}
                avatar
              />
            </Box>
          )}
        </Box>
        {/* Mobile: full-width stacked buttons (drawer-like); desktop: inline row. */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": { minHeight: { xs: 40, sm: 0 } },
          }}
        >
          {!isDetailMode && (
            <>
              <Button size="small" variant="contained" onClick={openCreateMajor} disabled={isCreating || !canEditBots}>
                New Major Job
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenConnectionsScreen?.()}
                disabled={!onOpenConnectionsScreen}
              >
                Connections
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => {
                  setMajorBulkDeleteOpen(true);
                }}
                disabled={!selectedMajorIds.length || isDeletingMajor || !canEditBots}
              >
                Delete selected
              </Button>
            </>
          )}
          {isDetailMode && onExitMajorDetail && (
            <Button size="small" variant="outlined" onClick={onExitMajorDetail}>
              Back to Major Jobs list
            </Button>
          )}
        </Box>
      </Box>

      {!isDetailMode && (
        <BotsTableScroll fill>
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 900 }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col />
            <col style={{ width: 160 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 130 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                <Checkbox
                  size="small"
                  indeterminate={someMajorsOnPageSelected && !allMajorsOnPageSelected}
                  checked={allMajorsOnPageSelected}
                  disabled={!majors.length}
                  onChange={(e) => toggleSelectAllMajorsOnPage(e.target.checked)}
                  inputProps={{ "aria-label": "Select all major jobs on this page" }}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Name</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Vendor</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Kind</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Connections</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Jobs</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>SSN</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {majors.map((m) => (
              <TableRow
                key={m.id}
                hover
                onClick={() => {
                  onOpenMajorDetail?.(m.id);
                }}
                sx={{ cursor: onOpenMajorDetail ? "pointer" : "default" }}
              >
                <TableCell
                  padding="checkbox"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ textAlign: "center", verticalAlign: "middle" }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedMajorIds.includes(m.id)}
                    onChange={(e) => toggleMajorCheckbox(m.id, e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  <Typography variant="body2" noWrap title={m.name}>
                    {m.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  <Typography variant="body2" noWrap title={m.vendor}>
                    {m.vendor}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  {m.bot_kind === "upload_bots" ? "Upload" : m.bot_kind === "delete_bots" ? "Delete" : "Download"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>{connections.filter((c) => normVendor(c.vendor) === normVendor(m.vendor)).length}</TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>{m.child_job_ids?.length ?? 0}</TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  {m.ssn_file_exists === true && (
                    <Tooltip title="SSN file exists">
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditMajor(m);
                      }}
                      disabled={isSavingMajor || !canEditBots}
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete major job"
                          disabled={!canEditBots}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMajorRowDeleteTarget(m);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!majors.length && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 3 }}>
                  <Typography color="text.secondary">No automations for this user yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </BotsTableScroll>
      )}

      <DeleteJobConfirmDialog
        open={majorRowDeleteTarget != null}
        title="Delete major job?"
        message={
          majorRowDeleteTarget ? (
            <>
              Delete &quot;{majorRowDeleteTarget.name}&quot;? Nested task jobs for this automation will be removed
              (per server rules).
            </>
          ) : null
        }
        onCancel={() => setMajorRowDeleteTarget(null)}
        onConfirm={() => {
          if (majorRowDeleteTarget) void confirmRemoveMajor(majorRowDeleteTarget);
        }}
        confirmDisabled={majorRowDeleteTarget == null || !canEditBots}
        busy={isDeletingMajor}
      />

      <DeleteJobConfirmDialog
        open={majorBulkDeleteOpen}
        title="Delete selected major jobs?"
        message={
          <>
            Delete {selectedMajorIds.length} selected major job{selectedMajorIds.length === 1 ? "" : "s"}? Nested task
            jobs will be removed (per server rules).
          </>
        }
        onCancel={() => setMajorBulkDeleteOpen(false)}
        onConfirm={() => void confirmBulkDeleteMajors()}
        confirmDisabled={!selectedMajorIds.length || !canEditBots}
        busy={isDeletingMajor}
      />

      {isDetailMode && !activeMajor && !detailMajorNotFound && <MajorJobWorkspaceSkeleton />}

      {activeMajor && isDetailMode && (
        <Paper variant="outlined" sx={{ p: 1.5, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {activeMajor.name}
              </Typography>
              {isPhone ? (
                <WorkspaceMetaList
                  rows={[
                    { title: "User", value: userLabel?.trim() ? userLabel.trim() : "User" },
                    {
                      title: "Action",
                      value: mjKindLabel(activeMajor.bot_kind),
                      color: `${mjKindTone(activeMajor.bot_kind)}.main`,
                    },
                    { title: "Vendor", value: (activeMajor.vendor ?? "").trim() || "-" },
                    ...(activeMajor.ssn_file_exists != null
                      ? [
                          {
                            title: "SSN",
                            value: activeMajor.ssn_file_exists ? "ready" : "not ready",
                            color: activeMajor.ssn_file_exists ? "success.main" : "warning.main",
                          },
                        ]
                      : []),
                    ...(mjBrowserProfiles
                      ? [{ title: "Browser profile", value: mjBrowserProfilesLabel(mjBrowserProfiles) }]
                      : []),
                  ]}
                />
              ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "stretch", mt: 0.75 }}>
                <MetaPill
                  title="User"
                  tone="primary"
                  icon={<PersonOutline />}
                  value={userLabel?.trim() ? userLabel.trim() : "User"}
                />
                <MetaPill
                  title="Action"
                  tone={mjKindTone(activeMajor.bot_kind)}
                  icon={mjKindIcon(activeMajor.bot_kind)}
                  value={mjKindLabel(activeMajor.bot_kind)}
                />
                <MetaPill
                  title="Vendor"
                  tone="secondary"
                  icon={<BusinessIcon />}
                  value={(activeMajor.vendor ?? "").trim() || "-"}
                />
                {activeMajor.ssn_file_exists != null && (
                  <MetaPill
                    title="SSN file"
                    tone={activeMajor.ssn_file_exists ? "success" : "warning"}
                    icon={activeMajor.ssn_file_exists ? <VerifiedUserIcon /> : <WarningAmberIcon />}
                    value={activeMajor.ssn_file_exists ? "ready" : "not ready"}
                  />
                )}
                {mjBrowserProfiles && (
                  <MetaPill
                    title="Browser profile"
                    tone={mjBrowserProfiles.profiles.length ? "info" : "neutral"}
                    icon={<LanguageIcon />}
                    value={mjBrowserProfilesLabel(mjBrowserProfiles)}
                    action={
                      mjBrowserProfiles.profiles.length > 0 && canEditBots ? (
                        <Tooltip title="Delete the stored Chrome profile(s) of this vendor - every bot of this user on this vendor logs in from scratch next time (2FA / captcha).">
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label="Delete browser profile"
                            onClick={() => setMjBrowserProfilesPrompt(true)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  />
                )}
              </Box>
              )}
              {mjBrowserProfilesError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setMjBrowserProfilesError(null)}>
                  {mjBrowserProfilesError}
                </Alert>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => openEditMajor(activeMajor)}
                disabled={isSavingMajor || majorHasRunningOrProgress || !canEditBots}
                title={
                  majorHasRunningOrProgress
                    ? "Stop all tasks (no progress) before editing the major job"
                    : ""
                }
                sx={{ flex: { xs: 1, sm: "0 0 auto" }, minHeight: { xs: 40, sm: 0 }, whiteSpace: "nowrap" }}
              >
                {isPhone ? "Edit" : "Edit Major Job"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => {
                  setMajorRowDeleteTarget(activeMajor);
                }}
                disabled={isDeletingMajor || !canEditBots}
                sx={{ flex: { xs: 1, sm: "0 0 auto" }, minHeight: { xs: 40, sm: 0 }, whiteSpace: "nowrap" }}
              >
                {isPhone ? "Delete" : "Delete Major Job"}
              </Button>
            </Stack>
          </Box>
          {poolOrphans.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {poolOrphans.length} saved pool id(s) do not match current connections for this vendor.
            </Alert>
          )}
          {(() => {
            const isDeleteKind = activeMajor.bot_kind === "delete_bots";
            return (
              <>
                <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={manageTab}
                    onChange={handleManageTabChange}
                    variant="scrollable"
                    allowScrollButtonsMobile
                    sx={{ minHeight: 40, flex: 1, minWidth: 0 }}
                  >
                    <Tab label="Task jobs" value="jobs" />
                    {!isDeleteKind && <Tab label="SSN Job" value="ssn" />}
                    <Tab label="Connections" value="connections" />
                  </Tabs>
                  <LiveUpdatesIndicator connected={mjWsConnected} />
                </Box>
                {/* ── SSN Job TabPanel ─────────────────────────────────── */}
                {!isDeleteKind && <TabPanel value={manageTab} index="ssn">
            {ssnJobLoading && <LinearProgress sx={{ mb: 1 }} />}
            {ssnJobError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {ssnJobError}
              </Alert>
            )}
            {!ssnJobLoading && !ssnJobError && ssnJobData !== null && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                {/* SSN File status */}
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN file</Typography>
                    <Box sx={{ flex: 1 }} />
                    {ssnJobData.ssn_file_exists === true && (
                      <>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => activeMajor && void openSsnFileViewer(activeMajor.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            sx={{ mr: 0.5 }}
                            onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {ssnJobData.ssn_file_exists === true && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={ssnJobFileDeleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
                        disabled={ssnJobFileDeleting || !canEditBots}
                        onClick={async () => {
                          if (!activeMajor || !canEditBots) return;
                          setSsnJobFileDeleting(true);
                          try {
                            await deleteSsnFileMut(activeMajor.id).unwrap();
                            await loadSsnJob(activeMajor.id);
                          } catch { } finally { setSsnJobFileDeleting(false); }
                        }}
                      >
                        Delete file
                      </Button>
                    )}
                  </Stack>
                  {ssnJobData.ssn_file_exists === true ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">
                        ssn_info.json exists in S3
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {ssnJobData.ssn_file_exists === null
                        ? "SSN file status unknown (S3 not configured)"
                        : "No SSN file in S3"}
                    </Typography>
                  )}
                </Box>

                {/* SSN Job */}
                {ssnJobData.job == null ? (() => {
                  const vendorConns = connections.filter((c) => c.vendor?.toLowerCase() === activeMajor?.vendor?.toLowerCase());
                  const effectiveSsnConnId = ssnJobSelectedConnId ?? (vendorConns.length > 0 ? vendorConns[0].id : null);
                  return (
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, py: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN Job</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vendorConns.length === 0
                        ? `No SSN Job yet - and no bot logins exist for vendor "${activeMajor?.vendor ?? ""}" on this user, so one cannot be created.`
                        : "No SSN Job created yet."}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {vendorConns.length === 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add fontSize="small" />}
                        onClick={openMajConnCreate}
                        disabled={!activeMajor || !canEditBots}
                      >
                        New connection
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add fontSize="small" />}
                      disabled={vendorConns.length === 0 || !canEditBots}
                      onClick={() => openSsnJobDialog("create", effectiveSsnConnId)}
                    >
                      Create
                    </Button>
                  </Box>
                  );
                })() : (() => {
                  const ssnTask = ssnJobData.task;
                  const ssnTaskId = ssnTask ? Number(ssnTask.id) : null;
                  const ssnIsRunning = mjTaskShowsStopInsteadOfRun(String(ssnTask?.task_status ?? ""));
                  const ssnCurrentCid = typeof ssnTask?.connection === "number" ? ssnTask.connection : null;
                  const ssnJobId = Number(ssnJobData.job?.id ?? 0);
                  const ssnAsMjTask: MjConnectionTask = {
                    id: ssnTaskId ?? 0,
                    job: ssnJobId,
                    task_status: String(ssnTask?.task_status ?? ""),
                    bot_work_status: String(ssnTask?.bot_work_status ?? ""),
                    task2fa_status: typeof ssnTask?.task2fa_status === "string" ? ssnTask.task2fa_status : "not_requested",
                    task2fa_auto_until: typeof ssnTask?.task2fa_auto_until === "string" ? ssnTask.task2fa_auto_until : null,
                    live_view_status: typeof ssnTask?.live_view_status === "string" ? ssnTask.live_view_status : "not_requested",
                    live_view_phase: typeof ssnTask?.live_view_phase === "string" ? ssnTask.live_view_phase : "",
                    bot_name: String(ssnTask?.bot_name ?? ""),
                    employee_number_from: Number(ssnTask?.employee_number_from ?? 0),
                    employee_number_to: Number(ssnTask?.employee_number_to ?? 0),
                    connection: ssnCurrentCid ?? undefined,
                  };
                  return (
                    <Accordion
                      expanded={ssnAccordionExpanded}
                      disableGutters
                      elevation={0}
                      TransitionProps={{ timeout: 0 }}
                      onChange={() => setSsnAccordionExpanded((v) => !v)}
                      sx={MJ_ACCORDION_ROW_SX}
                    >
                      <AccordionSummary
                        sx={{
                          ...MJ_ACC_SUMMARY_SX,
                          px: 2,
                          boxSizing: "border-box",
                          "& .MuiAccordionSummary-content": {
                            ...MJ_ACC_SUMMARY_SX["& .MuiAccordionSummary-content"],
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            margin: "0 !important",
                          },
                        }}
                      >
                        <Typography fontWeight={700} noWrap>SSN Job</Typography>
                        <StatusChipMj
                          label={String(ssnJobData.job?.task_status ?? "PENDING")}
                          color={taskRunStatusChipColor(String(ssnJobData.job?.task_status ?? "PENDING"))}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!canEditBots}
                            onClick={(e) => { e.stopPropagation(); openSsnJobDialog("edit"); }}
                          >
                            Edit job
                          </Button>
                          <Tooltip title="Refresh">
                            <span>
                              <IconButton size="small" color="primary" onClick={async (e) => { e.stopPropagation(); if (activeMajor) await loadSsnJob(activeMajor.id); }}>
                                {ssnJobLoading ? <CircularProgress size={18} color="primary" /> : <ReplayIcon sx={{ fontSize: 20 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete SSN job">
                            <span>
                              <IconButton size="small" color="error" disabled={ssnJobDeleting || ssnIsRunning || !canEditBots}
                                onClick={(e) => { e.stopPropagation(); setSsnJobDeleteConfirmOpen(true); }}>
                                {ssnJobDeleting ? <CircularProgress size={16} color="error" /> : <DeleteOutlineIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={ssnAccordionExpanded ? "Collapse" : "Expand"}>
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setSsnAccordionExpanded((v) => !v); }}>
                              <ExpandMore sx={{ fontSize: 24, transition: "transform 0.2s", transform: ssnAccordionExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2, borderTop: 1, borderColor: "divider" }}>
                        {ssnTask && (
                          <BotsTableScroll>
                          <Table size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: 760 }}>
                            <colgroup>
                              <col style={{ width: "48px" }} />
                              <col style={{ width: "145px" }} />
                              <col style={{ width: "145px" }} />
                              <col />
                              <col style={{ width: "160px" }} />
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>№</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Bot</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Connection</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {renderMjTaskRow({
                                task: ssnAsMjTask,
                                jobId: ssnJobId,
                                index: 1,
                              })}
                            </TableBody>
                          </Table>
                          </BotsTableScroll>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </Box>
            )}
            {ssnJobError && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <Button variant="outlined" size="small" onClick={() => activeMajor && loadSsnJob(activeMajor.id)}>
                  Retry
                </Button>
              </Box>
            )}
          </TabPanel>}
                <TabPanel value={manageTab} index="connections">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Available connections
                  </Typography>
            {connectionsError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Failed to load connections.
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 1.5,
                "& .MuiButton-root": {
                  flex: { xs: 1, sm: "0 0 auto" },
                  minHeight: { xs: 40, sm: 0 },
                },
              }}
            >
              <Button size="small" variant="contained" onClick={openMajConnCreate} disabled={!activeMajor || !canEditBots}>
                New connection
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => setPoolConnBulkDeleteOpen(true)}
                disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
              >
                Delete selected
              </Button>
            </Box>
            {connectionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <BotsTableScroll>
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 860 }}>
                  <colgroup>
                    <col style={{ width: 48 }} />
                    <col style={{ width: 60 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 130 }} />
                    <col />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 150 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                        <Checkbox
                          size="small"
                          checked={
                            poolConnIdsForMajor.length > 0 &&
                            poolConnIdsForMajor.every((id) => poolConnSelectedIds.includes(id))
                          }
                          indeterminate={
                            poolConnIdsForMajor.some((id) => poolConnSelectedIds.includes(id)) &&
                            !poolConnIdsForMajor.every((id) => poolConnSelectedIds.includes(id))
                          }
                          disabled={!poolConnIdsForMajor.length}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPoolConnSelectedIds((prev) => {
                              if (checked) {
                                return Array.from(new Set([...prev, ...poolConnIdsForMajor]));
                              }
                              const drop = new Set(poolConnIdsForMajor);
                              return prev.filter((id) => !drop.has(id));
                            });
                          }}
                          inputProps={{ "aria-label": "Select all linked connections" }}
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>№</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Login</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Vendor</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Host</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Client code</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poolConnIdsForMajor.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 3 }}>
                          <Typography color="text.secondary">
                            No vendor-matching bot connections linked to this automation yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      poolConnIdsForMajor.map((cid, idx) => {
                        const row = connections.find((c) => c.id === cid);
                        const usernameLabel = row?.username?.trim()
                          ? `@${row.username.trim()}`
                          : "-";
                        const hostLabel =
                          row?.host?.trim() || `default ${row?.vendor?.trim() || "vendor"} host`;
                        return (
                          <TableRow key={cid} hover>
                            <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                              <Checkbox
                                size="small"
                                checked={poolConnSelectedIds.includes(cid)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setPoolConnSelectedIds((prev) =>
                                    checked ? [...prev, cid] : prev.filter((x) => x !== cid),
                                  );
                                }}
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>{idx + 1}</TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={usernameLabel}>
                                {row ? usernameLabel : "..."}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={row?.vendor ?? ""}>
                                {row?.vendor ?? "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={hostLabel}
                                color={row?.host?.trim() ? "text.primary" : "text.secondary"}
                              >
                                {hostLabel}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={row?.client_code ?? ""}>
                                {row?.client_code?.trim() || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="center"
                                alignItems="center"
                              >
                                {row && (
                                  <Button size="small" disabled={!canEditBots} onClick={() => openMajConnEdit(row)}>
                                    Edit
                                  </Button>
                                )}
                                <Tooltip title="Delete connection">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label="Delete connection"
                                      disabled={!canEditBots}
                                      onClick={() => setDeleteConnConfirm(cid)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </BotsTableScroll>
              )}
                </TabPanel>
              </>
            );
          })()}
          <TabPanel value={manageTab} index="jobs">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Task jobs
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  ml: "auto",
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiButton-root": {
                    flex: { xs: 1, sm: "0 0 auto" },
                    minHeight: { xs: 40, sm: 0 },
                  },
                }}
              >
                <Button size="small" variant="contained" onClick={openCreateChildJob} disabled={!canEditBots}>
                  Add Task Job
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={openBulkDeleteChildren}
                  disabled={selectedChildJobIds.length === 0 || isDeletingJobsBulk || !canEditBots}
                >
                  Delete selected
                </Button>
              </Box>
            </Box>

            <BotsTableScroll>
              <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: MJ_JOBS_TABLE_MIN_WIDTH }}>
                <colgroup>
                  <col style={{ width: 48 }} />
                  <col />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 56 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                      <Checkbox
                        size="small"
                        indeterminate={taskJobRowsSelectIndeterminate}
                        checked={allTaskJobRowsSelected}
                        disabled={!taskJobRowIdsOnPage.length}
                        onChange={(e) => toggleSelectAllTaskJobRows(e.target.checked)}
                        inputProps={{ "aria-label": "Select all task jobs in this list" }}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BOTS_HEADER_CELL_SX, textAlign: "left" }}>Name</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Status</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Tasks</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Total</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Success</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Error</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                    <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX} />
                  </TableRow>
                </TableHead>
                <TableBody>
              {childJobs.filter((raw) => !isSSNJob(raw as Record<string, unknown>)).map((raw) => {
                const baseRow = raw as Record<string, unknown>;
                const id = Number(baseRow.id);
                const row = { ...baseRow, ...(mjJobDetailById[id] ?? {}) };
                const employeeStats = mjEmployeeStatsFromJob(row);
                const expanded = Boolean(expandedMjJobAccordion[id]);
                const tasks = mjAccordionTasksByJob[id] ?? [];
                const nestSel = mjNestedTaskSelectionByJob[id] ?? [];
                const allNestIds = tasks.map((t) => t.id);
                const allNestSelected = allNestIds.length > 0 && allNestIds.every((tid) => nestSel.includes(tid));
                const { running, total } = mjJobStatsTotals(row);
                const busyRow = Boolean(mjRefreshingJobIds[id]);
                return (
                  <Fragment key={id}>
                    <TableRow
                      hover
                      selected={expanded}
                      onClick={(e) => handleMjAccordionExpanded(e, id, !expanded)}
                      sx={{
                        cursor: "pointer",
                        ...(expanded ? { "& > td": { borderBottom: 0 } } : {}),
                      }}
                    >
                      <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedChildJobIds.includes(id)}
                          onChange={(e) => toggleChildCheckbox(id, e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <Typography fontWeight={700} noWrap title={mjJobDisplayName(row)}>
                          {mjJobDisplayName(row)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BOTS_BODY_CELL_SX}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <StatusChipMj
                            label={String(row.task_status ?? "")}
                            color={taskRunStatusChipColor(String(row.task_status ?? ""))}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Typography variant="body2" color="text.secondary">
                          {running}/{total}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Typography variant="body2" color="text.secondary">
                          {employeeStats.total}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Tooltip title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}>
                          <Typography variant="body2" color="text.secondary">
                            {employeeStats.success}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Tooltip title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}>
                          <Typography variant="body2" color="text.secondary">
                            {employeeStats.error}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{ ...BOTS_BODY_CELL_SX, whiteSpace: "nowrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Tooltip title="Refresh job row - recounts Total / Success / Errors from the stored logs">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={busyRow}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  void refreshMjJobRow(id);
                                }}
                              >
                                {busyRow ? (
                                  <CircularProgress size={18} color="primary" />
                                ) : (
                                  <ReplayIcon sx={{ fontSize: 20 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {activeMajor.bot_kind !== "delete_bots" && (
                            <Button
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleMjViewJobFiles(row);
                              }}
                            >
                              Files
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX}>
                        <IconButton size="small" aria-label={expanded ? "Collapse task job" : "Expand task job"}>
                          <ExpandMore
                            sx={{
                              color: "primary.main",
                              fontSize: 24,
                              transition: "transform 150ms",
                              transform: expanded ? "rotate(180deg)" : "none",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, borderBottom: expanded ? 1 : 0, borderColor: "divider" }}>
                        <Collapse in={expanded} timeout={0} unmountOnExit>
                          <Box
                            sx={{
                              pt: 1,
                              pb: 2,
                              px: 2,
                              borderTop: 1,
                              borderColor: "divider",
                              position: { xs: "sticky", sm: "static" },
                              left: 0,
                              width: { xs: "calc(100vw - 100px)", sm: "auto" },
                              boxSizing: "border-box",
                            }}
                          >
                      {baseRow.auto_recalculate_ranges !== false &&
                      baseRow.actual_total_employees == null &&
                      !mjRosterEmpty(baseRow) ? (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          Ranges will be set for all tasks after the first task
                          finishes collecting the roster.
                        </Alert>
                      ) : null}
                      {mjRangeNotice(baseRow) ? (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          {mjRangeNotice(baseRow)}
                        </Alert>
                      ) : null}
                      {mjJobRangeWarningsByJobId[id] ? (
                        <Alert
                          severity="warning"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobRangeWarningsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobRangeWarningsByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjJobActionAlertsByJobId[id] ? (
                        <Alert
                          severity="error"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobActionAlertsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobActionAlertsByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjJobNoticesByJobId[id] ? (
                        <Alert
                          severity="info"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobNoticesByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobNoticesByJobId[id]}
                        </Alert>
                      ) : null}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          justifyContent: { xs: "flex-start", sm: "space-between" },
                        }}
                      >
                        <Box sx={MJ_PROGRESS_CARD_SX}>
                          <EmployeeProgressBar
                            {...mjJobProgressProps(row)}
                            pendingLabel="ranges not set yet"
                            tooltip={mjJobCountersTooltip(employeeStats)}
                            leading={
                              <>
                                <SoftTag
                                  mono
                                  icon={<SmartToyIcon />}
                                  label={String(row.bot_name ?? "-")}
                                  title="Bot"
                                />
                                <SoftTag
                                  icon={<StraightenIcon />}
                                  label={mjRangeChipLabel(row)}
                                  title="Employee range of the job"
                                />
                                {mjBotOptionTags(row.additional_options, botOptionSpecs(String(row.bot_name ?? "")))}
                                {mjUploadFlagTags(row)}
                                {mjIsUploadJob(row) && mjSsnReadinessByJobId[id] ? (
                                  <SoftTag
                                    tone={mjSsnReadinessByJobId[id].ready ? "success" : "warning"}
                                    icon={
                                      mjSsnReadinessByJobId[id].ready ? <VerifiedUserIcon /> : <WarningAmberIcon />
                                    }
                                    label={mjSsnReadinessByJobId[id].ready ? "SSN ready" : "SSN: names only"}
                                    title={mjSsnReadinessText(mjSsnReadinessByJobId[id])}
                                  />
                                ) : null}
                                {String(row.error_pass_state ?? "") === "RUNNING" && (
                                  <SoftTag tone="warning" icon={<ReplayTagIcon />} label="Error pass running" />
                                )}
                              </>
                            }
                          />
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{
                            // Left-aligned on phones: this row sits inside the
                            // horizontally scrolled jobs table, so right-aligned
                            // or stretched controls would start off-screen.
                            ml: { sm: "auto" },
                            rowGap: 0.75,
                            // One height for every control in the row, the
                            // error-mode toggle included.
                            "& .MuiButton-root": { minHeight: { xs: 40, sm: 32 } },
                          }}
                        >
                          {(() => {
                            const errorModeOn = extractSkipErrorEmployees(
                              String(row.additional_options ?? ""),
                            ).processErrors;
                            const locked = mjJobAnythingInFlight(row);
                            return (
                              <ErrorModeToggle
                                checked={errorModeOn}
                                disabled={!canEditBots || mjProcessErrorsSavingJobId === id || locked}
                                onChange={(next) =>
                                  void toggleJobProcessErrors(
                                    id,
                                    String(row.additional_options ?? ""),
                                    next,
                                  )
                                }
                                tooltip={
                                  locked
                                    ? "The job has queued or running tasks - a running bot keeps the settings it started with, so this switch is locked until they stop."
                                    : errorModeOn
                                      ? "Error mode is on: every run of this job retries only the employees whose current status is ERROR. Employees no run has attempted yet are skipped - turn it off to run them."
                                      : "Off: a run processes the employees it has not attempted yet and skips the ones that errored. Turn on to retry only the employees whose current status is ERROR."
                                }
                              />
                            );
                          })()}
                          {row.bot_kind === "upload_bots" ||
                          String(row.bot_name ?? "").startsWith("upload_bots.") ? (
                            (() => {
                              // Rows nobody has run are what the coverage notice
                              // reports; with none there is nothing to run, and
                              // the tasks that did run keep their slices either
                              // way.
                              const uncovered = mjRangeNotice(row);
                              const { rows, suggested } = mjUncoveredRows(row);
                              const jobRunning = mjJobAnythingInFlight(row);
                              const busy = mjNewRowsRunningJobId === id;
                              // Merged rows have no log record yet, and an
                              // error-mode run walks only current ERRORs.
                              const errorModeOn = extractSkipErrorEmployees(
                                String(row.additional_options ?? ""),
                              ).processErrors;
                              return (
                                <Tooltip
                                  title={
                                    jobRunning
                                      ? "The job has queued or running tasks. Wait for them to finish, or stop them, before running the merged employees."
                                      : errorModeOn
                                        ? "Turn off Process error EEs first: in error mode the tasks for the merged employees would process nobody."
                                      : mjRosterEmpty(row)
                                        ? "The Matches file has no employees - nothing to run."
                                        : uncovered
                                        ? `${uncovered} They get tasks of their own; the tasks that already ran keep their employees and their logs and are not restarted.`
                                        : "Every employee in the Matches file already belongs to a task. Employees added through Merge reports show up here once the file has them."
                                  }
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={
                                        !canEditBots ||
                                        busy ||
                                        jobRunning ||
                                        errorModeOn ||
                                        !uncovered
                                      }
                                      startIcon={
                                        busy ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : undefined
                                      }
                                      onClick={() => {
                                        setMjNewRowsTaskCount(String(suggested));
                                        setMjNewRowsJob({ id, rows });
                                      }}
                                    >
                                      Run merged EEs
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            })()
                          ) : null}
                          {(() => {
                            const isUpload = mjIsUploadJob(row);
                            const label = isUpload ? "Build matches only" : "Build roster only";
                            const rosterBusy = mjRosterTaskBusyJobId === id;
                            const blocked = mjJobAnythingInFlight(row);
                            const rosterInfo = mjRosterInfoByJobId[id] ?? null;
                            const built = !rosterBusy && !blocked && mjRosterAlreadyBuilt(row, rosterInfo);
                            return (
                              <Tooltip
                                title={
                                  blocked
                                    ? "The job has a queued or running task - wait for it to finish, or stop it, first."
                                    : built
                                      ? mjRosterBuiltTooltip(row, rosterInfo)
                                      : `${label}: the bot logs in, ${
                                          isUpload
                                            ? "builds the Matches / Several matches / No matches files"
                                            : "collects the roster"
                                        }, sets the ranges and processes nobody.${mjRosterStoredEmptyNote(rosterInfo)}`
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!canEditBots || rosterBusy || blocked || built || mjJobActionBusy}
                                    startIcon={
                                      rosterBusy ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : undefined
                                    }
                                    onClick={() => setMjRosterConfirmJobId(id)}
                                  >
                                    {label}
                                  </Button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          {mjJobAggregateRunning(row) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={
                                mjRowBatchBusyJobId === id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <StopIcon fontSize="small" />
                                )
                              }
                              disabled={mjJobActionBusy || mjRowBatchBusyJobId === id || !canEditBots}
                              onClick={() => void mjStopJob(id)}
                            >
                              {mjRowBatchBusyJobId === id ? "Stopping…" : "Stop all"}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={
                                mjRowBatchBusyJobId === id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <PlayArrowIcon fontSize="small" />
                                )
                              }
                              disabled={mjJobActionBusy || mjRowBatchBusyJobId === id || !canEditBots}
                              onClick={() => void mjSsnGuardedRun(row, "Run all", () => mjRunJob(id))}
                            >
                              {mjRowBatchBusyJobId === id ? "Running…" : "Run all"}
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={nestSel.length === 0 || mjJobActionBusy || !canEditBots}
                            onClick={() =>
                              nestSel.length > 0 && setMjBatchDeleteNestDialog({ jobId: id, ids: [...nestSel] })
                            }
                          >
                            Delete selected ({nestSel.length})
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArticleOutlinedIcon fontSize="small" />}
                            onClick={() => openJobLogs(id, String(baseRow.name ?? ""))}
                          >
                            Logs
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => openMjManage(id)}>
                            Manage job
                          </Button>
                        </Stack>
                      </Box>
                      {mjAccordionLoadingJobId === id ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={22} />
                        </Box>
                      ) : (
                        <BotsTableScroll>
                          <Table
                            size="small"
                            stickyHeader
                            sx={{
                              tableLayout: "fixed",
                              minWidth: isPhone
                                ? MJ_TASKS_TABLE_MIN_WIDTH_XS
                                : MJ_TASKS_TABLE_MIN_WIDTH,
                            }}
                          >
                            <colgroup>
                              {(isPhone ? MJ_TASKS_COL_WIDTHS_XS : MJ_TASKS_COL_WIDTHS).map((w, ci) => (
                                <col key={ci} style={{ width: w }} />
                              ))}
                            </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                                <Checkbox
                                  size="small"
                                  indeterminate={nestSel.length > 0 && !allNestSelected}
                                  checked={allNestSelected}
                                  onChange={(e) => mjToggleAllNestedTasks(id, tasks, e.target.checked)}
                                  inputProps={{ "aria-label": "Select all tasks in this job" }}
                                />
                              </TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>№</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Status</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Bot</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Range</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Progress</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Connection</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(() => {
                              // The roster-only task sits first, outside the
                              // employee numbering: it processes nobody.
                              const rosterTask = tasks.find(
                                (t) => String(t.range_role ?? "").toUpperCase() === "ROSTER_ONLY",
                              );
                              const employeeTasks = tasks.filter((t) => t !== rosterTask);
                              const rowFor = (task: MjConnectionTask, index: number | string) =>
                                renderMjTaskRow({
                                  task,
                                  jobId: id,
                                  index,
                                  showCheckbox: true,
                                  showRange: true,
                                  rangePending: mjRangesPending(baseRow),
                                  showProgress: true,
                                  jobSnapshotAt: employeeStats.snapshotAt,
                                  showDelete: true,
                                  selected: nestSel.includes(task.id),
                                  onToggleSelect: (checked) =>
                                    mjToggleNestedTaskSelect(id, task.id, checked),
                                });
                              return (
                                <>
                                  {rosterTask ? rowFor(rosterTask, "R") : null}
                                  {employeeTasks.map((task, ti) => rowFor(task, ti + 1))}
                                </>
                              );
                            })()}
                            {!tasks.length && (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Typography color="text.secondary">No tasks loaded.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        </BotsTableScroll>
                      )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
                  {!childJobs.length && childJobsLoading && <MjJobsSkeletonRows />}
                  {!childJobs.length && !childJobsLoading && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: "center", py: 3 }}>
                        <Typography color="text.secondary">No child jobs yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </BotsTableScroll>
          </TabPanel>
        </Paper>
      )}

      {!activeMajor && !isDetailMode && (
        <Typography color="text.secondary">Select an automation row to manage connections and jobs.</Typography>
      )}

      <AppDialog
        open={majorEditOpen}
        onClose={() => setMajorEditOpen(false)}
        maxWidth="sm"
        title={<>{majorEditTarget ? "Edit Major Job" : "New Major Job"}</>}
        actions={
          <>
            <Button onClick={() => setMajorEditOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => void saveMajorForm()} disabled={isSavingMajor || isCreating || !canEditBots}>
              {isSavingMajor || isCreating ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        {/* Critical fields (vendor + bot kind) are locked once any task of this major job
            has downloaded any employees - changing them would cause data drift. */}
        {majorEditTarget && majorHasRunningOrProgress ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This major job has running tasks or downloaded employees. Vendor and bot scope cannot be
            changed; stop all tasks and clear progress to unlock them.
          </Alert>
        ) : null}
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Display name"
          required
          value={majName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMajName(e.target.value)}
        />
        <Autocomplete
          ListboxComponent={AutocompleteScrollListbox}
          componentsProps={autocompleteScrollPaperProps}
          freeSolo
          sx={{ mt: 1 }}
          options={majorFormVendorOptions}
          inputValue={majVendor}
          disabled={Boolean(majorEditTarget) && majorHasRunningOrProgress}
          onInputChange={(_e, v) => setMajVendor(v)}
          ListboxProps={MAJOR_FORM_LISTBOX}
          filterOptions={filterMajorFormVendors}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              required
              helperText="Task jobs use connections with this vendor."
            />
          )}
        />
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          select
          label="Bot scope"
          value={majKind}
          disabled={Boolean(majorEditTarget) && majorHasRunningOrProgress}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMajKind(e.target.value)}
        >
          <MenuItem value="download_bots">Download</MenuItem>
          <MenuItem value="upload_bots">Upload</MenuItem>
          <MenuItem value="delete_bots">Delete</MenuItem>
        </TextField>
        {majorFormError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {majorFormError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={majConnCreateOpen}
        onClose={() => setMajConnCreateOpen(false)}
        maxWidth="sm"
        title={<>New connection ({activeMajor?.vendor ?? "…"})</>}
        actions={
          <>
            <Button onClick={() => setMajConnCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => void submitMajConnCreate()} disabled={isMpcCreating || !canEditBots}>
              Create
            </Button>
          </>
        }
      >
        <TextField fullWidth label="Vendor" value={activeMajor?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
        <TextField
          required
          fullWidth
          label="Username"
          value={mcUsername}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcUsername(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Host"
          value={mcHost}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcHost(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Client code"
          value={mcClientCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcClientCode(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Password"
          type={mcShowPw ? "text" : "password"}
          value={mcPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setMcShowPw((v) => !v)} aria-label="toggle password">
                  {mcShowPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {mcError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mcError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={majConnEdit != null}
        onClose={() => setMajConnEdit(null)}
        maxWidth="sm"
        title="Edit connection"
        actions={
          <>
            <Button onClick={() => setMajConnEdit(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => void submitMajConnEdit()} disabled={isMpcUpdating || !canEditBots}>
              Save
            </Button>
          </>
        }
      >
        <TextField fullWidth label="Vendor" value={majConnEdit?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
        <TextField
          required
          fullWidth
          label="Username"
          value={meUsername}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeUsername(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Host"
          value={meHost}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeHost(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Client code"
          value={meClientCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeClientCode(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Password"
          type={meShowPw ? "text" : "password"}
          value={mePassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMePassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setMeShowPw((v) => !v)} aria-label="toggle password">
                  {meShowPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {meError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {meError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={deleteConnConfirm != null}
        onClose={() => setDeleteConnConfirm(null)}
        maxWidth="xs"
        title="Delete connection"
        actions={
          <>
            <Button onClick={() => setDeleteConnConfirm(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={isMpcDeleting || !canEditBots}
              onClick={() => deleteConnConfirm != null && void deleteConnectionHard(deleteConnConfirm)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Remove this connection from the user? This cannot be undone.
        </Typography>
      </AppDialog>

      <AppDialog
        open={poolConnBulkDeleteOpen}
        onClose={() => !poolConnBulkDeleting && setPoolConnBulkDeleteOpen(false)}
        maxWidth="xs"
        title="Delete selected connections?"
        actions={
          <>
            <Button
              onClick={() => setPoolConnBulkDeleteOpen(false)}
              disabled={poolConnBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
              onClick={async () => {
                if (!poolConnSelectedIds.length || !canEditBots) return;
                setPoolConnBulkDeleting(true);
                try {
                  for (const cid of poolConnSelectedIds) {
                    await deleteConnectionMutation(cid).unwrap();
                  }
                  setPoolConnSelectedIds([]);
                  setPoolConnBulkDeleteOpen(false);
                  await onRefreshConnections();
                  await refetch();
                } catch {
                  /* keep dialog open so the user can retry; backend message
                     surfaces via the connection list refresh */
                } finally {
                  setPoolConnBulkDeleting(false);
                }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Delete {poolConnSelectedIds.length} selected connection
          {poolConnSelectedIds.length === 1 ? "" : "s"}? This cannot be undone.
        </Typography>
      </AppDialog>

      <AppDialog
        open={childDialogOpen}
        onClose={() => setChildDialogOpen(false)}
        maxWidth="md"
        title="New task job"
        actions={
          <>
            <Button onClick={() => setChildDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => void createChildSubmit()} disabled={!canEditBots}>
              Create
            </Button>
          </>
        }
      >
        <TextField
          sx={{ mt: 1, mb: 1 }}
          label="Task Job Name"
          required
          fullWidth
          value={taskJobListName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTaskJobListName(e.target.value)}
        />
        <Autocomplete
          ListboxComponent={AutocompleteScrollListbox}
          componentsProps={autocompleteScrollPaperProps}
          sx={{ mb: 1 }}
          options={normalizedChildBots}
          value={
            normalizedChildBots.find((b) => botNameFromRegistry(b) === childBotName) ?? null
          }
          onChange={(_e, v) => setChildBotName(v ? botNameFromRegistry(v) : "")}
          filterOptions={filterChildDialogBots}
          getOptionLabel={(o) => o.bot_slug}
          renderOption={(props, option) => (
            <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
              {option.bot_slug}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Bot" required error={!childBotName && Boolean(childError)} />
          )}
        />
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={childProcessErrors}
              onChange={(e) => setChildProcessErrors(e.target.checked)}
            />
          }
          label="Auto process error employees (one retry pass after the job finishes)"
        />
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={childSeparateProfiles}
              onChange={(e) => setChildSeparateProfiles(e.target.checked)}
            />
          }
          label="Separate browser profile per task (off: every task reuses the prepared profile 1)"
        />
        {activeMajor?.bot_kind === "upload_bots" && (
          <TextField
            label="Document type *"
            size="small"
            fullWidth
            value={childDocumentType}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setChildDocumentType(e.target.value)}
            sx={{ mb: 1 }}
            placeholder="e.g. w2, paystubs, 1095"
          />
        )}
        <BotOptionFields
          options={botOptionSpecs(childBotName)}
          value={childAdditionalOptions}
          onChange={setChildAdditionalOptions}
          omitKeys={DOCS_TYPE_OMIT}
        />
        {activeMajor?.bot_kind === "delete_bots" && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Document types to delete *
            </Typography>
            {childDocumentTypes.map((dt, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Paylocity Historical W2s"
                  value={dt}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setChildDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                  }
                />
                <IconButton size="small" color="error" disabled={childDocumentTypes.length === 1}
                  onClick={() => setChildDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<Add fontSize="small" />}
              onClick={() => setChildDocumentTypes((prev) => [...prev, ""])}>
              Add type
            </Button>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Upload options
            </Typography>
            <Stack direction="column" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={childUploadAsZip}
                    onChange={(e) => setChildUploadAsZip(e.target.checked)}
                    size="small"
                  />
                }
                label="Upload as ZIP"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={childUnzipBefore}
                    onChange={(e) => setChildUnzipBefore(e.target.checked)}
                    size="small"
                  />
                }
                label="Unzip before uploading"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={childUseSsnMatch}
                    onChange={(e) => setChildUseSsnMatch(e.target.checked)}
                    size="small"
                  />
                }
                label="Use SSN match"
              />
            </Stack>
            {childSsnReadiness && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5 }}
                color={childSsnReadiness.ready ? "success.main" : "warning.main"}
              >
                {mjSsnReadinessText(childSsnReadiness)}
                {!childSsnReadiness.ready && childUseSsnMatch
                  ? " Create the SSN job on the major job's SSN Job tab (and one on the source vendor's major job) before the first run."
                  : ""}
              </Typography>
            )}
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Source folder (files to upload)
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                placeholder="Select a folder from Data Storage…"
                value={childSourcePath}
                InputProps={{ readOnly: true }}
                error={!childSourcePath && Boolean(childError)}
                sx={{ "& .MuiInputBase-input": { color: childSourcePath ? "text.primary" : "text.disabled" } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                onClick={() => openFolderPicker("create", childSourcePath)}
                sx={{ whiteSpace: "nowrap", minWidth: 90 }}
              >
                Browse
              </Button>
            </Stack>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Connections (tasks per connection)
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<Add fontSize="small" />}
            onClick={openMajConnCreate}
            disabled={!activeMajor || !canEditBots}
            sx={{ ml: "auto" }}
          >
            New connection
          </Button>
          <Tooltip title="Add connection row">
            <span>
              <IconButton
                size="small"
                color="primary"
                aria-label="add connection row"
                onClick={() => {
                  const n = nextUnusedChildPlanConnId;
                  if (n != null) setChildPlans([...childPlans, { connection_id: n, task_target_count: "1" }]);
                }}
                disabled={nextUnusedChildPlanConnId == null}
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {childPlans.map((row, i) => (
          <Box key={`${String(i)}-${String(row.connection_id)}`} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
            <TextField
              select
              label="Connection"
              value={String(row.connection_id)}
              sx={{ flex: 1 }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = childPlans.slice();
                next[i] = { ...row, connection_id: Number(e.target.value) };
                setChildPlans(next);
              }}
            >
              {connectionIdsForPlanRow(majorConnPickerIds, childPlans, i).map((cid) => {
                const ordinal = (majorConnPickerIds.indexOf(cid) ?? -1) + 1;
                return (
                  <MenuItem key={cid} value={String(cid)}>
                    {`${ordinal}. ${connectionPoolRowLabel(connections, cid) || "-"}`}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="Tasks"
              inputProps={{ inputMode: "numeric" }}
              value={row.task_target_count}
              sx={{ width: 112 }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = childPlans.slice();
                next[i] = {
                  ...row,
                  task_target_count: connPlanTasksDigitsOnly(e.target.value),
                };
                setChildPlans(next);
              }}
            />
            <Tooltip title="Remove row">
              <span>
                <IconButton
                  size="small"
                  aria-label="remove connection row"
                  onClick={() => {
                    if (childPlans.length <= 1) return;
                    setChildPlans(childPlans.filter((_r, ri) => ri !== i));
                  }}
                  disabled={childPlans.length <= 1}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        {childError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {childError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={mjManageJobId != null}
        onClose={closeMjManage}
        maxWidth="sm"
        title={
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap> {mjEffectiveManagedJob ? mjJobDisplayName(mjEffectiveManagedJob) : "Manage job"} </Typography> {mjEffectiveManagedJob ? ( <Typography variant="caption" color="text.secondary"> Task job{mjEffectiveManagedJobOrdinal != null ? ` #${mjEffectiveManagedJobOrdinal}` : ""} {" · "} {String(mjEffectiveManagedJob.bot_name ?? "-")} </Typography> ) : null} </Box> {mjEffectiveManagedJob ? ( <StatusChipMj label={String(mjEffectiveManagedJob.task_status ?? "")} color={taskRunStatusChipColor(String(mjEffectiveManagedJob.task_status ?? ""))} /> ) : null} </Stack>
          </>
        }
        actions={
          <>
            <Button onClick={closeMjManage}>Close</Button>
          </>
        }
      >
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={mjManageTab}
              onChange={(_e: React.SyntheticEvent, v: number) => setMjManageTab(v)}
            >
              <Tab label="Overview" />
              <Tab label="Matches" />
            </Tabs>
          </Box>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjManageJobError && (
          <Alert severity="error" sx={{ mb: 1.5, mt: 1 }}>
            {mjManageJobError}
          </Alert>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjManageJobNotice && (
          <Alert severity="info" sx={{ mt: 1 }} onClose={() => setMjManageJobNotice(null)}>
            {mjManageJobNotice}
          </Alert>
        )}
        {mjRosterNotice && (
          <Alert severity="success" sx={{ mt: 1 }} onClose={() => setMjRosterNotice(null)}>
            {mjRosterNotice}
          </Alert>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjEffectiveManagedJob && (
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Box sx={MJ_PROGRESS_CARD_SX}>
              <EmployeeProgressBar
                {...mjJobProgressProps(mjEffectiveManagedJob)}
                pendingLabel="ranges not set yet"
                tooltip={mjJobCountersTooltip(mjEmployeeStatsFromJob(mjEffectiveManagedJob))}
                leading={
                  <>
                    <SoftTag
                      icon={<StraightenIcon />}
                      label={mjRangeChipLabel(mjEffectiveManagedJob)}
                      title="Employee range of the job"
                    />
                    {mjBotOptionTags(
                      mjEffectiveManagedJob.additional_options,
                      botOptionSpecs(String(mjEffectiveManagedJob.bot_name ?? "")),
                    )}
                    {mjUploadFlagTags(mjEffectiveManagedJob)}
                    {mjManageJobId != null &&
                    mjIsUploadJob(mjEffectiveManagedJob) &&
                    mjSsnReadinessByJobId[mjManageJobId] ? (
                      <SoftTag
                        tone={mjSsnReadinessByJobId[mjManageJobId].ready ? "success" : "warning"}
                        icon={
                          mjSsnReadinessByJobId[mjManageJobId].ready ? (
                            <VerifiedUserIcon />
                          ) : (
                            <WarningAmberIcon />
                          )
                        }
                        label={mjSsnReadinessByJobId[mjManageJobId].ready ? "SSN ready" : "SSN: names only"}
                        title={mjSsnReadinessText(mjSsnReadinessByJobId[mjManageJobId])}
                      />
                    ) : null}
                    {(() => {
                      const employeeStats = mjEmployeeStatsFromJob(mjEffectiveManagedJob);
                      const stats = mjJobStatsTotals(mjEffectiveManagedJob);
                      return (
                        <>
                          <SoftTag icon={<GroupsIcon />} label={`Total ${employeeStats.total}`} title="Employees in the roster" />
                          <SoftTag
                            tone={Number(employeeStats.success) > 0 ? "success" : "neutral"}
                            icon={<CheckCircleIcon />}
                            label={`Success ${employeeStats.success}`}
                            title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}
                          />
                          <SoftTag
                            tone={Number(employeeStats.error) > 0 ? "error" : "neutral"}
                            icon={<ErrorCircleIcon />}
                            label={`Errors ${employeeStats.error}`}
                            title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}
                          />
                          <SoftTag
                            icon={<LayersIcon />}
                            label={`Tasks ${stats.running}/${stats.total}`}
                            title="Running (incl. queued) / total tasks"
                          />
                          {String(mjEffectiveManagedJob.error_pass_state ?? "") === "RUNNING" && (
                            <SoftTag tone="warning" icon={<ReplayTagIcon />} label="Error pass running" />
                          )}
                        </>
                      );
                    })()}
                  </>
                }
              />
            </Box>
            {mjEffectiveManagedJob.auto_recalculate_ranges !== false &&
            mjEffectiveManagedJob.actual_total_employees == null &&
            !mjRosterEmpty(mjEffectiveManagedJob) ? (
              <Alert severity="info">
                Ranges will be set for all tasks after the first task finishes
                collecting the roster.
              </Alert>
            ) : null}
            {mjRosterEmptyWarning(mjEffectiveManagedJob) ? (
              <Alert severity="warning">{mjRosterEmptyWarning(mjEffectiveManagedJob)}</Alert>
            ) : null}
            {(() => {
              const lines = summarizeBotOptions(
                botOptionSpecs(String(mjEffectiveManagedJob.bot_name ?? "")),
                String(mjEffectiveManagedJob.additional_options ?? ""),
                MJ_OPTION_SUMMARY_OMIT,
              );
              return lines.length ? (
                <Box sx={MJ_PROGRESS_CARD_SX}>
                  <Tooltip title="What Edit job holds for this bot; unset options run at their default.">
                    <Typography
                      variant="overline"
                      sx={{
                        display: "inline-block",
                        lineHeight: 1.2,
                        letterSpacing: 0.06,
                        fontWeight: 700,
                        fontSize: 10,
                        color: "text.secondary",
                        mb: 0.75,
                        cursor: "help",
                      }}
                    >
                      Bot options
                    </Typography>
                  </Tooltip>
                  <BotOptionSummary lines={lines} dense />
                </Box>
              ) : null;
            })()}
            <Divider sx={{ my: 0.5 }} />
            {mjJobAggregateRunning(mjEffectiveManagedJob) ? (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={
                  isMjBatchStopping ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <StopIcon fontSize="small" />
                  )
                }
                onClick={() => void mjStopJob()}
                disabled={mjJobActionBusy || isMjBatchStopping || !canEditBots}
              >
                {isMjBatchStopping ? "Stopping…" : "Stop all"}
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={
                  isMjBatchRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowIcon fontSize="small" />
                  )
                }
                onClick={() =>
                  void mjSsnGuardedRun(mjEffectiveManagedJob, "Run all", () => mjRunJob())
                }
                disabled={mjJobActionBusy || isMjBatchRunning || !canEditBots}
              >
                {isMjBatchRunning ? "Running…" : "Run all"}
              </Button>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Job
                </Typography>
                <Stack spacing={0.75}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={
                      mjAddTasksLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Add fontSize="small" />
                      )
                    }
                    onClick={() => void openMjAddTasksDialog()}
                    disabled={mjJobActionBusy || mjAddTasksLoading || !canEditBots}
                  >
                    {mjAddTasksLoading ? "Loading…" : "Add tasks"}
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={openMjEditJobDialog}
                    disabled={!canEditBots}
                  >
                    Edit job
                  </Button>
                  <Tooltip
                    title={
                      mjJobAnythingInFlight(mjEffectiveManagedJob)
                        ? "The job has a queued or running task - wait for it to finish, or stop it, first."
                        : mjRosterAlreadyBuilt(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                          ? mjRosterBuiltTooltip(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                          : `Logs in, builds the roster (or the matches files), sets the ranges and processes nobody - so No matches can be resolved before the real run.${mjRosterStoredEmptyNote(mjRosterInfo?.info ?? null)}`
                    }
                  >
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        disabled={
                          !canEditBots ||
                          mjJobActionBusy ||
                          mjRosterTaskBusyJobId === mjManageJobId ||
                          mjJobAnythingInFlight(mjEffectiveManagedJob) ||
                          mjRosterAlreadyBuilt(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                        }
                        onClick={() => mjManageJobId != null && setMjRosterConfirmJobId(mjManageJobId)}
                      >
                        {mjIsUploadJob(mjEffectiveManagedJob) ? "Build matches only" : "Build roster only"}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Logs
                </Typography>
                <Stack spacing={0.75}>
                  <Tooltip title="Download all stored logs of this job as one .zip">
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={
                          isMjLogsArchiveLoading ? (
                            <CircularProgress size={14} />
                          ) : (
                            <FileDownloadIcon fontSize="small" />
                          )
                        }
                        disabled={isMjLogsArchiveLoading || mjEffectiveManagedJob == null}
                        onClick={() =>
                          mjEffectiveManagedJob &&
                          void handleMjDownloadJobLogs(
                            Number(mjEffectiveManagedJob.id),
                            mjEffectiveManagedJob,
                          )
                        }
                      >
                        Download logs
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete all stored logs so the next run re-processes every employee">
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<DeleteSweepIcon fontSize="small" />}
                        disabled={
                          isMjClearLogsLoading ||
                          mjEffectiveManagedJob == null ||
                          !canEditBots
                        }
                        onClick={() =>
                          mjEffectiveManagedJob &&
                          void openMjClearLogs(Number(mjEffectiveManagedJob.id))
                        }
                      >
                        Clear logs
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Files
                </Typography>
                <Stack spacing={0.75}>
                  {activeMajor?.bot_kind !== "delete_bots" && (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<FolderOpenIcon fontSize="small" />}
                      onClick={() =>
                        mjEffectiveManagedJob && handleMjViewJobFiles(mjEffectiveManagedJob)
                      }
                    >
                      Open job files
                    </Button>
                  )}
                  {(() => {
                    const info =
                      mjRosterInfo && mjRosterInfo.jobId === mjManageJobId ? mjRosterInfo.info : null;
                    const isUpload = mjIsUploadJob(mjEffectiveManagedJob);
                    const inFlight = mjJobAnythingInFlight(mjEffectiveManagedJob);
                    const exists = mjRosterInfoExists(info);
                    return (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                          {mjRosterInfoLoading
                            ? "Checking the stored file..."
                            : info
                              ? mjRosterInfoLine(info)
                              : ""}
                        </Typography>
                        <Tooltip
                          title={
                            inFlight
                              ? "Stop the job's tasks first."
                              : isUpload
                                ? "Deletes the Matches, Several matches and No matches files (and the summary), so the next run matches again."
                                : "Deletes employees_info.json, so the next run collects the roster again."
                          }
                        >
                          <span style={{ display: "block" }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={!canEditBots || !exists || inFlight || mjRosterDeleting}
                              onClick={() =>
                                mjManageJobId != null && setMjRosterDeletePrompt({ jobId: mjManageJobId })
                              }
                            >
                              {isUpload ? "Delete all matches files" : "Delete roster"}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            inFlight
                              ? "Stop the job's tasks first."
                              : isUpload
                                ? "Replace the Matches file with a reviewed one (CSV, XLS or XLSX: s3_name, site_name, employee_id)."
                                : "Replace employees_info.json with your own JSON list of employees."
                          }
                        >
                          <span style={{ display: "block" }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              startIcon={<UploadFileIcon fontSize="small" />}
                              disabled={!canEditBots || inFlight}
                              onClick={() =>
                                mjManageJobId != null &&
                                openMjArtifactUpload(mjManageJobId, isUpload ? "matches" : "roster")
                              }
                            >
                              {isUpload ? "Upload matches file" : "Upload roster file"}
                            </Button>
                          </span>
                        </Tooltip>
                      </>
                    );
                  })()}
                </Stack>
              </Box>
              <Box sx={{ ...MJ_MANAGE_GROUP_SX, borderColor: "error.light" }}>
                <Typography
                  variant="caption"
                  sx={{ ...MJ_MANAGE_GROUP_LABEL_SX, color: "error.main" }}
                >
                  Danger zone
                </Typography>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  disabled={!canEditBots}
                  onClick={() => mjManageJobId != null && setMjDeleteJobId(mjManageJobId)}
                >
                  Delete job
                </Button>
              </Box>
            </Box>
          </Stack>
        )}
        {/* Matches tab — upload bots only */}
        {activeMajor?.bot_kind === "upload_bots" && mjManageTab === 1 && (
          <Box sx={{ pt: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{
                mb: 1.5,
                py: 1,
                px: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                Append reviewed rows to the Matches file, or replace it whole
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<MergeTypeIcon fontSize="small" />}
                  disabled={matchesLoading || matchesDeleting || !canEditBots}
                  onClick={openMatchesMergeDialog}
                >
                  Merge reports
                </Button>
                <Tooltip
                  title={
                    mjJobAnythingInFlight(mjEffectiveManagedJob ?? {})
                      ? "Stop the job's tasks first."
                      : "Replace the Matches file with a reviewed one (CSV, XLS or XLSX: s3_name, site_name, employee_id)."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon fontSize="small" />}
                      disabled={
                        matchesLoading ||
                        matchesDeleting ||
                        !canEditBots ||
                        mjJobAnythingInFlight(mjEffectiveManagedJob ?? {})
                      }
                      onClick={() =>
                        mjManageJobId != null && openMjArtifactUpload(mjManageJobId, "matches")
                      }
                    >
                      Upload matches file
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            {matchesSummary?.manual_upload && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Matches file uploaded by hand on{" "}
                {new Date(matchesSummary.manual_upload.uploaded_at).toLocaleString()}
                {matchesSummary.manual_upload.uploaded_by
                  ? ` by ${matchesSummary.manual_upload.uploaded_by}`
                  : ""}{" "}
                ({matchesSummary.manual_upload.rows} row(s)). The Several matches and No matches
                files below come from the bot&apos;s earlier run and are outdated.
              </Alert>
            )}
            {matchesMergeSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 1 }}
                onClose={() => setMatchesMergeSuccess(null)}
              >
                {matchesMergeSuccess}
              </Alert>
            )}
            {matchesLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, px: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            )}
            {matchesError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                action={
                  <Button
                    size="small"
                    onClick={() => mjManageJobId != null && void loadMatchesFiles(mjManageJobId)}
                  >
                    Retry
                  </Button>
                }
              >
                {matchesError}
              </Alert>
            )}
            {!matchesLoading && matchesFiles && (() => {
              const existingFiles = matchesFiles.filter((f) => f.exists);
              const allSelected =
                existingFiles.length > 0 &&
                matchesSelectedFiles.length === existingFiles.length;
              const someSelected =
                matchesSelectedFiles.length > 0 && !allSelected;
              return (
                <>
                  {matchesSelectedFiles.length > 0 && (
                    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {matchesSelectedFiles.length} selected
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={matchesDeleting || !canEditBots}
                        startIcon={
                          matchesDeleting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          mjManageJobId != null &&
                          void confirmBatchDeleteMatchesFiles(mjManageJobId)
                        }
                      >
                        {matchesDeleting ? "Deleting…" : "Delete selected"}
                      </Button>
                      <Button
                        size="small"
                        disabled={matchesDeleting}
                        onClick={() => setMatchesSelectedFiles([])}
                      >
                        Clear
                      </Button>
                    </Stack>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0, width: 44, pr: 0 }}>
                          <Checkbox
                            size="small"
                            indeterminate={someSelected}
                            checked={allSelected}
                            disabled={existingFiles.length === 0 || matchesDeleting}
                            onChange={(e) =>
                              setMatchesSelectedFiles(
                                e.target.checked
                                  ? existingFiles.map((f) => f.filename)
                                  : [],
                              )
                            }
                            sx={{ p: 0.5 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, pl: 0 }}>File</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Rows</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Size</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchesFiles.map((f) => {
                        const label =
                          f.filename === "s3_to_site_names_matches.csv"
                            ? "Matches"
                            : f.filename === "s3_to_site_names_several_matches.csv"
                              ? "Several matches"
                              : "No matches";
                        const sizeLabel =
                          f.size_bytes != null
                            ? f.size_bytes >= 1024
                              ? `${(f.size_bytes / 1024).toFixed(1)} KB`
                              : `${f.size_bytes} B`
                            : "—";
                        const isSelected = matchesSelectedFiles.includes(f.filename);
                        return (
                          <TableRow
                            key={f.filename}
                            selected={isSelected}
                            sx={{ opacity: matchesDeleting && isSelected ? 0.5 : 1 }}
                          >
                            <TableCell sx={{ pl: 0, pr: 0 }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={!f.exists || matchesDeleting}
                                onChange={(e) =>
                                  setMatchesSelectedFiles((prev) =>
                                    e.target.checked
                                      ? [...prev, f.filename]
                                      : prev.filter((n) => n !== f.filename),
                                  )
                                }
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                            <TableCell sx={{ pl: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={f.exists ? 600 : 400}
                                color={f.exists ? "text.primary" : "text.disabled"}
                              >
                                {label}
                              </Typography>
                              {!f.exists && (
                                <Typography variant="caption" color="text.disabled">
                                  not found
                                </Typography>
                              )}
                              {f.exists &&
                                matchesSummary?.manual_upload &&
                                f.filename !== "s3_to_site_names_matches.csv" && (
                                  <Typography variant="caption" color="warning.main" display="block">
                                    outdated - written before the manual upload
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                color={f.exists && f.rows === 0 ? "warning.main" : "text.secondary"}
                              >
                                {f.exists && f.rows != null ? f.rows : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              <Typography variant="caption" color="text.secondary">
                                {f.exists ? sizeLabel : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", pr: 0 }}>
                              {f.exists ? (
                                <Stack direction="row" gap={0.5} justifyContent="center">
                                  <Tooltip title="View">
                                    <IconButton
                                      size="small"
                                      disabled={matchesDeleting}
                                      onClick={() =>
                                        mjManageJobId != null &&
                                        void openMatchesFileViewer(mjManageJobId, f.filename)
                                      }
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      disabled={matchesDeleting}
                                      onClick={() =>
                                        mjManageJobId != null &&
                                        void downloadMatchesFile(mjManageJobId, f.filename)
                                      }
                                    >
                                      <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={matchesDeleting || !canEditBots}
                                      onClick={() => setMatchesDeleteFilename(f.filename)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {matchesSummary ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Matching summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mjMatchesSummaryHeadline(matchesSummary)}
                      </Typography>
                      {mjMatchesSummaryLines(matchesSummary).map((line, index) => (
                        <Typography
                          key={`${index}-${line}`}
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </>
              );
            })()}
            {!matchesLoading && !matchesError && matchesFiles === null && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No data loaded.
              </Typography>
            )}
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={mjAddTasksOpen}
        onClose={() => setMjAddTasksOpen(false)}
        maxWidth="md"
        title="Add tasks"
        actions={
          <>
            <Button onClick={() => setMjAddTasksOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void handleMjAddTasksConfirmed()}
              disabled={
                isAddingTasksToJob ||
                mjAddTasksDialogStats == null ||
                Boolean(mjAddTasksDialogStats.addBlockingReason) ||
                !canEditBots
              }
            >
              Add
            </Button>
          </>
        }
      >
        {mjAddTasksJobId != null && mjAddTasksDialogStats && (
          <Stack sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {mjAddTasksDialogStats.totalHelp}
            </Typography>
            {mjAddTasksDialogStats.planMode ? (
              <>
                {mjBulkAddRows.map((row, ri) => {
                  const pickPool =
                    mjAddPlanConnPool.length > 0
                      ? mjAddPlanConnPool
                      : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                  return (
                    <Box
                      key={`mj-add-${String(ri)}`}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: ri === 0 ? 1 : 0.75,
                        mb: 0.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        select
                        label="Connection"
                        sx={{ flex: 1 }}
                        value={
                          typeof row.connection_id === "number" && row.connection_id >= 1
                            ? String(row.connection_id)
                            : ""
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const next = mjBulkAddRows.slice();
                          next[ri] = { ...row, connection_id: Number(e.target.value) };
                          setMjBulkAddRows(next);
                        }}
                      >
                        {connectionIdsForPlanRow(pickPool, mjBulkAddRows, ri).map((cid) => (
                          <MenuItem key={cid} value={String(cid)}>
                            {mjConnLabelVerbose(connections, cid)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        sx={{ width: 124 }}
                        label="Tasks"
                        inputProps={{ inputMode: "numeric" }}
                        value={row.task_target_count}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const next = mjBulkAddRows.slice();
                          next[ri] = {
                            ...row,
                            task_target_count: connPlanTasksDigitsOnly(e.target.value),
                          };
                          setMjBulkAddRows(next);
                        }}
                      />
                      <Tooltip title="Remove row">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="remove row"
                            onClick={() => {
                              if (mjBulkAddRows.length <= 1) return;
                              setMjBulkAddRows(mjBulkAddRows.filter((_r, ji) => ji !== ri));
                            }}
                            disabled={mjBulkAddRows.length <= 1}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
                  <Tooltip title="Add row for another login">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="add row"
                        onClick={() => {
                          const pool =
                            mjAddPlanConnPool.length > 0
                              ? mjAddPlanConnPool
                              : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                          const n = nextUnusedPoolConnectionId(pool, mjBulkAddRows);
                          if (n != null) {
                            setMjBulkAddRows([...mjBulkAddRows, { connection_id: n, task_target_count: "1" }]);
                          }
                        }}
                        disabled={
                          nextUnusedPoolConnectionId(
                            mjAddPlanConnPool.length > 0
                              ? mjAddPlanConnPool
                              : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id),
                            mjBulkAddRows,
                          ) == null
                        }
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            ) : null}
            {!mjAddTasksDialogStats.planMode && mjBulkAddRows[0] != null && (
              <TextField
                sx={{ mt: 1 }}
                fullWidth
                label="Tasks to add"
                inputProps={{ inputMode: "numeric" }}
                value={mjBulkAddRows[0].task_target_count}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMjBulkAddRows([
                    {
                      ...mjBulkAddRows[0],
                      task_target_count: connPlanTasksDigitsOnly(e.target.value),
                    },
                  ])
                }
              />
            )}
          </Stack>
        )}
        {mjAddTasksDialogStats?.countHint?.trim() ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {mjAddTasksDialogStats.countHint}
          </Typography>
        ) : null}
        {!mjAddTasksErr && mjAddTasksDialogStats?.addBlockingReason?.trim() ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {mjAddTasksDialogStats.addBlockingReason}
          </Alert>
        ) : null}
        {mjAddTasksDialogStats?.planLevelHint?.trim() ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {mjAddTasksDialogStats.planLevelHint} The job&apos;s per-connection plan will be raised
            automatically when these tasks are added.
          </Alert>
        ) : null}
        {mjAddTasksErr ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mjAddTasksErr}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={ssnJobDialogOpen}
        onClose={() => setSsnJobDialogOpen(false)}
        maxWidth="sm"
        title={ssnJobDialogMode === "create" ? "Create SSN Job" : "Edit SSN Job"}
        actions={
          <>
            <Button onClick={() => setSsnJobDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !canEditBots ||
                ssnJobCreating ||
                ssnOptionsSaving ||
                (ssnJobDialogMode === "create"
                  ? !ssnJobSelectedConnId
                  : !ssnOptionsDirty)
              }
              onClick={() => void submitSsnJobDialog()}
            >
              {ssnJobCreating || ssnOptionsSaving
                ? "Saving..."
                : ssnJobDialogMode === "create"
                  ? "Create"
                  : "Save"}
            </Button>
          </>
        }
      >
        {ssnJobDialogMode === "edit" && ssnTaskLooksBusy ? (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            This SSN job has a running task. It keeps the options it started
            with; stop it before editing.
          </Alert>
        ) : null}
        {ssnJobDialogMode === "create" ? (
          <FormControl size="small" fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Connection</InputLabel>
            <Select
              value={ssnJobSelectedConnId ?? ""}
              label="Connection"
              onChange={(e) => setSsnJobSelectedConnId(Number(e.target.value) || null)}
            >
              {connections
                .filter((c) => c.vendor?.toLowerCase() === activeMajor?.vendor?.toLowerCase())
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {(c.username ?? "").trim() ? `@${c.username}` : c.host || String(c.id)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : null}
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Bot options
        </Typography>
        {ssnOptionSpecs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This vendor&apos;s SSN bot declares no options.
          </Typography>
        ) : (
          <BotOptionFields
            options={ssnOptionSpecs}
            value={ssnAdditionalOptions}
            onChange={setSsnAdditionalOptions}
            omitKeys={SSN_DERIVED_OMIT}
            disabled={!canEditBots}
          />
        )}
        {ssnJobDialogError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {ssnJobDialogError}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={mjEditJobOpen}
        onClose={() => setMjEditJobOpen(false)}
        maxWidth="md"
        title={<>Edit Task Job{mjEditJobOrdinal != null ? ` #${mjEditJobOrdinal}` : ""}</>}
        actions={
          <>
            <Button onClick={() => setMjEditJobOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={isMjEditJobSaving || mjEditJobHasRunningTasks || !canEditBots}
              onClick={() => void saveMjEditJob()}
            >
              {isMjEditJobSaving ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        {mjEditJobHasRunningTasks ? (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            This task job has running tasks. Stop them before editing.
          </Alert>
        ) : null}
        {mjEditJobBotLocked ? (
          <Alert severity="info" sx={{ mt: mjEditJobHasRunningTasks ? 0 : 1, mb: 1 }}>
            This task job already has progress ({mjEditJobProcessedEmployees} employee
            {mjEditJobProcessedEmployees === 1 ? "" : "s"} processed). The bot cannot be changed.
          </Alert>
        ) : null}
        <TextField
          sx={{ mt: 1, mb: 1 }}
          fullWidth
          label="Task Job Name"
          required
          value={mjEditJobName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditJobName(e.target.value)}
          disabled={mjEditJobHasRunningTasks}
        />
        <TextField
          sx={{ mb: 1 }}
          fullWidth
          label="Employees to"
          value={mjEditEmployeeTo}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          disabled={mjEditJobHasRunningTasks}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setMjEditEmployeeTo(sanitizeEmployeesInput(e.target.value))
          }
        />
        {mjEditJobBotLocked || mjEditJobHasRunningTasks ? (
          <TextField
            sx={{ mb: 1 }}
            fullWidth
            label="Bot"
            value={
              normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName)?.bot_slug ??
              (mjEditBotName.includes(".") ? mjEditBotName.slice(mjEditBotName.indexOf(".") + 1) : mjEditBotName)
            }
            disabled
            helperText={
              mjEditJobBotLocked
                ? "Cannot change bot while this task job has employee progress."
                : ""
            }
          />
        ) : (
          <Autocomplete
            ListboxComponent={AutocompleteScrollListbox}
            componentsProps={autocompleteScrollPaperProps}
            sx={{ mb: 1 }}
            options={normalizedChildBots}
            value={
              normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName) ?? null
            }
            onChange={(_e, v) => setMjEditBotName(v ? botNameFromRegistry(v) : "")}
            filterOptions={filterChildDialogBots}
            getOptionLabel={(o) => o.bot_slug}
            renderOption={(props, option) => (
              <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
                {option.bot_slug}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bot"
                required
                error={!mjEditBotName.trim() && Boolean(mjEditJobError)}
              />
            )}
          />
        )}
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={mjEditProcessErrors}
              onChange={(e) => setMjEditProcessErrors(e.target.checked)}
              disabled={mjEditJobHasRunningTasks}
            />
          }
          label="Auto process error employees (one retry pass after the job finishes)"
        />
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={mjEditSeparateProfiles}
              onChange={(e) => setMjEditSeparateProfiles(e.target.checked)}
            />
          }
          label="Separate browser profile per task (applies to tasks added from now on)"
        />
        {activeMajor?.bot_kind === "upload_bots" && (
          <TextField
            label="Document type *"
            size="small"
            fullWidth
            value={mjEditDocumentType}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditDocumentType(e.target.value)}
            sx={{ mb: 1 }}
            placeholder="e.g. w2, paystubs, 1095"
            disabled={mjEditJobHasRunningTasks}
          />
        )}
        <BotOptionFields
          options={botOptionSpecs(mjEditBotName)}
          value={mjEditAdditionalOptions}
          onChange={setMjEditAdditionalOptions}
          disabled={mjEditJobHasRunningTasks}
          omitKeys={DOCS_TYPE_OMIT}
        />
        {activeMajor?.bot_kind === "delete_bots" && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Document types to delete *
            </Typography>
            {mjEditDocumentTypes.map((dt, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Paylocity Historical W2s"
                  value={dt}
                  disabled={mjEditJobHasRunningTasks}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setMjEditDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                  }
                />
                <IconButton size="small" color="error"
                  disabled={mjEditDocumentTypes.length === 1 || mjEditJobHasRunningTasks}
                  onClick={() => setMjEditDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<Add fontSize="small" />}
              disabled={mjEditJobHasRunningTasks}
              onClick={() => setMjEditDocumentTypes((prev) => [...prev, ""])}>
              Add type
            </Button>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Upload options
            </Typography>
            <Stack direction="column" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUploadAsZip}
                    onChange={(e) => setMjEditUploadAsZip(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Upload as ZIP"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUnzipBefore}
                    onChange={(e) => setMjEditUnzipBefore(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Unzip before uploading"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUseSsnMatch}
                    onChange={(e) => setMjEditUseSsnMatch(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Use SSN match"
              />
            </Stack>
            {mjEditSsnReadiness && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5 }}
                color={mjEditSsnReadiness.ready ? "success.main" : "warning.main"}
              >
                {mjSsnReadinessText(mjEditSsnReadiness)}
              </Typography>
            )}
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Source folder (files to upload)
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                placeholder="Select a folder from Data Storage…"
                value={mjEditSourcePath}
                InputProps={{ readOnly: true }}
                disabled={mjEditJobHasRunningTasks}
                sx={{ "& .MuiInputBase-input": { color: mjEditSourcePath ? "text.primary" : "text.disabled" } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                disabled={mjEditJobHasRunningTasks}
                onClick={() => openFolderPicker("edit", mjEditSourcePath)}
                sx={{ whiteSpace: "nowrap", minWidth: 90 }}
              >
                Browse
              </Button>
            </Stack>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Tasks and their connections are managed directly in the tasks table (Add tasks / row
          actions).
        </Typography>
        {mjEditJobError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mjEditJobError}
          </Alert>
        ) : null}
      </AppDialog>

      <DeleteJobConfirmDialog
        open={mjDeleteJobId != null}
        title="Delete task job?"
        message={
          <>This permanently removes job{mjDeleteJobOrdinal != null ? ` #${mjDeleteJobOrdinal}` : ""}.</>
        }
        onCancel={() => setMjDeleteJobId(null)}
        onConfirm={() => void mjConfirmDeleteJob()}
        confirmDisabled={!canEditBots}
        busy={isMjDeletingJob}
      />

      <ConfirmDialog
        open={mjRosterConfirmJobId != null}
        title={
          mjRosterConfirmJobId != null &&
          mjIsUploadJob(
            (childJobs.find(
              (r) => Number((r as Record<string, unknown>).id) === mjRosterConfirmJobId,
            ) as Record<string, unknown> | undefined) ?? {},
          )
            ? "Build the matches files only?"
            : "Build the roster only?"
        }
        message="One bot logs in, builds the roster (for an upload job: the Matches, Several matches and No matches files), sets the task ranges from it and processes nobody. Review the result on the Matches tab, merge what you resolved, then press Run all."
        confirmLabel="Build"
        onConfirm={() => {
          const jobId = mjRosterConfirmJobId;
          if (jobId == null) return;
          setMjRosterConfirmJobId(null);
          void mjSsnGuardedRun(mjJobRowById(jobId), "Build matches only", () =>
            mjRunRosterTask(jobId),
          );
        }}
        onCancel={() => setMjRosterConfirmJobId(null)}
        loading={mjRosterTaskBusyJobId != null}
      />
      <ConfirmDialog
        open={mjSsnRunPrompt != null}
        title="Run without SSN matching?"
        message={
          mjSsnRunPrompt
            ? `${mjSsnRunPrompt.label}: SSN matching is not available - ${mjSsnRunPrompt.readiness.reason}. The bot will match employees by name only, which is less accurate: two employees with the same name can be confused. Create the SSN job(s) and let them finish first, or continue by names.`
            : ""
        }
        confirmLabel="Continue by names"
        onConfirm={() => {
          const prompt = mjSsnRunPrompt;
          setMjSsnRunPrompt(null);
          if (prompt) void prompt.proceed();
        }}
        onCancel={() => setMjSsnRunPrompt(null)}
      />
      <ConfirmDialog
        open={mjRosterDeletePrompt != null}
        title={
          mjRosterInfo?.info.kind === "matches" ? "Delete all matches files?" : "Delete the roster?"
        }
        message={(() => {
          const info = mjRosterInfo?.info;
          const shared = info?.sibling_job_ids.length
            ? ` Job(s) ${info.sibling_job_ids.map((id) => `#${id}`).join(", ")} read the same folder and are affected too.`
            : "";
          return info?.kind === "matches"
            ? `The Matches, Several matches and No matches files of this job (and the matching summary) are deleted - the job's own files and the legacy shared ones. The next run matches again from the source folder.${shared} Log records are addressed by employee number, so a rebuilt file may attribute the Logs of tasks that already ran differently.`
            : `employees_info.json is deleted. The next run collects the roster again from the vendor.${shared} Log records are addressed by employee number, so a rebuilt roster may attribute the Logs of tasks that already ran differently.`;
        })()}
        confirmLabel="Delete"
        onConfirm={() => void confirmMjRosterDelete()}
        onCancel={() => setMjRosterDeletePrompt(null)}
        loading={mjRosterDeleting}
      />
      <ConfirmDialog
        open={mjBrowserProfilesPrompt}
        title="Delete the browser profile?"
        message={`The stored Chrome profile(s) of ${activeMajor?.vendor ?? "this vendor"} for this user are deleted (${mjBrowserProfiles?.profiles.length ?? 0} file(s)). Every bot of this user on this vendor logs in from scratch on its next run - with 2FA or a captcha - and a fresh profile is stored after that login. ${mjBrowserProfiles?.jobs_using ?? 0} job(s) use it.`}
        confirmLabel="Delete"
        onConfirm={() => void confirmMjBrowserProfilesDelete()}
        onCancel={() => setMjBrowserProfilesPrompt(false)}
        loading={mjBrowserProfilesBusy}
      />
      <AppDialog
        open={mjArtifactUpload != null}
        onClose={() =>
          mjArtifactUpload?.step !== "uploading" &&
          mjArtifactUpload?.step !== "replacing" &&
          setMjArtifactUpload(null)
        }
        title={mjArtifactUpload?.purpose === "roster" ? "Upload roster file" : "Upload matches file"}
        actions={
          <>
            <Button
              onClick={() => setMjArtifactUpload(null)}
              disabled={mjArtifactUpload?.step === "uploading" || mjArtifactUpload?.step === "replacing"}
            >
              Close
            </Button>
            {mjArtifactUpload?.step !== "done" && (
              <Button
                variant="contained"
                disabled={
                  mjArtifactUpload?.step !== "ready" ||
                  !canEditBots ||
                  Boolean(mjArtifactUpload?.reindex && !mjArtifactUpload.confirmReindex)
                }
                onClick={() => void confirmMjArtifactReplace()}
              >
                Replace
              </Button>
            )}
          </>
        }
      >
        <Stack spacing={1.25}>
          <Typography variant="body2" color="text.secondary">
            {mjArtifactUpload?.purpose === "roster"
              ? "A JSON list of employees in the shape the bot writes (first_name, last_name, employee_id). It replaces employees_info.json; the next run walks this list."
              : "A reviewed matches file (CSV, XLS or XLSX) with s3_name, site_name, employee_id - the header row is optional, two columns are read as s3_name and employee_id. It replaces the Matches file whole; the Several matches and No matches files stay as they are and are marked outdated."}
          </Typography>
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={mjArtifactUpload?.step === "uploading" || mjArtifactUpload?.step === "replacing"}
          >
            {mjArtifactUpload?.file ? mjArtifactUpload.file.name : "Choose file"}
            <input
              type="file"
              hidden
              accept={mjArtifactUpload?.purpose === "roster" ? ".json" : ".csv,.xls,.xlsx"}
              onChange={(e) => void handleMjArtifactFileSelect(e)}
            />
          </Button>
          {mjArtifactUpload?.step === "uploading" && <LinearProgress />}
          {mjArtifactUpload?.step === "replacing" && <LinearProgress />}
          {mjArtifactUpload?.reindex && (
            <Alert severity="warning">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mjArtifactUpload.confirmReindex}
                    onChange={(e) =>
                      setMjArtifactUpload((prev) =>
                        prev ? { ...prev, confirmReindex: e.target.checked } : prev,
                      )
                    }
                  />
                }
                label={mjArtifactUpload.reindex.message}
              />
            </Alert>
          )}
          {mjArtifactUpload?.error && <Alert severity="error">{mjArtifactUpload.error}</Alert>}
          {mjArtifactUpload?.result && <Alert severity="success">{mjArtifactUpload.result}</Alert>}
        </Stack>
      </AppDialog>
      <AppDialog
        open={mjRosterExistsPrompt != null}
        onClose={() => setMjRosterExistsPrompt(null)}
        title="The roster is already built"
        actions={
          <>
            <Button onClick={() => setMjRosterExistsPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() =>
                mjRosterExistsPrompt && void mjRunRosterTask(mjRosterExistsPrompt.jobId, true)
              }
            >
              Run anyway
            </Button>
          </>
        }
      >
        <Stack spacing={1}>
          <Typography variant="body2">{mjRosterExistsPrompt?.message}</Typography>
          {mjRosterExistsPrompt?.files.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {mjRosterExistsPrompt.files.map((file) => (
                <Typography key={file} component="li" variant="caption" sx={{ wordBreak: "break-all" }}>
                  {file}
                </Typography>
              ))}
            </Box>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Delete the file from Manage job to rebuild it, or run anyway - the bot will log in and reuse the stored file.
          </Typography>
        </Stack>
      </AppDialog>
      <AppDialog
        open={mjNewRowsJob != null}
        onClose={() => setMjNewRowsJob(null)}
        maxWidth="xs"
        title="Run merged employees"
        actions={
          <>
            <Button onClick={() => setMjNewRowsJob(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !canEditBots ||
                mjNewRowsRunningJobId != null ||
                !(Number(mjNewRowsTaskCount) >= 1)
              }
              onClick={() => {
                if (!mjNewRowsJob) return;
                void runNewRosterRows(
                  mjNewRowsJob.id,
                  Math.max(1, Math.trunc(Number(mjNewRowsTaskCount) || 1)),
                );
              }}
            >
              Run
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            {mjNewRowsJob?.rows ?? 0} employee(s) in the Matches file have never
            been run - normally the ones added through Merge reports. They get
            tasks of their own; the tasks that already ran keep their employees
            and their logs, and are not restarted.
          </Typography>
          <TextField
            size="small"
            type="number"
            label="Split across how many tasks"
            value={mjNewRowsTaskCount}
            onChange={(event) => setMjNewRowsTaskCount(event.target.value)}
            inputProps={{ min: 1, max: 30 }}
            helperText="One bot container per task. Keep it within what the vendor tolerates at once."
          />
        </Stack>
      </AppDialog>

      <AppDialog
        open={mjClearLogsJobId != null}
        onClose={() => setMjClearLogsJobId(null)}
        maxWidth="xs"
        title="Clear all logs?"
        actions={
          <>
            <Button onClick={() => setMjClearLogsJobId(null)}>Cancel</Button>
            <Button
              color="warning"
              variant="contained"
              disabled={isMjClearLogsLoading || !canEditBots}
              onClick={() => void handleMjClearJobLogs()}
            >
              Clear logs
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            This permanently deletes this job's stored logs
            {mjClearLogsInventory ? ` (${mjClearLogsInventory.own} archive(s))` : ""}.
            The next run will re-process every employee from scratch. Continue?
          </Typography>
          {mjClearLogsInventory && mjClearLogsInventory.shared > 0 ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mjClearLogsIncludeShared}
                    onChange={(event) =>
                      setMjClearLogsIncludeShared(event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    {`Also clear the archives of other jobs in this folder (${mjClearLogsInventory.shared})`}
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                The folder is shared by every job of this document type, and a bot
                reads all of it when it starts. Leave this off to keep the other
                jobs' history - the next run will then still skip the employees
                those archives cover.
              </Typography>
            </>
          ) : null}
        </Stack>
      </AppDialog>

      <AppDialog
        open={ssnJobDeleteConfirmOpen}
        onClose={() => setSsnJobDeleteConfirmOpen(false)}
        maxWidth="xs"
        title="Delete SSN job?"
        actions={
          <>
            <Button onClick={() => setSsnJobDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={ssnJobDeleting || !canEditBots}
              onClick={async () => {
                if (!activeMajor || !canEditBots) return;
                setSsnJobDeleting(true);
                setSsnJobDeleteConfirmOpen(false);
                try {
                  await deleteSsnJobMut(activeMajor.id).unwrap();
                  await loadSsnJob(activeMajor.id);
                } catch (e: unknown) {
                  setSsnJobError(
                    (e as { data?: { message?: string } })?.data?.message ??
                      "Failed to delete SSN job.",
                  );
                } finally { setSsnJobDeleting(false); }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          This will permanently remove the SSN job and its task. The SSN file in S3 will not be deleted.
        </Typography>
      </AppDialog>

      <AppDialog
        open={twoFaMjTask != null}
        onClose={() => {
          setTwoFaMjTask(null);
          setTwoFaMjCode("");
          setTwoFaMjErr(null);
        }}
        maxWidth="xs"
        title="2FA code"
        actions={
          <>
            <Button
              onClick={() => {
                setTwoFaMjTask(null);
                setTwoFaMjCode("");
                setTwoFaMjErr(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" disabled={isSubmit2FAMj || !canEditBots} onClick={() => void handleMjSubmit2FACode()}>
              Submit
            </Button>
          </>
        }
      >
        {twoFaMjTask ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Task{(() => {
              const jid = typeof twoFaMjTask.job === "number" ? twoFaMjTask.job : null;
              if (jid == null) return "";
              const ord = taskOrdinalInJob(jid, twoFaMjTask.id);
              return ord != null ? ` #${ord}` : "";
            })()}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          autoFocus
          label="Code"
          value={twoFaMjCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTwoFaMjCode(e.target.value)}
          onKeyDown={(ev) => {
            if (
              ev.key === "Enter" &&
              !ev.shiftKey &&
              twoFaMjCode.trim().length > 0 &&
              !isSubmit2FAMj &&
              canEditBots
            ) {
              ev.preventDefault();
              void handleMjSubmit2FACode();
            }
          }}
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
        />
        {twoFaMjErr ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {twoFaMjErr}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={mjDeleteSingleTaskId != null}
        onClose={() => setMjDeleteSingleTaskId(null)}
        maxWidth="xs"
        title="Delete task?"
        actions={
          <>
            <Button onClick={() => setMjDeleteSingleTaskId(null)}>Cancel</Button>
            <Button color="error" variant="contained" disabled={isDeletingNestedTask || !canEditBots} onClick={() => void mjConfirmDeleteNestedTask()}>
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">Remove the selected task?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The task's log archive in S3 is deleted with it. Those logs are the only record of the employees this task processed, so the job stops counting them and the next run processes them again.
        </Typography>
      </AppDialog>

      <AppDialog
        open={mjBatchDeleteNestDialog != null && (mjBatchDeleteNestDialog.ids?.length ?? 0) > 0}
        onClose={() => setMjBatchDeleteNestDialog(null)}
        maxWidth="xs"
        title="Delete selected tasks?"
        actions={
          <>
            <Button onClick={() => setMjBatchDeleteNestDialog(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={isMjNestedTasksBatchDeleting || !canEditBots}
              onClick={() => void mjConfirmBatchDeleteNestedTasks()}
            >
              Delete all
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Delete {mjBatchDeleteNestDialog?.ids.length ?? 0} task(s) from this job? This cannot be undone.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Their log archives in S3 are deleted with them. Those logs are the only record of the employees they processed, so the job stops counting them and the next run processes them again.
        </Typography>
      </AppDialog>

      <DeleteJobConfirmDialog
        open={bulkChildDeleteOpen}
        title="Delete selected task jobs?"
        message={
          <>
            Delete {selectedChildJobIds.length} task job
            {selectedChildJobIds.length === 1 ? "" : "s"} and all their tasks for this major job? This cannot
            be undone.
          </>
        }
        onCancel={() => setBulkChildDeleteOpen(false)}
        onConfirm={() => void confirmBulkDeleteChildren()}
        confirmDisabled={!selectedChildJobIds.length || !canEditBots}
        busy={isDeletingJobsBulk}
      />

      {/* ── Folder Picker Dialog (upload bots source folder) ─────────────────── */}
      <AppDialog
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        maxWidth="sm"
        title="Select source folder"
        actions={
          <>
            <Button onClick={() => setFolderPickerOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!pickerBuiltPath}
              onClick={confirmFolderPicker}
            >
              Select
            </Button>
          </>
        }
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Choose the Data Storage folder containing the files you want to upload.
        </Typography>
        {storageFolders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No storage folders found for this user.
          </Typography>
        ) : (
          storageFolders.map((folder) => (
            <Box key={folder.id} sx={{ mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 0.5,
                  cursor: "pointer",
                  color: pickerSelectedFolder === folder.id ? "primary.main" : "text.primary",
                }}
                onClick={() => {
                  setPickerSelectedFolder(folder.id);
                  setPickerSelectedSub(null);
                }}
              >
                {folder.display_name || folder.folder_prefix}
              </Typography>
              {folder.subfolders.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  No subfolders cached
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ ml: 1 }}>
                  {folder.subfolders.map((sub) => {
                    const isSelected = pickerSelectedFolder === folder.id && pickerSelectedSub === sub;
                    return (
                      <Box
                        key={sub}
                        onClick={() => {
                          setPickerSelectedFolder(folder.id);
                          setPickerSelectedSub(sub);
                        }}
                        sx={{
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 1,
                          cursor: "pointer",
                          border: 1,
                          borderColor: isSelected ? "primary.main" : "divider",
                          bgcolor: isSelected ? "primary.50" : "background.paper",
                          color: isSelected ? "primary.main" : "text.primary",
                          fontSize: "0.8125rem",
                          fontWeight: isSelected ? 700 : 400,
                          "&:hover": { borderColor: "primary.main", bgcolor: "primary.50" },
                        }}
                      >
                        {sub}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          ))
        )}
        {pickerBuiltPath && (
          <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: "action.hover" }}>
            <Typography variant="caption" color="text.secondary">
              Selected path:
            </Typography>
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.75rem", mt: 0.25 }}
            >
              {pickerBuiltPath}
            </Typography>
          </Box>
        )}
      </AppDialog>

      {/* ── SSN File Viewer Dialog ────────────────────────────────────────────── */}
      <AppDialog
        open={ssnFileViewOpen}
        onClose={() => setSsnFileViewOpen(false)}
        maxWidth="md"
        title="ssn_info.json"
        actions={
          <>
            <Button
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
              disabled={ssnFileViewLoading}
            >
              Download
            </Button>
            <Button onClick={() => setSsnFileViewOpen(false)}>Close</Button>
          </>
        }
      >
        {ssnFileViewLoading ? (
          <LinearProgress sx={{ my: 2 }} />
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              fontFamily: "monospace",
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {ssnFileViewContent ?? ""}
          </Box>
        )}
      </AppDialog>

      {/* ── Matches CSV Viewer Dialog ─────────────────────────────────────────── */}
      <AppDialog
        open={matchesViewOpen}
        onClose={() => setMatchesViewOpen(false)}
        maxWidth="lg"
        title={
          matchesViewFilename === "s3_to_site_names_matches.csv"
            ? "Matches"
            : matchesViewFilename === "s3_to_site_names_several_matches.csv"
              ? "Several matches"
              : "No matches"
        }
        actions={
          <>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
              {matchesViewRows.length > 1 ? `${matchesViewRows.length - 1} row${matchesViewRows.length - 1 === 1 ? "" : "s"}` : ""}
            </Typography>
            <Button
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={() => mjManageJobId != null && void downloadMatchesFile(mjManageJobId, matchesViewFilename)}
              disabled={matchesViewLoading}
            >
              Download
            </Button>
            <Button onClick={() => setMatchesViewOpen(false)}>Close</Button>
          </>
        }
      >
        {matchesViewRows.length === 0 && !matchesViewLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            (empty)
          </Typography>
        )}
        {matchesViewRows.length > 0 && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {matchesViewRows[0].map((col, ci) => (
                  <TableCell key={ci} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matchesViewRows.slice(1).map((row, ri) => (
                <TableRow key={ri} hover>
                  {row.map((cell, ci) => (
                    <TableCell key={ci} sx={{ fontSize: "0.8rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AppDialog>

      {/* ── Matches Merge Reports Dialog ──────────────────────────────────────── */}
      <AppDialog
        open={matchesMergeOpen}
        onClose={() => {
          if (matchesMergeStep !== "merging") resetMatchesMergeState();
        }}
        maxWidth="sm"
        title="Merge reports"
        actions={
          <>
            <Button
              onClick={resetMatchesMergeState}
              disabled={matchesMergeStep === "merging"}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={matchesMergeStep !== "ready" || !matchesMergeStagingKey || !canEditBots}
              onClick={() => void confirmMatchesMerge()}
              startIcon={
                matchesMergeStep === "merging" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <MergeTypeIcon fontSize="small" />
                )
              }
            >
              {matchesMergeStep === "merging" ? "Merging…" : "Merge"}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a reviewed file (CSV, XLS, XLSX). Rows will be appended to the Matches
          file. Several matches and No matches files are not modified. Rows already
          present in Matches are skipped.
        </Typography>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            component="label"
            disabled={matchesMergeStep === "uploading" || matchesMergeStep === "merging" || !canEditBots}
          >
            Choose file
            <input
              type="file"
              hidden
              accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => void handleMatchesMergeFileSelect(e)}
            />
          </Button>
          {matchesMergeFile && (
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {matchesMergeFile.name}
            </Typography>
          )}
          {matchesMergeStep === "uploading" && (
            <Stack direction="row" gap={0.75} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Uploading…
              </Typography>
            </Stack>
          )}
          {matchesMergeStep === "ready" && (
            <Typography variant="caption" color="success.main">
              Ready to merge
            </Typography>
          )}
        </Stack>
        {matchesMergeError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {matchesMergeError}
          </Alert>
        )}
      </AppDialog>

      {/* The operator's window into a bot's browser */}
      <TaskLiveViewDialog
        taskId={liveViewMjTaskId}
        canEdit={canEditBots}
        onClose={() => {
          if (liveViewMjTaskId != null) {
            setLiveViewMjDismissed((prev) =>
              prev.includes(liveViewMjTaskId)
                ? prev
                : [...prev, liveViewMjTaskId],
            );
          }
          setLiveViewMjTaskId(null);
        }}
      />

      {/* ── Matches Delete Confirmation ───────────────────────────────────────── */}
      <AppDialog
        open={matchesDeleteFilename != null}
        onClose={() => !matchesDeleting && setMatchesDeleteFilename(null)}
        maxWidth="xs"
        title="Delete matches file?"
        actions={
          <>
            <Button onClick={() => setMatchesDeleteFilename(null)} disabled={matchesDeleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => mjManageJobId != null && void confirmDeleteMatchesFile(mjManageJobId)}
              disabled={matchesDeleting || !canEditBots}
              startIcon={matchesDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {matchesDeleting ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Are you sure you want to delete{" "}
          <strong>
            {matchesDeleteFilename === "s3_to_site_names_matches.csv"
              ? "Matches"
              : matchesDeleteFilename === "s3_to_site_names_several_matches.csv"
                ? "Several matches"
                : "No matches"}
          </strong>
          ? This action cannot be undone.
        </Typography>
      </AppDialog>

      {/* ── Logs Dialog (All logs / File logs / Status logs) ─────────────────── */}
      <LogsDialog
        open={logsScope != null}
        onClose={() => setLogsScope(null)}
        title={logsTitle}
        scope={logsScope}
      />

      {/* ── Task error popup (clickable status chip) ──────────────────────────── */}
      <AppDialog
        open={mjErrorPopupTask != null}
        onClose={() => setMjErrorPopupTask(null)}
        maxWidth="xs"
        title="Task error"
        actions={
          <>
            <Button onClick={() => setMjErrorPopupTask(null)}>Close</Button>
            <Button
              variant="contained"
              disabled={mjJobActionBusy || !canEditBots}
              onClick={() => {
                if (mjErrorPopupTask) {
                  void mjRunTaskRow(mjErrorPopupTask.jobId, mjErrorPopupTask.task);
                }
                setMjErrorPopupTask(null);
              }}
            >
              Rerun
            </Button>
          </>
        }
      >
        <Alert severity="error" sx={{ mt: 1 }}>
          The bot ran into errors. Please rerun the task or contact the dev team.
        </Alert>
      </AppDialog>
    </Box>
  );
}
